import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import reviewsAdminTranslations from "./reviewsAdminTranslations";
import { HOME_PAGE_LANDING } from "./homePageLanding";

export const SUPPORTED_LANGS = ["en", "he", "ru", "es", "ua"];

const LANGUAGE_ALIASES = {
  iw: "he",
  he: "he",
  ru: "ru",
  en: "en",
  es: "es",
  ua: "ua",
  uk: "ua",
};

export const normalizeLanguage = (code) => {
  if (!code || typeof code !== "string") {
    return null;
  }
  const lower = code.toLowerCase();
  const base = lower.split(/[-_]/)[0];
  const normalized = LANGUAGE_ALIASES[base] || base;
  return SUPPORTED_LANGS.includes(normalized) ? normalized : null;
};

export const resolveLanguage = (code, fallback = "en") =>
  normalizeLanguage(code) ?? fallback;

const translations = {
  ru: {
    language: "Русский",
    nav: {
      home: "Главная",
      dashboard: "Дашборд",
      profile: "Профиль",
      news: "Новости",
      bugs: "Ошибки",
      errors: "Ошибки",
      tasks: "Задачи",
      devices: "Контроллеры",
      rooms: "Зоны выращивания",
      strains: "Сорта",
      cuttings: "Библиотека черенков",
      secondaryMenu: "Еще",
      adminMenu: "Админ",
      about: "О компании",
      manageCuttings: "Управление черенками",
      manageStrains: "Управление сортами",
      manageReviews: "Проф. обзоры",
      manageNews: "Управление новостями",
      users: "Пользователи",
      adminPanel: "Панель администрирования",
      manageFarms: "Управление объектами",
      unassignedDevices: "Свободные контроллеры",
      wifiBle: "Wi‑Fi BLE",
      farmGroup: "Объект",
      farmsBreadcrumb: "Объекты",
      livePanel: "Живая панель",
      farm: "Объект",
      geneticsGroup: "Генетика",
      engineeringPanel: "Инженерная панель",
      adminBreadcrumb: "Админ",
      allControllers: "Все контроллеры",
    },
    roleSwitcher: {
      label: "Тест роли",
      reset: "Сброс",
    },
    role: {
      admin: "Админ",
      superuser: "Суперпользователь",
      farmOwner: "Владелец объекта",
      farmManager: "Менеджер объекта",
      worker: "Работник",
    },
    planTier: {
      included: "Старт",
      basic: "Базовый",
      standard: "Стандарт",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    deviceTabs: {
      overview: "Обзор",
      settings: "Настройки",
    },
    farmsAdmin: {
      title: "Управление объектами",
      colName: "Название",
      colOwner: "Владелец",
      colUsers: "Пользователей",
      colDevices: "Контроллеров",
      colActions: "Действия",
      create: "Создать новый объект",
      edit: "Редактировать",
      usersAction: "Пользователи",
      delete: "Удалить",
      editTitle: "Редактировать объект",
      ownerUnassigned: "Без владельца",
      saveChanges: "Сохранить изменения",
      savingChanges: "Сохранение…",
      confirmDeleteTitle: "Удаление объекта",
      confirmDelete: (name) =>
        `Удалить объект «${name}»? Доступно только для пустого объекта (без пользователей, контроллеров и данных).`,
      modalTitle: "Новый объект",
      farmNamePlaceholder: "Название объекта",
      createSubmit: "Создать",
      creating: "Создание…",
      deleting: "Удаление…",
      accessDenied: "Раздел доступен только администраторам и суперпользователям.",
      usersAccessDenied: "Управление пользователями доступно только администраторам и суперпользователям.",
      moveFarmLabel: "Перенести на другой объект",
      apply: "Применить",
      unassigned: "Не привязана",
      freePoolOption: "-- Свободный пул (Отвязать) --",
      freeStatus: "Статус: Свободен",
      moving: "Сохранение…",
      farmUpdated: "Привязка контроллера к объекту обновлена",
      deviceFreed: "Контроллер освобождён и переведён в свободный пул",
      farmUpdateNoop: "Без изменений",
      farmUpdateFailed: "Не удалось изменить объект",
      farmColumn: "Объект",
      selectFarmNewUser: "Объект",
      loading: "Загрузка…",
      empty: "Нет объектов",
      loadError: "Не удалось загрузить список объектов",
      createFailed: "Не удалось создать объект",
      deleteFailed: "Не удалось удалить объект",
      updateFailed: "Не удалось обновить объект",
      planTypeLabel: "Тарифный план",
    },
    welcome: {
      title: "ДОБРО ПОЖАЛОВАТЬ В SHEFAGREENAPP",
      subtitle: "Shefa Green — умное управление плантациями и климатом."
    },
    home: {
      hero: {
        title: "Управляйте сортами и циклами выращивания с Shefa Green",
        subtitle: "Защищённая платформа для сортов, размножения, зон выращивания, климата, воды, оборудования, дневника гроувера, аналитики и будущей AI-поддержки решений по выращиванию.",
        slogan: "Сорта в центре. Выращивание под контролем.",
        openDashboard: "Открыть дашборд",
        about: "О проекте",
      },
      public: {
        eyebrow: "Рабочее пространство вокруг сортов",
        valueEyebrow: "Для операций гроувера",
        valueTitle: "Система вокруг сортов, генетики и циклов выращивания, а не просто устройств",
        valueSubtitle:
          "Shefa Green соединяет размножение, зоны выращивания, пресеты, дневник, действия контроллеров, задачи и аналитику в один операционный слой для профессиональных гроуверов.",
        title: "Shefa Green помогает гроуверам управлять всем циклом выращивания вокруг сортов.",
        body:
          "Shefa Green объединяет сорта, циклы выращивания, зоны выращивания, черенки, климат, воду, оборудование, дневник гроувера, аналитику и будущую AI-поддержку решений в одном защищённом рабочем пространстве. Каждый сорт можно вести через размножение, перемещение между зонами выращивания, стадии роста, наблюдения, обработки и историю выращивания, а ShefaGreen Controllers помогают поддерживать среду вокруг растения.",
        notice:
          "Операционные данные доступны только после входа. Публичные посетители не видят имена контроллеров, ID контроллеров, названия зон выращивания, названия объектов, сенсорные значения, задачи, записи сортов, черенки, дневник выращивания или результаты аналитики.",
        statsAria: "Ключевые преимущества платформы Shefa Green",
        stats: {
          strainCentered: {
            value: "100%",
            label: "Вокруг сортов",
          },
          aiReady: {
            value: "AI-ready",
            label: "Roadmap поддержки решений",
          },
          protected: {
            value: "Secure",
            label: "Защищённый workspace",
          },
        },
        preview: {
          activeStrain: "Активный сорт",
          flowering: "Цветение",
          cycleProgress: "Прогресс цикла выращивания",
        },
        controllerPill: "ShefaGreen Controllers",
        controllersTitle: "Мониторинг и управление средой выращивания",
        controllersBody:
          "ShefaGreen Controllers — это оборудование для выращивания, которое может не только мониторить, но и управлять климатом, вентиляцией, охлаждением, отоплением, орошением, качеством воды, потоком воды и статусом оборудования в защищённом workspace.",
        ctaBadge: "Защищённая профессиональная платформа",
        ctaTitle: "Одно защищённое рабочее пространство для управления выращиванием",
        ctaBody:
          "Ведите каждый сорт от генетики до урожая, пока команда, контроллеры, дневник, аналитика и будущий AI-ассистент остаются связанными в одном месте.",
        ctaButton: "Войти в Shefa Green",
        pricing: {
          eyebrow: "Тарифы",
          title: "Планы подписки для объектов",
          subtitle:
            "Разница между планами — в глубине хранения и просмотра истории климат-данных в облаке и в доступности расширенного анализа VPD (дефицита давления пара).",
          commonFeatures:
            "На любом тарифе доступны одни и те же модули: сорта, зоны выращивания, пресеты, дневник, контроллеры, задачи, климат и воду — ограничения касаются глубины телеметрии, см. ниже.",
          retentionNote:
            "Срок хранения «сырой» климат-телеметрии задаётся планом: за пределами окна точки удаляются; графики и выборки не уходят дальше указанного периода.",
          footer:
            "Тариф назначает администратор Shefa Green. Чтобы сменить план объекта, свяжитесь с поддержкой.",
          plans: {
            included: {
              tagline: "Уровень по умолчанию после подключения",
              bullets: [
                "1 день истории климат-данных для графиков и обзора.",
                "Расширенные режимы анализа с акцентом на VPD отключены; базовые климатические метрики доступны.",
              ],
            },
            basic: {
              tagline: "Короткий операционный обзор",
              bullets: ["3 дня истории климат-данных.", "Расширенный анализ VPD недоступен."],
            },
            standard: {
              tagline: "Недельное окно для отчётов",
              bullets: ["7 дней истории климат-данных.", "Расширенный анализ VPD недоступен."],
            },
            pro: {
              tagline: "Месяц истории и VPD",
              bullets: [
                "30 дней истории климат-данных.",
                "Расширенный анализ VPD включён (функция включается на уровне объекта в системе).",
              ],
            },
            enterprise: {
              tagline: "Долгий архив и VPD",
              bullets: ["365 дней истории климат-данных.", "Расширенный анализ VPD включён."],
            },
          },
        },
        capabilities: {
          temperature: "Температура",
          humidity: "Влажность",
          co2: "CO2",
          ventilation: "Вентиляция",
          cooling: "Охлаждение",
          heating: "Отопление",
          irrigation: "Орошение",
          waterFlow: "Поток воды",
          ec: "EC",
          ph: "pH",
          equipmentStatus: "Статус оборудования",
        },
        modules: {
          strains: {
            title: "Сорта",
            description:
              "Сделайте генетику центром системы: ведите каждый сорт через размножение, перемещения, стадии роста, наблюдения, обработки и историю выращивания.",
          },
          growthPresets: {
            title: "Пресеты роста",
            description:
              "Планируйте настраиваемые пресеты для стадий выращивания, чтобы цели зон выращивания и рабочие рутины соответствовали жизненному циклу растения.",
          },
          growerDiary: {
            title: "Дневник гроувера",
            description:
              "Записывайте события выращивания, заметки, наблюдения, обработки, смены стадий, историю зон выращивания и решения гроувера.",
          },
          analytics: {
            title: "Аналитика",
            description:
              "Сравнивайте сорта, зоны выращивания, стадии роста, климат, воду, циклы выращивания и операционные результаты во времени.",
          },
          controllers: {
            title: "ShefaGreen Controllers",
            description:
              "Используйте ShefaGreen Controllers как оборудование для выращивания, которое может мониторить и управлять средой в защищённом workspace.",
          },
          climateAutomation: {
            title: "Автоматизация климата",
            description:
              "Поддержка температуры, влажности, CO2, вентиляции, охлаждения, отопления и настроенных климатических выходов из одного workspace.",
          },
          waterControl: {
            title: "Контроль воды",
            description:
              "Работа с орошением, потоком воды, EC, pH, ORP, уровнем воды и температурой воды как частью цикла выращивания.",
          },
          cuttings: {
            title: "Черенки",
            description:
              "Управляйте размножением и черенками как частью полного жизненного цикла сорта — от исходной генетики до назначения в зону выращивания.",
          },
          tasks: {
            title: "Оборудование и задачи",
            description:
              "Координируйте статус оборудования, обслуживание, follow-up, рабочие задачи, проблемы и ответственность команды с доступом по ролям.",
          },
          aiRoadmap: {
            title: "Аналитика и будущий AI-ассистент",
            description:
              "Планируется будущий интеллектуальный слой, который будет понимать историю выращивания, находить паттерны, анализировать результаты и предлагать возможные действия на основе собранных данных.",
          },
        },
      },
      ticker: {
        ariaLabel: "Лента статусов устройств в реальном времени",
        online: (n) => `Онлайн: ${n}`,
        offline: (n) => `Офлайн: ${n}`,
        lastSeen: (time) => `последний сеанс ${time}`,
        empty: "Нет данных об устройствах",
      },
      strains: {
        title: "Сорта сегодня",
        viewAll: "Все сорта",
        empty: "Нет сортов для отображения",
        updated: (date) => `Обновлено ${date}`,
      },
      news: {
        title: "Новости",
        viewAll: "Все новости",
      },
      tasks: {
        title: "Задачи",
        viewAll: "Все задачи",
        empty: "Нет открытых задач",
        superuserOnly: "Задачи видны только суперпользователям.",
      },
      quickActions: {
        title: "Быстрые действия",
        qr: "QR",
      },
      footer: {
        telegram: "Telegram-бот",
      },
    },
    dashboard: {
      title: "Живая панель",
      lastUpdated: (time) => `Обновлено ${time}`,
      autoRefresh: {
        label: "Автообновление",
        enabled: (seconds) => `Обновление каждые ${seconds} сек`,
        disabled: "Автообновление приостановлено",
      },
      refreshNow: "Обновить сейчас",
      errors: {
        farmContextRequired: "Выберите объект или войдите снова, чтобы загрузить обзор.",
      },
      summary: {
        title: "Сводка системы",
        totalDevices: "Всего устройств",
        online: "Устройств онлайн",
        offline: "Устройств офлайн",
        lastData: "Последние данные",
        totalStrains: "Всего сортов",
        noData: "Нет данных",
      },
      climate: {
        title: "Климат по объекте (среднее)",
        temperature: "Средняя температура",
        humidity: "Средняя влажность",
        co2: "Средний CO₂",
        noData: "—",
      },
      alerts: {
        title: "Последние проблемы и предупреждения",
        viewAll: "Все обращения",
        empty: "Нет критичных событий за последний период.",
        deviceOffline: "Контроллер офлайн",
      },
      recentDevices: {
        title: "Недавние контроллеры",
        viewAll: "Все контроллеры",
        empty: "Нет недавней активности устройств",
        lastSeen: (time) => `последний сеанс ${time}`,
      },
      status: {
        online: "Онлайн",
        offline: "Офлайн",
      },
      strains: {
        title: "Последние сорта",
        viewAll: "Все сорта",
        empty: "Нет обновлений по сортам",
      },
      news: {
        title: "Последние новости",
        viewAll: "Все новости",
        empty: "Новостей пока нет",
      },
      tasks: {
        title: "Открытые задачи",
        viewAll: "Все задачи",
        empty: "Нет открытых задач",
        superuserOnly: "Задачи видны только суперпользователям.",
      },
      rooms: {
        title: "Зоны выращивания",
        heading: "Помещения и климат",
        subtitle:
          "Пространства внутри объекта. Контроллеры и дневник гроувера привязаны к выбранной зоне выращивания.",
        create: "Создать зону выращивания",
        namePlaceholder: "Название (например, Вегетарий)",
        typePlaceholder: "Тип (необязательно)",
        empty: "Комнат пока нет",
        loading: "Загрузка…",
        open: "Открыть",
        delete: "Удалить",
        confirmDeleteRoom:
          "Удалить эту зону выращивания? Контроллеры останутся на объекте, но без привязки к зоне выращивания.",
        addRoom: "Добавить зону выращивания",
      },
      planBadge: (label) => `План: ${label}`,
    },
    roomType: {
      seedling: "Рассада / проращивание",
      vegetation: "Вегетация",
      flowering: "Цветение",
      drying: "Сушка",
    },
    roomIcon: {
      leaf: "Лист",
      seed: "Семя",
      sun: "Свет",
      "grow-light": "Свет",
      droplet: "Капля",
      "water-drop": "Капля воды",
      fan: "Вентиляция",
      "ventilation-fan": "Вентиляция",
      home: "Зона выращивания",
      "grow-room": "Зона выращивания",
      layers: "Слои",
      thermometer: "Климат",
      climate: "Климат",
      flower: "Цветение",
      "mother-plant": "Маточник",
      "clone-cutting": "Черенок",
      "drying-room": "Сушка",
      storage: "Хранение",
      equipment: "Оборудование",
    },
    roomForm: {
      createTitle: "Новая зона выращивания",
      editTitle: "Редактировать зону выращивания",
      name: "Название",
      type: "Тип",
      typeNone: "—",
      icon: "Иконка",
      description: "Описание",
      farmHint: "Объект",
      farmIdLabel: "ID объекта (контекст)",
      farmIdTitle: "Передаётся в X-Farm-Context для привязки зоны выращивания",
      save: "Сохранить",
      saving: "Сохранение…",
      cancel: "Отмена",
      close: "Закрыть",
      nameRequired: "Введите название.",
      saveFailed: "Не удалось сохранить",
      noFarmContext: "Не выбран активный объект. Откройте обзор объекта из меню и повторите.",
      error503: "Сервер временно недоступен. Попробуйте через минуту.",
      error504: "Превышено время ожидания. Повторите запрос.",
      error400: "Неверные данные",
      errorTypeKey: "Некорректный тип зоны выращивания. Выберите значение из списка.",
      errorIconKey: "Некорректная иконка. Выберите значение из списка.",
      error403: "Нет прав на изменение зон выращивания.",
      error401: "Сессия истекла. Войдите снова.",
      errorHttp: (code) => `Ошибка запроса (HTTP ${code})`,
    },
    roomCard: {
      openRoom: (name) => `Открыть: ${name || 'зона выращивания'}`,
      onlineOffline: (on, off) => `${on} онлайн / ${off} офлайн`,
      unitOnline: "онлайн",
      unitOffline: "офлайн",
      manage: "Настройки зоны выращивания",
      configure: "Настроить",
      noData: "Нет данных",
      labelTemperature: "Температура",
      labelHumidity: "Влажность",
      labelCo2: "CO₂",
      climateGroup: "Климат",
      relayGroup: "Розетки и оборудование",
      outletGroup: "Розетки и оборудование",
      relayAc: "Кондиционер",
      relayDehumidifier: "Осушитель",
      relayCo2Valve: "CO₂",
      relayReserve: "Резервная розетка",
      relayStateOn: "вкл",
      relayStateOff: "выкл",
    },
    roomsPage: {
      breadcrumbInRoom: "Помещение",
      back: "К живой панели",
      backToRooms: "К списку зон выращивания",
      backDashboard: "К живой панели",
      diaryRoomHint: "Каждая запись сохраняется для этой зоны выращивания и включает снимок датчиков помещения.",
      devices: "Контроллеры в зоне выращивания",
      noDevices:
        "В этой зоне выращивания пока нет контроллеров. Назначьте зону выращивания в настройках контроллера.",
      diaryTitle: "Дневник гровера",
      diaryNote: "Заметка",
      diaryAdd: "Добавить запись",
      diaryEmpty: "Записей пока нет",
      diarySensors: "Показатели датчиков (авто)",
      sensorsNone: "Нет строк датчиков",
      loadError: "Не удалось загрузить зону выращивания",
      editRoom: "Редактировать",
      deleteRoom: "Удалить",
      roomDeleted: "Зона выращивания удалена",
      claimInvalidContext: "Невозможно определить ID контроллеры или контекст зоны выращивания.",
      claimFailed: "Не удалось привязать контроллер.",
      claimSuccess: "Связь установлена! Контроллер активировал зону выращивания.",
      notConnectedTitle: "Зона выращивания не подключена",
      notConnectedDescription:
        "Добавьте новый Shefa Green Controller в эту зону выращивания, чтобы запустить телеметрию и управление.",
      connectArgon: "Подключить Shefa Green Controller",
      connectModalTitle: "Подключение Shefa Green Controller",
      step1: "Шаг 1 из 3",
      step2: "Шаг 2 из 3",
      step3: "Шаг 3 из 3",
      prepareDeviceTitle: "Подготовка контроллеры",
      prepareDeviceDescription:
        "Включите Shefa Green Controller в розетку. Убедитесь, что он мигает синим - это режим готовности к привязке.",
      searchingHint: "Shefa Green Controller ищет свою зону выращивания...",
      identifyDeviceTitle: "Идентификация контроллеры",
      identifyDeviceDescription: "Сканируйте QR-код контроллеры или введите deviceId вручную.",
      scanQr: "Сканировать QR",
      deviceIdLabel: "Controller ID",
      deviceIdPlaceholder: "например, 34003f001447343339383037",
      connectDevice: "Подключить",
      claimingInProgress: "Ищем Shefa Green Controller и устанавливаем связь...",
      updatingRoomData: "Обновляем данные зоны выращивания...",
      next: "Продолжить",
      back: "Назад",
      close: "Закрыть",
      statusOnline: "Online",
      statusOffline: "Offline",
      lightDay: "Световой день",
      lightNight: "Ночь",
      lightAlertLampOff: "Внимание: лампа не горит?",
      summaryTotalRooms: "Всего зон выращивания",
      summaryActiveControllers: "Активных контроллеров",
      summaryProblems: "Проблем",
      modeGrowing: "Растем",
      modeSleeping: "Спим",
      modeNeedsSetup: "Требуется настройка",
      setupRequired: "Требуется настройка: подключите контроллер для запуска мониторинга.",
      kpiTemperature: "Температура",
      kpiHumidity: "Влажность",
      kpiConnection: "Связь",
      kpiWifiRssi: "Wi-Fi (RSSI)",
      actionSettings: "Настройки",
      actionCharts: "Графики",
      actionBlink: "Мигнуть",
      refreshingStatuses: "Обновляем статусы зон выращивания...",
      breadcrumbOverview: "Обзор",
      kpiCo2: "CO₂",
      kpiDevicesOnline: "Контроллеры онлайн",
      growerDiarySummaryTitle: "Сводка по дням",
      growerDiarySummaryHint:
        "Периоды «День» и «Ночь» задаются по статусу света в телеметрии (day_status), а не по времени суток.",
      growerDiaryTablePlaceholder:
        "Здесь будет таблица минимумов и максимумов за световой день и ночь.",
      tableDate: "Дата",
      tableMetric: "Параметр",
      tablePeriodDay: "День (свет вкл)",
      tablePeriodNight: "Ночь (свет выкл)",
      tableMin: "Мин",
      tableMax: "Макс",
      diaryAnalyticsTitle: "Дневник и Аналитика",
      analyticsLoading: "Загрузка сводки…",
      analyticsError: "Не удалось загрузить сводку",
      analyticsTruncated: "Обрезка выборки",
      analyticsTruncatedBanner: (cap) =>
        `В расчёт вошли только последние ${cap} измерений в окне; более ранние точки не учтены. При необходимости сузьте период или набор устройств.`,
      analyticsSamples: "Точек в расчёте",
      colPeriod: "Период",
      colTempMm: "Темп. мин–макс",
      colRhMm: "Влажн. мин–макс",
      colCo2Avg: "CO₂ ср.",
      colVpdAvg: "VPD ср.",
      colDailyNote: "Заметка дня",
      dailyNoteSave: "Сохранить",
      dailyNoteSaved: "Сохранено",
      periodDayShort: "День",
      periodNightShort: "Ночь",
    },
    invitePage: {
      title: "Присоединиться к объекте как сотрудник",
      invalidLink: "Ссылка-приглашение недействительна.",
      missingToken: "Токен приглашения отсутствует.",
      registrationFailed: "Регистрация не удалась.",
      emailPlaceholder: "Email",
      passwordPlaceholder: "Пароль",
      creatingAccount: "Создаем аккаунт...",
      createAccount: "Создать аккаунт",
    },
    unassignedPage: {
      title: "Свободные контроллеры",
      subtitle: "Назначьте объект каждому контроллеру.",
      colDevice: "Контроллер",
      colFarm: "Объект",
      apply: "Назначить объект",
      loading: "Загрузка…",
      empty: "Нет свободных контроллеров",
      loadError: "Не удалось загрузить",
      saveError: "Не удалось назначить объект",
      saved: "Объект обновлена",
    },
    adminControllers: {
      title: "Все контроллеры",
      subtitle: "Список контроллеров по всем объектам. Поиск и фильтры применяются на этой странице.",
      searchLabel: "Поиск",
      searchPlaceholder: "ID, имя, объект или зона выращивания…",
      statusFilter: "Статус",
      statusAll: "Все",
      statusOnline: "Онлайн",
      statusOffline: "Оффлайн",
      displayFilter: "Тип дисплея",
      displayAll: "Все",
      colControllerId: "ID контроллера",
      colDeviceName: "Имя",
      colFarm: "Объект",
      colRoom: "Зона выращивания",
      colStatus: "Статус",
      colHardware: "Конфигурация",
      colLastSeen: "Последняя активность",
      colActions: "Действия",
      badgeOnline: "Онлайн",
      badgeOffline: "Оффлайн",
      viewLogs: "Логи",
      reboot: "Перезапуск",
      rebootConfirm: "Перезагрузить этот контроллер (soft reset)?",
      rebootFailed: "Не удалось выполнить перезапуск",
      unbind: "Отвязать",
      unbindTitle: "Отвязать от объекты и зоны выращивания",
      unbindModalTitle: "Отвязать контроллер",
      unbindModalBody:
        "Вы уверены, что хотите отвязать контроллер {{id}} от объекта «{{farm}}»? Контроллер вернётся в список свободных контроллеров.",
      unbindConfirm: "Отвязать",
      unbindCancel: "Отмена",
      unbindFailed: "Не удалось отвязать",
      assign: "К зоне выращивания",
      assignTitle: "Привязать к объекте и зоне выращивания",
      assignModalTitle: "Привязать к объекте и зоне выращивания",
      assignModalHint: "Выберите объект и зону выращивания — в записи контроллера обновятся farmId и roomId.",
      assignFarmLabel: "Объект",
      assignFarmPlaceholder: "Выберите объект…",
      assignRoomLabel: "Зона выращивания",
      assignRoomPlaceholder: "Выберите зону выращивания…",
      assignRoomLoading: "Загрузка зон выращивания…",
      assignSubmit: "Привязать",
      assignCancel: "Отмена",
      assignFailed: "Не удалось привязать",
      assignNeedFarmRoom: "Укажите объект и зону выращивания",
      controllerTypeWater: "Водяной контроллер",
      controllerTypeClimate: "Климат-контроллер",
      controllerTypeGeneric: "Контроллер",
      empty: "Нет устройств по фильтру",
      loading: "Загрузка…",
      loadError: "Не удалось загрузить список",
    },
    deviceAssignment: {
      roomLabel: "Зона выращивания",
      roomNone: "Без зоны выращивания",
      roomSave: "Сохранить зону выращивания",
      releaseDevice: "Освободить контроллер",
      releaseConfirm:
        "Снять привязку к объекте и зоне выращивания? Контроллер станет доступен для назначения на другую объект (реестр суперпользователя).",
      releaseDone: "Контроллер освобождено",
      releaseError: "Не удалось освободить",
      releaseHint: "Только суперпользователь — сбрасывает объект и зону выращивания.",
      unbindFarm: "Отвязать от объекта",
      unbindConfirm:
        "Отвязать контроллер от объекта? Он появится в реестре свободных до повторной привязки.",
      unbindDone: "Контроллер отвязано",
      unbindError: "Не удалось отвязать",
      superuserOnly: "Только суперпользователь",
    },
    loading: "Загрузка...",
    noNews: "Новостей пока нет",
    updated: "Обновлено",
    refresh: "Обновить",
    toggleNavigation: "Переключить навигацию",
    retry: "Повторить",
    showOffline: (n) => `Показать офлайн контроллеры (${n})`,
    hideOffline: "Скрыть офлайн контроллеры",
    noData: "Нет данных",
    no_data: "Нет данных",
    statusOn: "ON",
    statusOff: "OFF",
    co2CheckTank: "Проверьте баллон",
    firmwareUpload: "Загрузка прошивки",
    upload: "Загрузить",
    cuttings: "Черенки",
    news: "Новости",
    latestReadings: "Последние показания",
    loginFailed: "Не удалось войти",
    copyToClipboard: "Скопировать в буфер обмена:",
    saveFailed: "Не удалось сохранить",
    settingsSavedSuccess: "Настройки успешно сохранены",
    yes: "Да",
    no: "Нет",
    timeSyncTitle: "Синхронизация времени",
    refreshTimeSync: "Обновить",
    timeSyncHint: "Используйте NTP как основной источник времени. Контроллер должен считать в UTC и применять timezone только для логики день/ночь.",
    serverTimeLabel: "Время сервера",
    controllerTimeLabel: "Время контроллера",
    timeDriftLabel: "Разница (drift)",
    timeUnknown: "Неизвестно",
    farmTimezoneLabel: "Часовой пояс объекта",
    farmTimezoneHint: "Сохраните IANA timezone (например, Asia/Jerusalem). Это значение возвращается из /api/system/time и применяется на контроллере для режима день/ночь.",
    saveTimezone: "Сохранить часовой пояс",
    co2ManagementTitle: "Управление CO2",
    co2OffsetLabel: "Значение калибровки CO2 (ppm)",
    co2OffsetHint: "Коррекция показаний относительно эталона VDL.",
    calibrationTargetPpmPlaceholder: "Стандарт: 400. Для Мёртвого моря: 580",
    syncNow: "Синхронизировать сейчас",
    forceCalibrateButton: "Начать калибровку",
    forceCalibrateHint: "Запускать только после 5 мин проветривания.",
    calibrationStatus: {
      pending: "⏳ Ожидание синхронизации с контроллером...",
      started: (left) => `✅ Калибровка запущена (5 мин) ${left}`,
    },
    manualTimeLabel: "Ручное время",
    setManualTime: "Установить время",
    manualTimeHint: "Используйте только в изолированных сетях без NTP. Команда отправляет Unix-time в UTC вместе с часовым поясом объекта.",
    waterPage: {
      idLabel: "ID",
      firmwareLabel: "Прошивка",
      range2h: "2ч",
      range24h: "24ч",
      range7d: "7д",
      rangeCustom: "Период",
      latestTitle: "Последние данные",
      noHistory: "История воды пока отсутствует.",
      inletLine: "Вход",
      outletLine: "Выход",
      litersPerMinuteShort: "л/мин",
      litersShort: "л",
      successOk: "ОК",
      genericError: "Ошибка",
    },
    deviceSettingsPage: {
      calibrationVentilationConfirm: "Убедитесь, что помещение проветривалось не менее 5 минут перед нажатием.",
      timezonePlaceholder: "Asia/Jerusalem",
      co2OffsetPlaceholder: "0",
      currentValue: "Текущее",
      co2OffsetSaveHint:
        "Смещение CO₂ из блока выше сохраняется вместе с часовым поясом кнопкой «Сохранить часовой пояс».",
      advancedFunctionsTitle: "Расширенные команды Particle",
      vpdProPlanLock:
        "Анализ VPD доступен в тарифах Pro и Enterprise. Обновите план объекта, чтобы менять тип VPD.",
      locked: "заблокировано",
    },
    deviceSettingsModal: {
      identityTitle: "Контроллер",
      identityHint: "Название помогает быстро отличить этот контроллер в зонах выращивания и на дашборде.",
      notificationsTitle: "Уведомления",
      notificationsHint: "Выберите, какие события должны приходить в Telegram и журнал объекта.",
      actionsTitle: "Действия контроллера",
      actionsHint: "Ручные сервисные команды для выбранного контроллера.",
      restartButton: "Перезапустить контроллер",
      restartConfirmTitle: "Подтвердите перезапуск контроллера",
      restartWarning: "Контроллер может быть недоступен около 30-90 секунд во время перезагрузки. Реле, пороги, режимы и EEPROM-настройки не изменятся.",
      restartConfirmButton: "Да, перезапустить",
      restartSending: "Отправляем команду",
      restartAccepted: "Команда перезапуска принята. Контроллер перезагружается и может быть офлайн 30-90 секунд.",
      restartFailed: "Не удалось перезапустить контроллер.",
      restartNotReachable: "Перезапуск не выполнен: контроллер сейчас недоступен.",
      thresholdHint: "Эти значения задают рабочий диапазон для дня и ночи. Сохраняйте их отдельно от ручного управления розетками.",
      co2ManagementHint: "Калибровку запускайте только при стабильном эталонном воздухе.",
      outletOn: "розетка включена",
      outletOff: "розетка выключена",
      modeAutoBadge: "A",
      modeManualOnBadge: "M:ON",
      modeManualOffBadge: "M:OFF",
      relayModeAria: (label) => `${label}: режим розетки`,
      modeAuto: "Авто",
      modeManualOn: "Ручная блокировка: вкл",
      modeManualOff: "Ручная блокировка: выкл",
      deviceNameTitle: "Имя контроллеры",
      deviceNamePlaceholder: "Название контроллеры",
      telegramAlerts: "Telegram-уведомления",
      powerLoss: "Потеря питания",
      thresholdAlerts: "Уведомления по порогам",
      trendAlerts: "Уведомления по трендам",
      climateDeliveryOn: "Климатические уведомления доставляются",
      climateDeliveryBlocked: "Климатические уведомления не будут доставлены",
      climateDeliveryOff: "Климатические уведомления выключены",
      climateDeliveryCounts: (enabled, total) => `Получатели Climate alerts: ${enabled}/${total}`,
      climateDeliveryNoRecipients:
        "Уведомления контроллера включены, но нет привязанных Telegram-получателей для климатических оповещений.",
      climateDeliveryClimateDisabled:
        "Уведомления контроллера включены, но у всех доступных Telegram-получателей выключена категория Climate alerts.",
      climateDeliveryTelegramSettingsHint:
        "Откройте настройки уведомлений в Telegram-боте и включите категорию Climate alerts.",
      enableClimateAlertsForMe: "Включить Climate alerts для меня",
      enablingClimateAlerts: "Включаем Climate alerts",
      climateAlertsEnabledForMe: "Climate alerts включены для вашего Telegram.",
      climateAlertsEnableFailed: "Не удалось включить Climate alerts.",
      co2Management: "Управление CO2",
      co2Offset: "Значение калибровки CO2 (ppm)",
      startCalibration: "Начать калибровку",
      calibrationTargetPpmLabel: "Значение калибровки (PPM)",
      calibrationTargetPpmPlaceholder: "Стандарт: 400. Для Мёртвого моря: 580",
      calibrationHint: "Запускать только после 5 мин проветривания",
      calibrationConfirm: "Вы уверены? Запускать только после 5 минут проветривания!",
      relayManagement: "Управление розетками",
      controlModelHint: "По умолчанию розетками управляет сценарий. Ручное управление отображается как особый режим переопределения.",
      waterMetric: { ph: "pH", ec: "EC", orp: "ORP", temp: "Температура", level: "Уровень воды", volume: "Объем" },
    },
    deviceCard: {
      unknownDeviceName: "Климат-контроллер",
      idLabel: "ID",
      idDialogTitle: "ID контроллера",
      idPreviewAria: "Показать полный ID контроллера и скопировать его",
      copyId: "Копировать ID",
      copied: "Скопировано",
      connectivityOnline: "Онлайн",
      connectivityNoRecentData: "Нет свежих данных",
      connectivityOffline: "Офлайн",
      controllerOfflineCommands: "Контроллер офлайн. Команды не выполняются.",
      syncTimeFailed: "Не удалось синхронизировать время",
      calibrationFailed: "Калибровка не удалась",
      climateParamsUpdated: "Параметры климата обновлены",
      relayCommandTimeout: "Ошибка: контроллер не подтвердило выполнение команды (таймаут)",
      relayCommandUnconfirmed:
        "Режим сохранён, но контроллер не прислал подтверждение. Если отображение не совпадает с устройством, обновите страницу.",
      modeLabel: "режим",
      errorGeneric: "Ошибка",
      invalidThreshold: "Неверный порог: укажите числовые Min/Max и проверьте, что Min <= Max.",
    },
    settingsApplyFailedPrefix: "Не удалось применить",
    settingsProgressSavingDb: "Сохраняем настройки в базе...",
    settingsProgressApplyingController: "Применяем пороги на контроллер",
    settingsProgressControllerCheck: "Пороги в базе сохранены, проверяем контроллер...",
    settingsProgressSavingSystem: "Сохраняем системные параметры...",
    settingsProgressSyncingView: "Синхронизируем отображение...",
    co2OffsetNotSavedWarning: "CO2 Offset не сохранён (timezone endpoint недоступен)",
    noCuttings: "Черенков нет",
    noStrains: "Сортов не найдено",
    noBugs: "Нет багов для отображения",
    login: "Войти",
    logout: "Выйти",
    profile: {
      title: "Профиль",
      subtitle: "Данные аккаунта и привязка Telegram.",
      farm: "Объект",
      linkTelegram: "Подключить Telegram",
      telegramLinked: "Telegram подключён",
      telegramNotLinked: "Telegram не подключён",
      openTelegramBot: "Открыть Telegram-бота",
      disconnectTelegram: "Отключить Telegram",
      disconnectingTelegram: "Отключаем Telegram…",
      disconnectError: "Не удалось отключить Telegram. Попробуйте позже.",
      linkError: "Не удалось получить ссылку. Попробуйте позже.",
      openingBot: "Открываем бота…",
      refreshStatus: "Обновить статус",
      telegramHint:
        "Подключите Telegram, чтобы получать оповещения. После подключения здесь можно открыть бота или отключить Telegram.",
    },
    telegramConnect: {
      eyebrow: "Telegram",
      title: "Подключение аккаунта Telegram",
      description:
        "Подключите аккаунт Telegram, чтобы получить доступ к объектам, зонам выращивания, оповещениям и инструментам контроллеров.",
      button: "Подключить Telegram",
      connecting: "Подключаем Telegram...",
      linkError: "Не удалось создать ссылку Telegram. Попробуйте позже.",
      signInAgain: "Что-то пошло не так. Войдите снова.",
      rateLimit: "Подождите несколько секунд и попробуйте снова.",
      retryButton: "Повторить подключение",
      openTelegramButton: "Открыть Telegram и завершить подключение",
      copyConnectCodeButton: "Скопировать код подключения",
      copyConnectCodeDone: "Код подключения скопирован",
      copyError: "Не удалось скопировать код. Скопируйте его вручную.",
      widgetLoading: "Загружаем кнопку входа Telegram...",
      widgetLoadError: "Не удалось загрузить кнопку входа Telegram. Обновите страницу или используйте резервный способ.",
      manualFallbackHint: "Если Telegram откроется без кода запуска, скопируйте код подключения и вставьте его в чат с ботом.",
      afterLoginHint: "Если вы ещё не вошли, сначала выполните вход — подключение продолжится автоматически.",
    },
    rateLimitPage: {
      eyebrow: "Shefa Green",
      title: "Немного замедлитесь",
      body:
        "Мы временно ограничили число запросов с вашего браузера из‑за необычно высокой активности. Так приложение остаётся стабильным для всех.",
      hint: "Подождите несколько минут, затем обновите страницу или нажмите кнопку ниже, чтобы повторить попытку.",
      ctaRetry: "Попробовать снова",
      ctaHome: "На главную",
      tabsHint:
        "Если открыто несколько вкладок Shefa Green, закройте лишние — так страница не будет отправлять лишние фоновые запросы.",
      retryIn: (totalSeconds) => {
        const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
        if (s <= 0) return "Можно попробовать снова.";
        if (s >= 120) {
          const m = Math.ceil(s / 60);
          return `Повторите попытку примерно через ${m} мин.`;
        }
        return `Повторите попытку примерно через ${s} с.`;
      },
    },
    theme: {
      dark: "Тёмная",
      light: "Светлая",
      system: "Как в системе",
      toggleAria: "Переключить тему: тёмная, светлая или как в системе",
      tapNext: "нажмите для переключения",
      farmMenu: "Контекст объекта",
      farmHomeToken: "Домашняя объект (токен)",
    },
    username: "Логин",
    email: "Email",
    password: "Пароль",
    users: "Пользователи",
    addUser: "Добавить пользователя",
    role: "Роль",
    delete: "Удалить",
    manageCuttings: "Управление черенками",
    byName: "По названию",
    byCode: "По коду",
    both: "Оба",
    manageStrains: "Управление сортами",
    manageReviews: "Проф. обзоры",
    manageNews: "Управление новостями",
    addCutting: "Добавить черенок",
    addStrain: "Добавить сорт",
    addNews: "Добавить новость",
    reportBug: "Сообщить об ошибке",
    strainsLoadError: "Не удалось загрузить список сортов.",
    strainsAdminCrashed: "Произошла ошибка при загрузке страницы управления сортами.",
    pin: "Закрепить",
    unpin: "Открепить",
    pinnedBugs: (n) => `\uD83D\uDCCC Закреплённые баги (${n})`,
    recentlyCreated: "Создан недавно",
    recentUpdate: "Недавнее обновление",
    closeBug: "Закрыть",
    closedBugs: (n) => `Закрытые баги (${n})`,
    showClosed: "Показать закрытые баги",
    hideClosed: "Скрыть закрытые баги",
    showOnlyMine: "Только мои баги",
    urgentOnly: "Только срочные",
    sort: "Сортировка",
    sortByPriority: "По приоритету",
    sortByDateDesc: "Новые → старые",
    sortByDateAsc: "Старые → новые",
    sortByStatus: "По статусу",
    shownOfTotal: (s, t) => `Показано ${s} из ${t} багов`,
    addTask: "Добавить задачу",
    addComment: "Добавить комментарий",
    priority: "Приоритет",
    priorityLabel: (value) => `Приоритет: ${value}`,
    priorityHigher: "Выше",
    priorityLower: "Ниже",
    lastActivity: "Последняя активность",
    showAllComments: "Показать все комментарии",
    noCommentsYet: "Комментариев пока нет",
    completeTask: "Выполнено",
    showCompleted: "Показать завершённые",
    hideCompleted: "Скрыть завершённые",
    tasksDashboard: {
      subtitle: "Ежедневный центр управления",
      summaryUrgent: "Срочные",
      summaryToday: "Сегодня",
      summaryOverdue: "Просрочено",
      summarySystem: "От системы",
      summaryCompleted: "Выполнено",
      sectionToday: "Задачи на сегодня",
      sectionProblems: "Требуют внимания",
      sectionScheduled: "Запланировано",
      sectionHistory: "История и выполненные",
      tabToday: "Сегодня",
      tabProblems: "Проблемы",
      tabScheduled: "Расписание",
      tabHistory: "История",
      quickAddTitle: "Быстрое добавление",
      recommendationsTitle: "Авторекомендации",
      recommendationsHint:
        "Когда будет включена автоматизация по датчикам, здесь появятся задачи по аномалиям.",
      recommendationsEmptyPrimary: "Нет активных системных рекомендаций.",
      recommendationsEmptySecondary:
        "Рекомендации появятся здесь, когда данные по климату, поливу, подключению контроллеров или оборудованию выйдут за ожидаемый диапазон.",
      noTasksInView: "В этом разделе пока нет задач.",
      emptyToday:
        "На сегодня задач нет. Здесь показываются только активные: без срока, со сроком на сегодня или просроченные. Задания с датой после сегодня — во вкладке «Расписание».",
      emptyProblems:
        "Задач с проблемами нет. Эта вкладка собирает просроченные, срочный приоритет и статусы «не хватает» или «отклонено».",
      emptyScheduled:
        "Нет задач с датой после сегодня. Добавьте срок позже текущего дня или откройте «Сегодня» для текущей работы.",
      emptyHistory: "Завершённых задач пока нет — они появятся здесь после отметки «готово».",
      completedSectionTitle: "Завершённые задачи",
      postpone: "Отложить",
      postponeModalTitle: "Отложить задачу",
      postponeLaterToday: "Позже сегодня",
      postponeTomorrowMorning: "Завтра утром",
      postponeTomorrowEvening: "Завтра вечером",
      postponeChooseDateTime: "Выбрать дату и время",
      postponeApply: "Применить",
      postponeFailed: "Не удалось изменить срок. Попробуйте ещё раз.",
      postponeInvalidDate: "Укажите корректную дату и время.",
      openDetails: "Подробнее",
      dueLabel: "Срок (дата и время)",
      addPhotos: "Фото",
      roomOrLocation: "Зона выращивания / место",
      optionalLinksHint: "По желанию: привязка к объекту инфраструктуры (если данные загрузились).",
      linkRoom: "Зона выращивания",
      linkDevice: "Контроллер / контроллер",
      linkStrain: "Сорт",
      linkCutting: "Черенок",
      linkNoneOption: "—",
      cuttingLabel: "Черенок",
      groupNoRoom: "Без зоны выращивания / не привязано",
      groupTaskCount: (n) => {
        const abs = Math.abs(Number(n)) || 0;
        const mod10 = abs % 10;
        const mod100 = abs % 100;
        if (mod100 >= 11 && mod100 <= 14) return `${abs} задач`;
        if (mod10 === 1) return `${abs} задача`;
        if (mod10 >= 2 && mod10 <= 4) return `${abs} задачи`;
        return `${abs} задач`;
      },
      filterRoom: "Зона выращивания",
      filterTaskType: "Тип задачи",
      filterPriority: "Приоритет",
      filterSource: "Источник",
      filterSearch: "Поиск",
      filterSearchPlaceholder: "Описание, место, зона выращивания, контроллер, сорт, черенок, комментарии…",
      filterAllRooms: "Все зоны выращивания",
      filterAllTypes: "Все типы",
      filterAllPriorities: "Все приоритеты",
      filterAllSources: "Все источники",
      clearFilters: "Сбросить фильтры",
      emptyFiltered: "Нет задач по этим фильтрам. Измените условия или нажмите «Сбросить».",
      sourceManual: "Вручную",
      sourceSystem: "Система",
      pageTitle: "Задачи",
      completeTask: "Выполнено",
      showCompleted: "Показать завершённые",
      hideCompleted: "Скрыть завершённые",
      addTask: "Добавить задачу",
      addComment: "Добавить комментарий",
      fieldDescription: "Описание",
      fieldPriority: "Приоритет",
      fieldComment: "Комментарий",
      statusInProgress: "В работе",
      statusNeeds: "Не хватает",
      statusDone: "Готово",
      statusRejected: "Отклонено",
      completedAtLabel: "Выполнено",
      lastActivity: "Последняя активность",
      priorityUrgent: "Срочно",
      systemReasonSection: "Системная причина",
      systemReasonPrimaryLabel: "Причина",
      systemTriggerRange: (min, max) => `Допустимо ${min}–${max}`,
      systemTriggerMinOnly: (min) => `от ${min}`,
      systemTriggerMaxOnly: (max) => `до ${max}`,
      systemTriggeredAt: "Сработало",
      systemTriggerTypeLabel: "Тип срабатывания",
      systemTriggerMetricLabel: "Метрика",
      systemTriggerValueLabel: "Значение",
      systemAllowedRangeLabel: "Допустимый диапазон",
      taskType: "Тип задачи",
      systemTask: "Системная",
      reason: "Комментарий",
      noDueDate: "Без срока",
      priorityLow: "Низкий",
      priorityMedium: "Средний",
      priorityHigh: "Высокий",
      taskTypes: {
        climate: "Климат",
        watering: "Полив",
        plant_inspection: "Осмотр растений",
        equipment: "Оборудование",
        cuttings: "Черенки",
        nutrition: "Питание",
        pests_disease: "Вредители / болезни",
        cleaning: "Уборка",
        other: "Другое",
      },
    },
    openedBy: "Открыл",
    details: "Детали",
    tasks: "Задачи",
    bugs: "Баги",
    open: "Открыто",
    inProgress: "В работе",
    testing: "На тестировании",
    urgent: "Срочно",
    veryUrgent: "Очень срочно",
    needs: "Не хватает",
    done: "Готово",
    rejected: "Отклонено",
    fixed: "Исправлено",
    closed: "Закрыто",
    edit: "Редактировать",
    update: "Обновить",
    cancel: "Отмена",
    save: "Сохранить",
    title: "Заголовок",
    name: "Название",
    content: "Текст",
    description: "Описание",
    strain: "Сорт",
    status: "Статус",
    location: "Локация",
    code: "Код",
    history: "История",
    statusHistory: "История статусов",
    statusHistoryEmpty: "История изменений пуста",
    statusHistoryFrom: "Из",
    statusHistoryTo: "В",
    statusHistoryBy: "Автор",
    statusHistoryAt: "Когда",
    link: "Ссылка",
    event: "Событие",
    comment: "Комментарий",
    photo: "Фото",
    screenshot: "Скриншот",
    savePhoto: "Сохранить фото",
    photoSaved: "Фото сохранено",
    photoShortName: "Короткое имя",
    photoDescriptionDetailed: "Описание (подробно)",
    photoIncludeInDiary: "В дневник",
    photoDiaryOnly: "Только дневник",
    photoStage: "Этап",
    photoStageFilter: "Фильтр по этапу",
    photoStageAll: "Все этапы",
    photoTakenAt: "Временная метка (таймлайн)",
    photoPosition: "Позиция",
    photoMove: "Перемещение по таймлайну",
    moveLeft: "Влево",
    moveRight: "Вправо",
    downloadDiary: "Скачать дневник",
    diaryExportTooMany: (limit) => `Экспорт ограничен ${limit} фото`,
    diaryExportNoPhotos: "Нет фото, отмеченных для дневника",
    photoDetails: "Карточка фото",
    photoStageLabels: {
      cut: "Срез",
      veg: "Вега",
      flowering: "Цветение",
      harvest: "Сбор",
      drying: "Сушка",
      trim: "Тримминг",
      curing: "Кюринг",
      lab_tests: "Лаб. анализы",
      issue: "Проблема",
    },
    gardenerDiary: "Дневник садовода",
    viewReviewsForStrain: "Смотреть отзывы по этому сорту",
    legacyDiaryReadOnly:
      "Дневник устарел и доступен только для чтения. Для новых оценок используйте Профессиональные обзоры.",
    goToReviews: "Перейти к обзорам",
    addLog: "Добавить запись",
    confirmDelete: "Удалить?",
    author: "Автор",
    id: "ID",
    slug: "Идентификатор",
    genetics: "Генетика",
    geneticsType: "Тип генетики",
    geneticsDominance: "Доминирование (%)",
    shortDescription: "Короткое описание",
    thcPercent: "THC %",
    cbdPercent: "CBD %",
    floweringWeeks: "Цветение (недели)",
    floweringCategory: "Категория цветения",
    floweringEarly: "Раннее",
    floweringMedium: "Среднее",
    floweringLate: "Позднее",
    yield: "Урожайность",
    yieldLow: "Низкая",
    yieldMedium: "Средняя",
    yieldHigh: "Высокая",
    yieldVeryHigh: "Очень высокая",
    plantHeight: "Высота растения",
    heightLow: "Низкая",
    heightMedium: "Средняя",
    heightHigh: "Высокая",
    terpenes: "Терпены",
    aromaFlavor: "Аромат/вкус",
    effectDirection: "Направление эффекта",
    effectUp: "Вверх",
    effectNeutral: "Нейтрально",
    effectDown: "Вниз",
    effectSensations: "Ощущения",
    uniqueTrait: "Уникальная черта",
    positioning: "Позиционирование",
    positioningCommercial: "Коммерческий",
    positioningBoutique: "Бутик",
    positioningExotic: "Экзотик",
    positioningNiche: "Нишевый",
    recommendedTime: "Рекомендуемое время",
    recommendedDay: "День",
    recommendedEvening: "Вечер",
    recommendedNight: "Ночь",
    targetAudience: "Целевая аудитория",
    audienceBeginners: "Новички",
    audienceExperienced: "Опытные",
    audienceUniversal: "Универсально",
    passportCore: "CORE",
    passportCorePlus: "CORE+",
    passportExtended: "EXTENDED",
    tagLimit: "Максимум 6 тегов (эффекты — 5).",
    imageLimit: "Максимум 5 изображений.",
    maxImages: "Достигнут лимит изображений.",
    extendedLabReports: "Лабораторные PDF/ссылки (через запятую)",
    extendedFullCannabinoidProfile: "Полный профиль каннабиноидов",
    extendedSecondaryTerpenes: "Вторичные терпены (через запятую)",
    extendedCultivationNotes: "Заметки по выращиванию",
    extendedInternalCommercialNotes: "Внутренние коммерческие заметки",
    statusRd: "R&D",
    statusTest: "Тест",
    statusPreCommercial: "Предкоммерческий",
    statusCommercial: "Коммерческий",
    weeks: "нед.",
    images: "Изображения",
    publicUrl: "Публичный URL",
    transplant: "пересадка",
    treatment: "обработка",
    move: "перемещение",
    viewMore: "Подробнее",
    collapse: "Свернуть",
    duplicate: "Дублировать",
    downloadQrPdf: "Скачать QR (PDF)",
    downloadAllQrPdf: "Скачать все QR (PDF)",
    downloadExcel: "Скачать все QR (Excel)",
    downloadExcelReport: "Скачать Excel",
    downloadReadings: "Показания…",
    downloadSettingsChanges: "Изменения настроек…",
    from: "От",
    to: "До",
    download: "Скачать",
    downloadFailed: "Не удалось скачать файл",

    labelModes: {
      nameOnly: "Только название",
      numberOnly: "Только номер",
      nameAndNumber: "Название + номер",
    },
    copyQrText: "Копировать текст QR",
    copyLabelText: "Копировать текст ярлыка",
    export: "Экспорт",
    hide: "Скрыть",
    readMore: "Читать далее",
    currentReadings: "Актуальные показания",
    thresholdValues: "Пороговые значения",
    day: "День",
    night: "Ночь",
    dayNight: "День/Ночь",
    preset: "Пресет",
    socketStates: "Состояние розеток",
    temperature: "Температура",
    humidity: "Влажность",
    co2: "CO₂",
    vpd: "VPD",
    light: "PPFD",
    lightStatus: "Статус света",
    moisture: "Влажность почвы",
    ec: "EC",
    ph: "pH",
    nitrogen: "N",
    phosphorus: "P",
    potassium: "K",
    inMedium: "В субстрате",
    inAir: "В воздухе",
    search: "Поиск",
    all: "Все",
    climate: "Климат",
    waterInAndOut: "Вода вход/выход",
    noDevicesFound: "Контроллеры не найдены",
    waterDevices: "Водные контроллеры",
    waterTrends: "Тренды воды",
    waterHistoryRange: "Диапазон истории воды",
    noWaterHistory: "Истории воды пока нет.",
    waterTemperature: "Температура воды",
    waterLevel: "Уровень воды",
    waterVolume: "Объём воды",
    tank: "Бак",
    of: "из",
    maxHeight: "Макс. высота",
    centimetersShort: "см",
    noTankData: "Нет данных по баку",
    water: {
      inlet: "Вход",
      outlet: "Выход",
      outletStatus: "Выход",
      units: {
        litersShort: "л",
        centimetersShort: "см",
      },
      hints: {
        ph_optimal: "Оптимальный диапазон",
      },
      errors: {
        ph_out_of_range: "pH вне диапазона",
        low_level: "Низкий уровень воды",
        temp_high: "Внимание: температура выше 28°C",
      },
    },
    time: "Время",
    liters: "Литры (л)",
    summary: "Сводка",
    autoRefresh: "Автообновление",
    getConfig: "Получить конфигурацию",
    pushNow: "Отправить сейчас",
    softResetConfirm: "Перезагрузить контроллер?",
    apply: "Применить",
    config: "Конфигурация",
    calibrateIn: "Калибровать вход",
    calibrateOut: "Калибровать выход",
    inTotalDelta: "Вход всего Δ",
    outTotalDelta: "Выход всего Δ",
    avg: "среднее",
    displayIntervalLabel: "Интервал дисплея",
    serverIntervalLabel: "Интервал сервера",
    lastTs: "Последняя метка времени",
    inPpl: "Вход ppl",
    outPpl: "Выход ppl",
    unknown: "неизвестно",
    online: "онлайн",
    date: "Дата",
    marketName: "Рыночное название",
    internalNumber: "Внутренний #",
    firmwareUpdate: "Обновление прошивки",
    latest: "Последняя",
    lastUploaded: "Загружено",
    flash: "Прошить",
    photoUploading: "Загрузка фото...",
    photoTooLarge: "Превышен размер (макс. 3 MB)",
    unsupportedFormat: "Неподдерживаемый формат",
    tooManyFiles: "Слишком много файлов",
    editPhoto: "Редактировать фото",
    rotateLeft: "Повернуть влево",
    rotateRight: "Повернуть вправо",
    zoom: "Масштаб",
    uploadFailed: "Ошибка загрузки",
    flashFailed: "Ошибка прошивки",
    invalidDeviceId:
      "Неверный формат ID контроллеры. Проверьте, что он введён без пробелов и содержит не менее 24 символов.",
    getFreshData: "Обновить данные",
    notifyOnOffline: "Сообщать при отключении питания",
    powerLossAlerts: "Оповещения в Telegram при пропадании питания",
    telegramAlertsOn: "Уведомления Telegram: ВКЛ",
    telegramAlertsOff: "Уведомления Telegram: ВЫКЛ",
    trendAlerts: "Тенденция уведомлений",
    climateCharts: "Графики климата",
    climateNoData24h: "Нет данных за последние 24 часа.",
    climateVpdProOnly: "Анализ VPD доступен в тарифе Pro и Enterprise.",
    climateExpandDetails: "Развернуть детали",
    climateHideDetails: "Скрыть детали",
    climateRelays: "Реле",
    climateSetpointsDay: "Пороги: День",
    climateSetpointsNight: "Пороги: Ночь",
    climateSaveThresholds: "Сохранить пороги",
    climateSaving: "Сохранение...",
    climateLightStatus: "Освещение",
    climateNightPhaseInRoom: "🌙 НОЧНАЯ ФАЗА (В КОМНАТЕ)",
    last24Hours: "за последние 24 часа",
    settings: "Настройки",
    deviceSettings: "Настройки контроллера",
    deviceSettingsBrandTitle: "Shefa Green Controller — настройки",
    deviceSettingsBrandSubtitle: (deviceId) =>
      deviceId ? `ID контроллера: ${deviceId}` : "",
    backToController: "Назад к контроллеру",
    backToControllers: "Назад ко всем контроллерам",
    selectFunction: "Выберите функцию",
    commandSuccess: "✅ Команда выполнена успешно",
    commandError: "❌ Ошибка: проверь аргумент",
    particleAuthError:
      "Ошибка авторизации. Проверьте access_token и принадлежность контроллеры вашему аккаунту.",
    refreshReadings: "Обновить показания",
    refreshConfig: "Обновить конфигурацию",
    displayInterval: "Интервал дисплея (мин)",
    serverInterval: "Интервал сервера (мин)",
    saveConfig: "Сохранить конфигурацию",
    loadConfig: "Загрузить конфигурацию",
    factoryReset: "Заводской сброс",
    wipeEEPROM: "Очистить EEPROM",
    relayLabels: { A: "Кондиционер", D: "Сушка", C: "CO₂", L: "Свет", reserve: "Резерв", mask: "маска" },
    intervalError: "Интервал должен быть >0 и <=1440 минут",
    functions: {
      setHumidityThreshold: "💧 Влажность: пороги (%)",
      setTemperatureThreshold: "🌡 Температура: пороги (°C)",
      setCO2Threshold: "CO₂: пороги (ppm)",
      toggleSocket: "🔌 Розетки: Вкл/Выкл/Авто",
      calibrateCO2ppm: "🔧 Калибровка CO₂",
      setDisplayInterval: "🕒 Интервал экрана (мин)",
      setServerInterval: "📤 Интервал отправки (мин)",
      syncTimeNow: "🕒 Синхронизировать время",
      setTimezone: "🌍 Установить часовой пояс",
      setManualTime: "⌚ Установить время вручную",
      getReadings: "📈 Обновить показания",
      softReset: "🔄 Перезапуск контроллера",
      setVPDType: "🌿 Формула VPD",
    },
    args: {
      min: "Мин",
      max: "Макс",
      ppm: "PPM",
      minutes: "Минуты",
      timezone: "Часовой пояс",
      manualTimestamp: "Время",
      type: "Тип",
      submit: "Отправить",
    },
    enums: {
      air: "Воздух",
      leaf: "Лист",
      "leaf+": "Лист+",
      on: "Вкл",
      off: "Выкл",
      auto: "Авто",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
    },
    hints: {
      setHumidityThreshold: "пример: 40.0,60.0",
      setTemperatureThreshold: "пример: 18.0,25.0",
      setCO2Threshold: "пример: 400,1000",
      calibrateCO2ppm: "пример: 400",
      setDisplayInterval: "Введите количество в минутах (например, 1).",
      setServerInterval: "Введите интервал отправки в минутах, минимум 1.",
      toggleSocket: "пример: 1,on",
      setVPDType: "air | leaf | leaf+",
    },
  },
  en: {
    language: "English",
    nav: {
      home: "Home",
      dashboard: "Dashboard",
      profile: "Profile",
      news: "News",
      bugs: "Bugs",
      errors: "Errors",
      tasks: "Tasks",
      devices: "Controllers",
      rooms: "Growing Spaces",
      strains: "Strains",
      cuttings: "Cuttings",
      secondaryMenu: "More",
      adminMenu: "Admin",
      about: "About",
      manageCuttings: "Manage cuttings",
      manageStrains: "Manage strains",
      manageReviews: "Professional reviews",
      manageNews: "Manage news",
      users: "Users",
      adminPanel: "Admin Panel",
      manageFarms: "Manage facilities",
      unassignedDevices: "Unassigned controllers",
      wifiBle: "Wi‑Fi BLE",
      farmGroup: "Facility",
      farmsBreadcrumb: "Facilities",
      livePanel: "Live panel",
      farm: "Facility",
      geneticsGroup: "Genetics",
      engineeringPanel: "Engineering panel",
      adminBreadcrumb: "Admin",
      allControllers: "All controllers",
    },
    roleSwitcher: {
      label: "Test role",
      reset: "Reset",
    },
    role: {
      admin: "Admin",
      superuser: "Superuser",
      farmOwner: "Facility owner",
      farmManager: "Facility manager",
      worker: "Worker",
    },
    planTier: {
      included: "Included",
      basic: "Basic",
      standard: "Standard",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    deviceTabs: {
      overview: "Overview",
      settings: "Settings",
    },
    farmsAdmin: {
      title: "Facility management",
      colName: "Name",
      colOwner: "Owner",
      colUsers: "Users",
      colDevices: "Controllers",
      colActions: "Actions",
      create: "Create facility",
      edit: "Edit",
      usersAction: "Users",
      delete: "Delete",
      editTitle: "Edit facility",
      ownerUnassigned: "No owner",
      saveChanges: "Save changes",
      savingChanges: "Saving…",
      confirmDeleteTitle: "Delete facility",
      confirmDelete: (name) =>
        `Delete facility «${name}»? You can only delete an empty facility (no users, controllers, or related data).`,
      modalTitle: "New facility",
      farmNamePlaceholder: "Facility name",
      createSubmit: "Create",
      creating: "Creating…",
      deleting: "Deleting…",
      accessDenied: "This section is for admins and superusers only.",
      usersAccessDenied: "User management is available to admins and superusers only.",
      moveFarmLabel: "Move to another facility",
      apply: "Apply",
      unassigned: "Unassigned",
      freePoolOption: "-- Free pool (Unbind) --",
      freeStatus: "Status: Free",
      moving: "Saving…",
      farmUpdated: "Controller facility updated",
      deviceFreed: "Controller unbound and moved to free pool",
      farmUpdateNoop: "No change",
      farmUpdateFailed: "Could not update controller facility",
      farmColumn: "Facility",
      selectFarmNewUser: "Facility",
      loading: "Loading…",
      empty: "No facilities yet",
      loadError: "Failed to load facilities",
      createFailed: "Failed to create facility",
      deleteFailed: "Failed to delete facility",
      updateFailed: "Failed to update facility",
      planTypeLabel: "Subscription plan",
    },
    welcome: {
      title: "WELCOME TO SHEFAGREENAPP",
      subtitle: "Shefa Green – Smart Plantations and Climate Management."
    },
    home: {
      hero: {
        title: "Manage strains and cultivation cycles with Shefa Green",
        subtitle: "A protected grower platform for strains, propagation, growing spaces, climate, water, equipment, grower diary, analytics, and future AI-assisted cultivation decisions.",
        slogan: "Strain-centered cultivation. Controlled environments.",
        openDashboard: "Open Dashboard",
        about: "About",
      },
      public: {
        eyebrow: "Strain-centered cultivation workspace",
        valueEyebrow: "Built for grower operations",
        valueTitle: "Built around strains, genetics, and cultivation cycles, not just controllers",
        valueSubtitle:
          "Shefa Green connects propagation, growing spaces, presets, diary records, controller actions, tasks, and analytics into one operating layer for professional growers.",
        title: "Shefa Green helps growers manage the full cultivation process around strains.",
        body:
          "Shefa Green brings strains, cultivation cycles, growing spaces, cuttings, climate, water, equipment, grower diary, analytics, and future AI-assisted decision support into one protected workspace. Each strain can be followed through propagation, growing space movement, growth stages, observations, treatments, and grow history while ShefaGreen Controllers support the environment growers create around the crop.",
        notice:
          "Operational data is available only after login. Public visitors cannot see controller names, controller IDs, growing space names, facility names, sensor values, tasks, strain records, cutting records, grow diary entries, or analytics results.",
        statsAria: "Shefa Green platform highlights",
        stats: {
          strainCentered: {
            value: "100%",
            label: "Strain-centered",
          },
          aiReady: {
            value: "AI-ready",
            label: "Decision support roadmap",
          },
          protected: {
            value: "Secure",
            label: "Protected workspace",
          },
        },
        preview: {
          activeStrain: "Active strain",
          flowering: "Flowering",
          cycleProgress: "Cultivation cycle progress",
        },
        controllerPill: "ShefaGreen Controllers",
        controllersTitle: "Monitor and control the growing environment",
        controllersBody:
          "ShefaGreen Controllers are cultivation equipment for both monitoring and control: climate, ventilation, cooling, heating, irrigation, water quality, flow, and equipment status can all become part of the protected workspace.",
        ctaBadge: "Protected professional platform",
        ctaTitle: "One protected workspace for cultivation management",
        ctaBody:
          "Guide every strain from genetics to harvest while the team, controllers, diary, analytics, and future AI assistant stay connected in one place.",
        ctaButton: "Enter Shefa Green",
        pricing: {
          eyebrow: "Subscription tiers",
          title: "Facility subscription plans",
          subtitle:
            "Plans mainly differ by how far back climate telemetry is retained and viewable in the cloud, and by access to expanded Vapor Pressure Deficit (VPD) analysis.",
          commonFeatures:
            "Every tier includes the same workspace modules—strains, growing spaces, presets, diary, controllers, tasks, climate, and water tooling—while telemetry depth follows the limits below.",
          retentionNote:
            "Raw climate samples are rotated once they fall outside your plan window; charts and queries cannot reach beyond that retention horizon.",
          footer:
            "Plans are assigned by Shefa Green administrators. Contact support to upgrade or change your facility tier.",
          plans: {
            included: {
              tagline: "Default onboarding tier",
              bullets: [
                "1 day of climate telemetry history for charts and review.",
                "VPD-forward analysis modes are off; baseline climate readings remain available.",
              ],
            },
            basic: {
              tagline: "Short operational lookback",
              bullets: ["3 days of climate telemetry history.", "Expanded VPD analysis is not included."],
            },
            standard: {
              tagline: "Weekly reporting window",
              bullets: ["7 days of climate telemetry history.", "Expanded VPD analysis is not included."],
            },
            pro: {
              tagline: "One month plus VPD",
              bullets: [
                "30 days of climate telemetry history.",
                "Expanded VPD analysis enabled (wired to your facility feature flag).",
              ],
            },
            enterprise: {
              tagline: "Long retention plus VPD",
              bullets: ["365 days of climate telemetry history.", "Expanded VPD analysis enabled."],
            },
          },
        },
        capabilities: {
          temperature: "Temperature",
          humidity: "Humidity",
          co2: "CO2",
          ventilation: "Ventilation",
          cooling: "Cooling",
          heating: "Heating",
          irrigation: "Irrigation",
          waterFlow: "Water flow",
          ec: "EC",
          ph: "pH",
          equipmentStatus: "Equipment status",
        },
        modules: {
          strains: {
            title: "Strains",
            description:
              "Make genetics the center of the workspace: track each strain through propagation, growing space movement, growth stages, observations, treatments, and grow history.",
          },
          growthPresets: {
            title: "Growth Presets",
            description:
              "Plan configurable cultivation presets for different stages so growers can align growing spaces, targets, and routines with the crop lifecycle.",
          },
          growerDiary: {
            title: "Grower Diary",
            description:
              "Record cultivation events, notes, observations, treatments, stage changes, growing space history, and grower decisions in context.",
          },
          analytics: {
            title: "Analytics",
            description:
              "Compare strains, growing spaces, growth stages, climate conditions, water conditions, grow cycles, and operational results over time.",
          },
          controllers: {
            title: "ShefaGreen Controllers",
            description:
              "Use ShefaGreen Controllers as cultivation equipment that can monitor and control the growing environment from inside the protected workspace.",
          },
          climateAutomation: {
            title: "Climate Automation",
            description:
              "Support temperature, humidity, CO2, ventilation, cooling, heating, and configured climate outputs from the same cultivation workspace.",
          },
          waterControl: {
            title: "Water Control",
            description:
              "Track and support irrigation, water flow, EC, pH, ORP, water level, and water temperature as part of the growth cycle.",
          },
          cuttings: {
            title: "Cuttings",
            description:
              "Manage propagation and cutting workflows as part of the full strain lifecycle, from source genetics to growing space assignment.",
          },
          tasks: {
            title: "Equipment & Tasks",
            description:
              "Coordinate equipment status, maintenance, follow-up, work items, issues, and grower responsibilities with role-based access.",
          },
          aiRoadmap: {
            title: "Analytics & Future AI Assistant",
            description:
              "A future intelligence layer is planned to understand grow history, detect patterns, analyze results, and suggest possible grower actions based on collected data.",
          },
        },
      },
      ticker: {
        ariaLabel: "Live controller status ticker",
        online: (n) => `Online: ${n}`,
        offline: (n) => `Offline: ${n}`,
        lastSeen: (time) => `last seen ${time}`,
        empty: "No controller activity yet",
      },
      strains: {
        title: "Strains today",
        viewAll: "All strains",
        empty: "No strains to show",
        updated: (date) => `Updated ${date}`,
      },
      news: {
        title: "News",
        viewAll: "All news",
      },
      tasks: {
        title: "Tasks",
        viewAll: "All tasks",
        empty: "No open tasks",
        superuserOnly: "Tasks are visible to superusers only.",
      },
      quickActions: {
        title: "Quick actions",
        qr: "QR",
      },
      footer: {
        telegram: "Telegram Bot",
      },
    },
    dashboard: {
      title: "Live overview",
      lastUpdated: (time) => `Last updated ${time}`,
      autoRefresh: {
        label: "Auto-refresh",
        enabled: (seconds) => `Refreshing every ${seconds} seconds`,
        disabled: "Auto-refresh paused",
      },
      refreshNow: "Refresh now",
      errors: {
        farmContextRequired: "Choose a facility or sign in again to load this overview.",
      },
      summary: {
        title: "System summary",
        totalDevices: "Total controllers",
        online: "Online controllers",
        offline: "Offline controllers",
        lastData: "Last controller reading",
        totalStrains: "Total strains",
        noData: "No data yet",
      },
      climate: {
        title: "Facility-wide climate (average)",
        temperature: "Mean temperature",
        humidity: "Mean humidity",
        co2: "Mean CO₂",
        noData: "—",
      },
      alerts: {
        title: "Recent issues & alerts",
        viewAll: "All issues",
        empty: "No critical issues in the last activity window.",
        deviceOffline: "Controller offline",
      },
      recentDevices: {
        title: "Recent controllers",
        viewAll: "All controllers",
        empty: "No recent controller activity",
        lastSeen: (time) => `Last seen ${time}`,
      },
      status: {
        online: "Online",
        offline: "Offline",
      },
      strains: {
        title: "Latest strains",
        viewAll: "All strains",
        empty: "No strain updates yet",
      },
      news: {
        title: "Latest news",
        viewAll: "All news",
        empty: "No news published yet",
      },
      tasks: {
        title: "Open tasks",
        viewAll: "All tasks",
        empty: "No open tasks",
        superuserOnly: "Tasks are visible to superusers only.",
      },
      rooms: {
        title: "Growing Spaces",
        heading: "Spaces & climate",
        subtitle:
          "Spaces on this facility. Controllers and the grower diary are scoped to each growing space.",
        addRoom: "Add growing space",
        create: "Create growing space",
        namePlaceholder: "Name (e.g. veg growing space)",
        typePlaceholder: "Type (optional)",
        empty: "No growing spaces yet",
        loading: "Loading…",
        open: "Open",
        delete: "Delete",
        confirmDeleteRoom:
          "Delete this growing space? Controllers will be unassigned from the growing space.",
      },
      planBadge: (label) => `Plan: ${label}`,
    },
    roomType: {
      seedling: "Propagation / seedling",
      vegetation: "Vegetation",
      flowering: "Flowering",
      drying: "Drying",
    },
    roomIcon: {
      leaf: "Leaf",
      seed: "Seed",
      sun: "Sun / light",
      "grow-light": "Grow light",
      droplet: "Droplet",
      "water-drop": "Water drop",
      fan: "Air / fan",
      "ventilation-fan": "Ventilation fan",
      home: "Growing Space",
      "grow-room": "Growing space",
      layers: "Layers",
      thermometer: "Climate",
      climate: "Climate",
      flower: "Flower",
      "mother-plant": "Mother plant",
      "clone-cutting": "Clone cutting",
      "drying-room": "Drying space",
      storage: "Storage",
      equipment: "Equipment",
    },
    roomForm: {
      createTitle: "Add growing space",
      editTitle: "Edit growing space",
      name: "Name",
      type: "Growing Space type",
      typeNone: "—",
      icon: "Icon",
      description: "Description",
      farmHint: "Facility",
      farmIdLabel: "Context facility ID",
      farmIdTitle: "Used in X-Farm-Context for this request",
      save: "Save",
      saving: "Saving…",
      cancel: "Cancel",
      close: "Close",
      nameRequired: "Enter a growing space name.",
      saveFailed: "Could not save growing space.",
      noFarmContext: "No active facility selected. Open the facility overview from the menu and try again.",
      error503: "The server is temporarily unavailable. Please try again in a moment.",
      error504: "The request took too long. Please try again.",
      error400: "Invalid data",
      errorTypeKey: "Invalid growing space type. Choose one of the list values.",
      errorIconKey: "Invalid icon. Choose one of the list values.",
      error403: "You don’t have permission to change growing spaces.",
      error401: "Session expired. Sign in again.",
      errorHttp: (code) => `Request failed (HTTP ${code})`,
    },
    roomCard: {
      openRoom: (name) => `Open ${name || 'growing space'}`,
      onlineOffline: (on, off) => `${on} online / ${off} offline`,
      unitOnline: "online",
      unitOffline: "offline",
      manage: "Growing Space settings",
      configure: "Configure",
      noData: "No data",
      labelTemperature: "Temperature",
      labelHumidity: "Humidity",
      labelCo2: "CO₂",
      climateGroup: "Climate",
      relayGroup: "Outlets and equipment",
      outletGroup: "Outlets and equipment",
      relayAc: "Air conditioning",
      relayDehumidifier: "Dehumidifier",
      relayCo2Valve: "CO₂",
      relayReserve: "Auxiliary outlet",
      relayStateOn: "on",
      relayStateOff: "off",
    },
    roomsPage: {
      breadcrumbInRoom: "Growing Space",
      back: "Back to live panel",
      backToRooms: "Back to growing spaces",
      backDashboard: "Back to live panel",
      diaryRoomHint: "Each entry is stored for this growing space and includes a snapshot from growing space sensors.",
      devices: "Controllers in this growing space",
      noDevices:
        "No controllers in this growing space yet. Pick a growing space in controller settings to assign controllers here.",
      diaryTitle: "Grower's diary",
      diaryNote: "Note",
      diaryAdd: "Add entry",
      diaryEmpty: "No diary entries yet",
      diarySensors: "Sensor snapshot (auto)",
      sensorsNone: "No sensor rows",
      loadError: "Failed to load growing space",
      editRoom: "Edit growing space",
      deleteRoom: "Delete growing space",
      roomDeleted: "Growing Space was deleted",
      claimInvalidContext: "Cannot detect controller ID or growing space context.",
      claimFailed: "Failed to claim controller.",
      claimSuccess: "Connection established! The controller brought the growing space to life.",
      notConnectedTitle: "Growing Space is not connected",
      notConnectedDescription:
        "Add a new Shefa Green Controller to this growing space to start telemetry and control.",
      connectArgon: "Connect Shefa Green Controller",
      connectModalTitle: "Connect Shefa Green Controller",
      step1: "Step 1 of 3",
      step2: "Step 2 of 3",
      step3: "Step 3 of 3",
      prepareDeviceTitle: "Prepare controller",
      prepareDeviceDescription:
        "Plug Shefa Green Controller into power. Make sure it blinks blue - this means it's ready to pair.",
      searchingHint: "Shefa Green Controller is looking for its growing space...",
      identifyDeviceTitle: "Identify controller",
      identifyDeviceDescription: "Scan the controller QR code or enter deviceId manually.",
      scanQr: "Scan QR",
      deviceIdLabel: "Controller ID",
      deviceIdPlaceholder: "e.g. 34003f001447343339383037",
      connectDevice: "Connect",
      claimingInProgress: "Finding Shefa Green Controller and establishing connection...",
      updatingRoomData: "Updating growing space data...",
      next: "Continue",
      back: "Back",
      close: "Close",
      statusOnline: "Online",
      statusOffline: "Offline",
      lightDay: "Light period",
      lightNight: "Night",
      lightAlertLampOff: "Warning: lamp might be off?",
      summaryTotalRooms: "Total growing spaces",
      summaryActiveControllers: "Active controllers",
      summaryProblems: "Issues",
      modeGrowing: "Growing",
      modeSleeping: "Sleeping",
      modeNeedsSetup: "Setup required",
      setupRequired: "Setup required: connect a controller to enable monitoring.",
      kpiTemperature: "Temperature",
      kpiHumidity: "Humidity",
      kpiConnection: "Connection",
      kpiWifiRssi: "Wi-Fi (RSSI)",
      actionSettings: "Settings",
      actionCharts: "Charts",
      actionBlink: "Blink",
      refreshingStatuses: "Refreshing growing space statuses...",
      breadcrumbOverview: "Overview",
      kpiCo2: "CO₂",
      kpiDevicesOnline: "Controllers online",
      growerDiarySummaryTitle: "Daily summary",
      growerDiarySummaryHint:
        "“Day” and “Night” buckets follow light status in telemetry (day_status), not the clock.",
      growerDiaryTablePlaceholder:
        "A min/max table for light-on and light-off periods will appear here.",
      tableDate: "Date",
      tableMetric: "Metric",
      tablePeriodDay: "Day (lights on)",
      tablePeriodNight: "Night (lights off)",
      tableMin: "Min",
      tableMax: "Max",
      diaryAnalyticsTitle: "Diary & Analytics",
      analyticsLoading: "Loading summary…",
      analyticsError: "Failed to load summary",
      analyticsTruncated: "Sample capped",
      analyticsTruncatedBanner: (cap) =>
        `Only the latest ${cap} readings in the window were included; older points were omitted. Narrow the time window or controller set if you need full coverage.`,
      analyticsSamples: "Readings in summary",
      colPeriod: "Period",
      colTempMm: "Temp min–max",
      colRhMm: "RH min–max",
      colCo2Avg: "CO₂ avg",
      colVpdAvg: "VPD avg",
      colDailyNote: "Day note",
      dailyNoteSave: "Save",
      dailyNoteSaved: "Saved",
      periodDayShort: "Day",
      periodNightShort: "Night",
      dayNightCycleTitle: "Day/night cycle validation",
      dayNightCycleEnable: "Enable day/night cycle validation",
      dayStartTime: "Day start time",
      dayEndTime: "Day end time",
      expectedMode: "Expected",
      actualLight: "Actual light",
      validationStatus: "Status",
      lastCheckedTime: "Last checked time",
      dayNightCycleSaved: "Day/night cycle settings saved",
      dayNightCycleSaveFailed: "Failed to save day/night cycle settings",
      dayNightCycleOk: "OK",
      dayNightCycleMismatch: "MISMATCH",
      dayNightCycleUnknown: "UNKNOWN",
      dayNightCycleMismatchWarning: "Light cycle mismatch detected",
      noClimateControllerAssigned: "No climate controller assigned",
      lightStatusUnknown: "Light status unknown",
    },
    invitePage: {
      title: "Join facility as worker",
      invalidLink: "Invite link is invalid.",
      missingToken: "Invite token is missing.",
      registrationFailed: "Registration failed.",
      emailPlaceholder: "Email",
      passwordPlaceholder: "Password",
      creatingAccount: "Creating account...",
      createAccount: "Create account",
    },
    unassignedPage: {
      title: "Unassigned controllers",
      subtitle: "Assign each controller to a facility.",
      colDevice: "Controller",
      colFarm: "Facility",
      apply: "Assign facility",
      loading: "Loading…",
      empty: "No unassigned controllers",
      loadError: "Failed to load",
      saveError: "Could not assign facility",
      saved: "Facility updated",
    },
    adminControllers: {
      title: "All controllers",
      subtitle: "Every controller across facilities. Search and filters apply on this page.",
      searchLabel: "Search",
      searchPlaceholder: "Controller ID, name, facility, or growing space…",
      statusFilter: "Status",
      statusAll: "All",
      statusOnline: "Online",
      statusOffline: "Offline",
      displayFilter: "Display type",
      displayAll: "All",
      colControllerId: "Controller ID",
      colDeviceName: "Controller name",
      colFarm: "Facility",
      colRoom: "Growing Space",
      colStatus: "Status",
      colHardware: "Hardware",
      colLastSeen: "Last seen",
      colActions: "Actions",
      badgeOnline: "Online",
      badgeOffline: "Offline",
      viewLogs: "View logs",
      reboot: "Reboot",
      rebootConfirm: "Soft reset this controller?",
      rebootFailed: "Reboot failed",
      unbind: "Unbind",
      unbindTitle: "Disconnect from facility and growing space",
      unbindModalTitle: "Unbind controller",
      unbindModalBody:
        "Are you sure you want to unbind controller {{id}} from facility \"{{facility}}\"? The controller will return to the unassigned list.",
      unbindConfirm: "Unbind",
      unbindCancel: "Cancel",
      unbindFailed: "Unbind failed",
      assign: "Link to growing space",
      assignTitle: "Bind controller to facility and growing space",
      assignModalTitle: "Bind to facility and growing space",
      assignModalHint: "Select a facility and a growing space. This sets facility and growing space on the controller record (same as growing space claim).",
      assignFarmLabel: "Facility",
      assignFarmPlaceholder: "Choose a facility…",
      assignRoomLabel: "Growing Space",
      assignRoomPlaceholder: "Choose a growing space…",
      assignRoomLoading: "Loading growing spaces…",
      assignSubmit: "Bind",
      assignCancel: "Cancel",
      assignFailed: "Could not bind controller",
      assignNeedFarmRoom: "Select both a facility and a growing space",
      controllerTypeWater: "Water Controller",
      controllerTypeClimate: "Climate Controller",
      controllerTypeGeneric: "Controller",
      empty: "No controllers match the filters",
      loading: "Loading…",
      loadError: "Could not load controllers",
    },
    deviceAssignment: {
      roomLabel: "Growing Space",
      roomNone: "No growing space",
      roomSave: "Save growing space assignment",
      releaseDevice: "Release controller",
      releaseConfirm:
        "Clear facility and growing space for this controller? It becomes available for assignment to another facility (superuser registry).",
      releaseDone: "Controller released",
      releaseError: "Could not release controller",
      releaseHint: "Superuser only — removes facility and growing space assignment.",
      unbindFarm: "Unbind from facility",
      unbindConfirm:
        "Unbind this controller from the facility? It will appear in the unassigned registry until reassigned.",
      unbindDone: "Controller unbound",
      unbindError: "Could not unbind controller",
      superuserOnly: "Superuser only",
    },
    loading: "Loading...",
    noNews: "No news yet",
    updated: "Updated",
    refresh: "Refresh",
    toggleNavigation: "Toggle navigation",
    retry: "Retry",
    showOffline: (n) => `Show offline controllers (${n})`,
    hideOffline: "Hide offline controllers",
    noData: "No data",
    no_data: "No data",
    statusOn: "ON",
    statusOff: "OFF",
    co2CheckTank: "Check tank",
    firmwareUpload: "Firmware upload",
    upload: "Upload",
    cuttings: "Cuttings",
    news: "News",
    latestReadings: "Latest readings",
    loginFailed: "Login failed",
    copyToClipboard: "Copy to clipboard:",
    saveFailed: "Failed to save",
    settingsSavedSuccess: "Settings saved successfully",
    yes: "Yes",
    no: "No",
    timeSyncTitle: "Time Sync",
    refreshTimeSync: "Refresh",
    timeSyncHint: "Use NTP as the primary clock source. Controller calculations should stay in UTC and apply timezone only for day/night logic.",
    serverTimeLabel: "Server time",
    controllerTimeLabel: "Controller time",
    timeDriftLabel: "Drift",
    timeUnknown: "Unknown",
    farmTimezoneLabel: "Facility timezone",
    farmTimezoneHint: "Store an IANA timezone such as Asia/Jerusalem. This timezone is returned by /api/system/time and should be applied on the controller for day/night mode.",
    saveTimezone: "Save timezone",
    co2ManagementTitle: "CO2 Management",
    co2OffsetLabel: "CO2 calibration value (ppm)",
    co2OffsetHint: "Reading correction against a VDL reference sensor.",
    calibrationTargetPpmPlaceholder: "Standard: 400. Dead Sea: 580",
    syncNow: "Sync now",
    forceCalibrateButton: "Start calibration",
    forceCalibrateHint: "Run only after 5 minutes of ventilation.",
    calibrationStatus: {
      pending: "⏳ Waiting for controller sync...",
      started: (left) => `✅ Calibration started (5 min) ${left}`,
    },
    manualTimeLabel: "Manual time",
    setManualTime: "Set time",
    manualTimeHint: "Use only in isolated networks without NTP access. The command sends Unix time in UTC together with the facility timezone.",
    waterPage: {
      idLabel: "ID",
      firmwareLabel: "Firmware",
      range2h: "2h",
      range24h: "24h",
      range7d: "7d",
      rangeCustom: "Custom",
      latestTitle: "Latest data",
      noHistory: "No water history yet.",
      inletLine: "Inlet",
      outletLine: "Outlet",
      litersPerMinuteShort: "lpm",
      litersShort: "L",
      successOk: "OK",
      genericError: "Error",
    },
    deviceSettingsPage: {
      calibrationVentilationConfirm: "Make sure the growing space has been ventilated for at least 5 minutes before confirming.",
      timezonePlaceholder: "Asia/Jerusalem",
      co2OffsetPlaceholder: "0",
      currentValue: "Current",
      co2OffsetSaveHint:
        "The CO₂ offset in the section above is saved together with the facility timezone when you tap «Save timezone».",
      advancedFunctionsTitle: "Advanced Particle commands",
      vpdProPlanLock:
        "VPD analysis is available on Pro and Enterprise plans. Upgrade the facility plan to change VPD type.",
      locked: "locked",
    },
    deviceSettingsModal: {
      identityTitle: "Controller",
      identityHint: "The name helps identify this controller in growing spaces and dashboards.",
      notificationsTitle: "Notifications",
      notificationsHint: "Choose which events should be sent to Telegram and the facility log.",
      actionsTitle: "Controller actions",
      actionsHint: "Manual service commands for the selected controller.",
      restartButton: "Restart controller",
      restartConfirmTitle: "Confirm controller restart",
      restartWarning: "The controller may be offline for about 30-90 seconds during reboot. Relays, thresholds, modes, and EEPROM config will not be changed.",
      restartConfirmButton: "Yes, restart",
      restartSending: "Sending command",
      restartAccepted: "Restart command accepted. The controller is restarting and may be offline for 30-90 seconds.",
      restartFailed: "Failed to restart controller.",
      restartNotReachable: "Restart failed because the controller is not reachable.",
      thresholdHint: "These values define the day and night operating range. Save them separately from manual outlet control.",
      co2ManagementHint: "Start calibration only when the reference air is stable.",
      outletOn: "outlet on",
      outletOff: "outlet off",
      modeAutoBadge: "A",
      modeManualOnBadge: "M:ON",
      modeManualOffBadge: "M:OFF",
      relayModeAria: (label) => `${label} outlet control mode`,
      modeAuto: "Auto",
      modeManualOn: "Manual lock: on",
      modeManualOff: "Manual lock: off",
      deviceNameTitle: "Controller name",
      deviceNamePlaceholder: "Controller name",
      telegramAlerts: "Telegram alerts",
      powerLoss: "Power loss",
      thresholdAlerts: "Threshold alerts",
      trendAlerts: "Trend alerts",
      climateDeliveryOn: "Climate notifications are deliverable",
      climateDeliveryBlocked: "Climate notifications will not be delivered",
      climateDeliveryOff: "Climate notifications are off",
      climateDeliveryCounts: (enabled, total) => `Climate alert recipients: ${enabled}/${total}`,
      climateDeliveryNoRecipients:
        "Controller notifications are enabled, but no linked Telegram recipients can receive climate alerts.",
      climateDeliveryClimateDisabled:
        "Controller notifications are enabled, but every eligible Telegram recipient has Climate alerts turned off.",
      climateDeliveryTelegramSettingsHint:
        "Open notification settings in the Telegram bot and enable the Climate alerts category.",
      enableClimateAlertsForMe: "Enable Climate alerts for me",
      enablingClimateAlerts: "Enabling Climate alerts",
      climateAlertsEnabledForMe: "Climate alerts are enabled for your Telegram account.",
      climateAlertsEnableFailed: "Failed to enable Climate alerts.",
      co2Management: "CO2 Management",
      co2Offset: "CO2 calibration value (ppm)",
      startCalibration: "Start calibration",
      calibrationTargetPpmLabel: "Calibration value (PPM)",
      calibrationTargetPpmPlaceholder: "Standard: 400. Dead Sea: 580",
      calibrationHint: "Run only after 5 minutes of ventilation",
      calibrationConfirm: "Are you sure? Run only after 5 minutes of ventilation!",
      relayManagement: "Outlet control overrides",
      controlModelHint: "Automatic scenario control is the default. Manual control is shown as an override state.",
      waterMetric: { ph: "pH", ec: "EC", orp: "ORP", temp: "Temperature", level: "Water level", volume: "Volume" },
    },
    deviceCard: {
      unknownDeviceName: "Climate controller",
      idLabel: "ID",
      idDialogTitle: "Controller ID",
      idPreviewAria: "View the full controller ID and copy it",
      copyId: "Copy ID",
      copied: "Copied",
      connectivityOnline: "Online",
      connectivityNoRecentData: "No recent data",
      connectivityOffline: "Offline",
      controllerOfflineCommands: "Controller is offline. Commands are not executed.",
      syncTimeFailed: "Failed to sync time",
      calibrationFailed: "Calibration failed",
      climateParamsUpdated: "Climate parameters updated",
      relayCommandTimeout: "Error: controller did not confirm command execution (timeout)",
      relayCommandUnconfirmed:
        "Mode was saved, but the controller did not send confirmation. Refresh the page if the state does not match the controller.",
      modeLabel: "mode",
      errorGeneric: "Error",
      invalidThreshold: "Invalid threshold: enter numeric Min and Max, and ensure Min <= Max.",
    },
    settingsApplyFailedPrefix: "Failed to apply",
    settingsProgressSavingDb: "Saving settings to database...",
    settingsProgressApplyingController: "Applying thresholds to controller",
    settingsProgressControllerCheck: "Thresholds saved in database, checking controller...",
    settingsProgressSavingSystem: "Saving system parameters...",
    settingsProgressSyncingView: "Syncing display...",
    co2OffsetNotSavedWarning: "CO2 Offset was not saved (timezone endpoint unavailable)",
    noCuttings: "No cuttings yet",
    noStrains: "No strains found",
    noBugs: "No bugs to display",
    login: "Login",
    logout: "Logout",
    profile: {
      title: "Profile",
      subtitle: "Account details and Telegram linking.",
      farm: "Facility",
      linkTelegram: "Connect Telegram",
      telegramLinked: "Telegram connected",
      telegramNotLinked: "Telegram not connected",
      openTelegramBot: "Open Telegram bot",
      disconnectTelegram: "Disconnect Telegram",
      disconnectingTelegram: "Disconnecting Telegram…",
      disconnectError: "Could not disconnect Telegram. Try again later.",
      linkError: "Could not get the link. Try again later.",
      openingBot: "Opening bot…",
      refreshStatus: "Refresh status",
      telegramHint:
        "Connect Telegram to receive alerts. Once connected, you can open the bot or disconnect Telegram here.",
    },
    telegramConnect: {
      eyebrow: "Telegram",
      title: "Telegram account connection",
      description:
        "Connect your Telegram account to access Facilities, Growing Spaces, alerts, and controller tools.",
      button: "Connect Telegram",
      connecting: "Connecting Telegram...",
      linkError: "Could not create the Telegram link. Try again later.",
      signInAgain: "Something went wrong. Please sign in again.",
      rateLimit: "Please wait a few seconds and try again.",
      retryButton: "Retry connection",
      openTelegramButton: "Open Telegram and finish connection",
      copyConnectCodeButton: "Copy connect code",
      copyConnectCodeDone: "Connect code copied",
      copyError: "Could not copy the code. Copy it manually instead.",
      widgetLoading: "Loading Telegram login button...",
      widgetLoadError: "Telegram login button could not be loaded. Please refresh or use fallback.",
      manualFallbackHint: "If Telegram opens without the start code, copy the connect code and paste it into the bot chat.",
      afterLoginHint: "If you are not logged in yet, sign in first — linking will continue automatically after login.",
    },
    rateLimitPage: {
      eyebrow: "Shefa Green",
      title: "Slow down for a moment",
      body:
        "We have temporarily limited requests from your browser because traffic was unusually high. This helps keep Shefa Green reliable for everyone.",
      hint: "Please wait a few minutes, then refresh the page or use the button below to try again.",
      ctaRetry: "Try again",
      ctaHome: "Go to home",
      tabsHint:
        "If several tabs have Shefa Green open, close the ones you do not need so the page stops sending background requests.",
      retryIn: (totalSeconds) => {
        const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
        if (s <= 0) return "You can try again now.";
        if (s >= 120) {
          const m = Math.ceil(s / 60);
          return `You can try again in about ${m} minutes.`;
        }
        return `You can try again in about ${s} seconds.`;
      },
    },
    theme: {
      dark: "Dark",
      light: "Light",
      system: "System",
      toggleAria: "Switch theme: dark, light, or match system",
      tapNext: "click to cycle",
      farmMenu: "Facility context",
      farmHomeToken: "Home facility (token)",
    },
    username: "Username",
    email: "Email",
    password: "Password",
    users: "Users",
    addUser: "Add user",
    role: "Role",
    delete: "Delete",
    manageCuttings: "Manage cuttings",
    byName: "Show by name",
    byCode: "Show by code",
    both: "Show both",
    manageStrains: "Manage strains",
    manageReviews: "Professional reviews",
    manageNews: "Manage news",
    addCutting: "Add cutting",
    addStrain: "Add strain",
    addNews: "Add news",
    reportBug: "Report bug",
    strainsLoadError: "Unable to load strains.",
    strainsAdminCrashed: "Something went wrong while loading the strains admin page.",
    pin: "Pin",
    unpin: "Unpin",
    pinnedBugs: (n) => `\uD83D\uDCCC Pinned bugs (${n})`,
    recentlyCreated: "Created recently",
    recentUpdate: "Recent update",
    closeBug: "Close",
    closedBugs: (n) => `Closed bugs (${n})`,
    showClosed: "Show Closed Bugs",
    hideClosed: "Hide Closed Bugs",
    showOnlyMine: "Only my bugs",
    urgentOnly: "Urgent only",
    sort: "Sort by",
    sortByPriority: "Priority",
    sortByDateDesc: "Newest first",
    sortByDateAsc: "Oldest first",
    sortByStatus: "Status",
    shownOfTotal: (s, t) => `Showing ${s} of ${t} bugs`,
    addTask: "Add task",
    addComment: "Add comment",
    priority: "Priority",
    priorityLabel: (value) => `Priority: ${value}`,
    priorityHigher: "Higher",
    priorityLower: "Lower",
    lastActivity: "Last activity",
    showAllComments: "Show all comments",
    noCommentsYet: "No comments yet",
    completeTask: "Complete",
    showCompleted: "Show Completed",
    hideCompleted: "Hide Completed",
    tasksDashboard: {
      subtitle: "Your daily control center",
      summaryUrgent: "Urgent",
      summaryToday: "Today",
      summaryOverdue: "Overdue",
      summarySystem: "System",
      summaryCompleted: "Completed",
      sectionToday: "Today's tasks",
      sectionProblems: "Needs attention",
      sectionScheduled: "Scheduled",
      sectionHistory: "Completed & history",
      tabToday: "Today",
      tabProblems: "Problems",
      tabScheduled: "Scheduled",
      tabHistory: "History",
      quickAddTitle: "Quick add",
      recommendationsTitle: "Automatic recommendations",
      recommendationsHint:
        "When sensor automation is enabled, anomaly-based tasks will appear here.",
      recommendationsEmptyPrimary: "No active system recommendations.",
      recommendationsEmptySecondary:
        "Recommendations will appear here when climate, water, controller connection, or equipment data goes outside the expected range.",
      noTasksInView: "Nothing in this view yet.",
      emptyToday:
        "Nothing for today. This tab lists active tasks with no due date, due today (calendar day), or overdue. Work due after today lives under Scheduled.",
      emptyProblems:
        "No problem tasks. This tab shows overdue items, urgent priority, and statuses “needs” or “rejected”.",
      emptyScheduled:
        "No dated work after today. Set a due date after this calendar day, or check Today for what is due now.",
      emptyHistory: "No completed tasks yet — they will appear here once marked done.",
      completedSectionTitle: "Completed tasks",
      postpone: "Postpone",
      postponeModalTitle: "Postpone task",
      postponeLaterToday: "Later today",
      postponeTomorrowMorning: "Tomorrow morning",
      postponeTomorrowEvening: "Tomorrow evening",
      postponeChooseDateTime: "Choose date and time",
      postponeApply: "Apply",
      postponeFailed: "Could not update the due date. Please try again.",
      postponeInvalidDate: "Enter a valid date and time.",
      openDetails: "Details",
      dueLabel: "Due date / time",
      addPhotos: "Photos",
      roomOrLocation: "Growing Space / location",
      optionalLinksHint: "Optional: link to a facility entity (when lists load).",
      linkRoom: "Growing Space",
      linkDevice: "Controller / controller",
      linkStrain: "Strain",
      linkCutting: "Cutting",
      linkNoneOption: "—",
      cuttingLabel: "Cutting",
      groupNoRoom: "Unassigned / No growing space",
      groupTaskCount: (n) => (Number(n) === 1 ? '1 task' : `${n} tasks`),
      filterRoom: "Growing Space",
      filterTaskType: "Task type",
      filterPriority: "Priority",
      filterSource: "Source",
      filterSearch: "Search",
      filterSearchPlaceholder: "Description, location, growing space, controller, strain, cutting, comments…",
      filterAllRooms: "All growing spaces",
      filterAllTypes: "All types",
      filterAllPriorities: "All priorities",
      filterAllSources: "All sources",
      clearFilters: "Clear filters",
      emptyFiltered: "No tasks match these filters. Adjust them or clear filters.",
      sourceManual: "Manual",
      sourceSystem: "System",
      pageTitle: "Tasks",
      completeTask: "Complete",
      showCompleted: "Show completed",
      hideCompleted: "Hide completed",
      addTask: "Add task",
      addComment: "Add comment",
      fieldDescription: "Description",
      fieldPriority: "Priority",
      fieldComment: "Comment",
      statusInProgress: "In progress",
      statusNeeds: "Needs attention",
      statusDone: "Done",
      statusRejected: "Rejected",
      completedAtLabel: "Completed",
      lastActivity: "Last activity",
      priorityUrgent: "Urgent",
      systemReasonSection: "System reason",
      systemReasonPrimaryLabel: "Reason",
      systemTriggerRange: (min, max) => `Allowed ${min}–${max}`,
      systemTriggerMinOnly: (min) => `Min ${min}`,
      systemTriggerMaxOnly: (max) => `Max ${max}`,
      systemTriggeredAt: "Triggered at",
      systemTriggerTypeLabel: "Trigger type",
      systemTriggerMetricLabel: "Metric",
      systemTriggerValueLabel: "Value",
      systemAllowedRangeLabel: "Allowed range",
      taskType: "Task type",
      systemTask: "System",
      reason: "Note",
      noDueDate: "No due date",
      priorityLow: "Low",
      priorityMedium: "Medium",
      priorityHigh: "High",
      taskTypes: {
        climate: "Climate",
        watering: "Watering",
        plant_inspection: "Plant inspection",
        equipment: "Equipment",
        cuttings: "Cuttings",
        nutrition: "Nutrition",
        pests_disease: "Pests or disease",
        cleaning: "Cleaning",
        other: "Other",
      },
    },
    openedBy: "Opened by",
    details: "Details",
    tasks: "Tasks",
    bugs: "Bugs",
    open: "Open",
    inProgress: "In progress",
    testing: "In testing",
    urgent: "Urgent",
    veryUrgent: "Very urgent",
    needs: "Needs parts",
    done: "Done",
    rejected: "Rejected",
    fixed: "Fixed",
    closed: "Closed",
    edit: "Edit",
    update: "Update",
    cancel: "Cancel",
    save: "Save",
    title: "Title",
    name: "Name",
    content: "Content",
    description: "Description",
    strain: "Strain",
    status: "Status",
    location: "Location",
    code: "Code",
    history: "History",
    statusHistory: "Status history",
    statusHistoryEmpty: "No status changes recorded",
    statusHistoryFrom: "From",
    statusHistoryTo: "To",
    statusHistoryBy: "By",
    statusHistoryAt: "At",
    link: "Link",
    event: "Event",
    comment: "Comment",
    photo: "Photo",
    screenshot: "Screenshot",
    savePhoto: "Save photo",
    photoSaved: "Photo saved",
    photoShortName: "Short name",
    photoDescriptionDetailed: "Description (detailed)",
    photoIncludeInDiary: "Include in diary",
    photoDiaryOnly: "Diary only",
    photoStage: "Stage",
    photoStageFilter: "Stage filter",
    photoStageAll: "All stages",
    photoTakenAt: "Timeline stamp",
    photoPosition: "Position",
    photoMove: "Timeline order",
    moveLeft: "Move left",
    moveRight: "Move right",
    downloadDiary: "Download diary",
    diaryExportTooMany: (limit) => `Diary export is limited to ${limit} photos`,
    diaryExportNoPhotos: "No photos selected for the diary",
    photoDetails: "Photo details",
    photoStageLabels: {
      cut: "Cut",
      veg: "Vegetative",
      flowering: "Flowering",
      harvest: "Harvest",
      drying: "Drying",
      trim: "Trim",
      curing: "Curing",
      lab_tests: "Lab tests",
      issue: "Issue",
    },
    gardenerDiary: "Gardener diary",
    viewReviewsForStrain: "View Reviews for this Strain",
    legacyDiaryReadOnly:
      "Legacy diary is read-only. Please use the Professional Reviews system for new evaluations.",
    goToReviews: "Go to Reviews",
    addLog: "Add entry",
    confirmDelete: "Delete?",
    author: "Author",
    id: "ID",
    slug: "Slug",
    genetics: "Genetics",
    geneticsType: "Genetics type",
    geneticsDominance: "Dominance (%)",
    shortDescription: "Short description",
    thcPercent: "THC %",
    cbdPercent: "CBD %",
    floweringWeeks: "Flowering (weeks)",
    floweringCategory: "Flowering category",
    floweringEarly: "Early",
    floweringMedium: "Medium",
    floweringLate: "Late",
    yield: "Yield",
    yieldLow: "Low",
    yieldMedium: "Medium",
    yieldHigh: "High",
    yieldVeryHigh: "Very High",
    plantHeight: "Plant height",
    heightLow: "Low",
    heightMedium: "Medium",
    heightHigh: "High",
    terpenes: "Terpenes",
    aromaFlavor: "Aroma/flavor",
    effectDirection: "Effect direction",
    effectUp: "Up",
    effectNeutral: "Neutral",
    effectDown: "Down",
    effectSensations: "Sensations",
    uniqueTrait: "Unique trait",
    positioning: "Positioning",
    positioningCommercial: "Commercial",
    positioningBoutique: "Boutique",
    positioningExotic: "Exotic",
    positioningNiche: "Niche",
    recommendedTime: "Recommended time",
    recommendedDay: "Day",
    recommendedEvening: "Evening",
    recommendedNight: "Night",
    targetAudience: "Target audience",
    audienceBeginners: "Beginners",
    audienceExperienced: "Experienced",
    audienceUniversal: "Universal",
    passportCore: "CORE",
    passportCorePlus: "CORE+",
    passportExtended: "EXTENDED",
    tagLimit: "Max 6 tags (effects — 5).",
    imageLimit: "Max 5 images.",
    maxImages: "Image limit reached.",
    extendedLabReports: "Lab PDFs/links (comma-separated)",
    extendedFullCannabinoidProfile: "Full cannabinoid profile",
    extendedSecondaryTerpenes: "Secondary terpenes (comma-separated)",
    extendedCultivationNotes: "Cultivation notes",
    extendedInternalCommercialNotes: "Internal commercial notes",
    statusRd: "R&D",
    statusTest: "Test",
    statusPreCommercial: "Pre-commercial",
    statusCommercial: "Commercial",
    weeks: "weeks",
    images: "Images",
    publicUrl: "Public URL",
    transplant: "transplant",
    treatment: "treatment",
    move: "move",
    viewMore: "View more",
    collapse: "Collapse",
    duplicate: "Duplicate",
    downloadQrPdf: "Download QR (PDF)",
    downloadAllQrPdf: "Download all QR (PDF)",
    downloadExcel: "Download all QR (Excel)",
    downloadExcelReport: "Download Excel",
    downloadReadings: "Readings…",
    downloadSettingsChanges: "Settings changes…",
    from: "From",
    to: "To",
    download: "Download",
    downloadFailed: "Download failed",
    reviewsAdmin: {
      sections: {
        filters: "Filters",
        strainSelection: "Strain selection",
        tastingMetadata: "Tasting metadata",
        visual: "Visual",
        aroma: "Aroma",
        flavor: "Flavor",
        smokeQuality: "Smoke quality",
        effect: "Effect",
        scales: "Scales and recognition points",
        points: "Points - what to boost and what to preserve",
        final: "Final",
        savedReviews: "Saved reviews",
      },
      fields: {
        strain: "Strain",
        customStrainName: "Custom strain name",
        customStrainCode: "Custom strain code",
        strainSelectionRequired: "Strain selection",
        tastingDate: "Tasting date",
        authorNameOptional: "Author (optional)",
        consumptionMethod: "Consumption method",
        dosage: "Dosage / number of puffs",
        context: "Context",
        visualScore: "Visual score",
        budShape: "Bud shape",
        densityOnSqueeze: "Density on squeeze",
        trichomesCharacter: "Trichomes character",
        trichomeCoverage: "Trichome coverage",
        colorPalette: "Color palette",
        firstVisualImpression: "First visual impression",
        aromaIntensity: "Aroma intensity",
        aromaOpening: "Aroma opening",
        aromaProfile: "Aroma profile",
        flavorMatchesAroma: "Does flavor match aroma",
        flavorIntensity: "Flavor intensity",
        flavorDynamics: "Flavor dynamics",
        aftertaste: "Aftertaste",
        throatFeel: "Throat feel",
        burn: "Burn",
        ash: "Ash",
        tarOilyRing: "Tar / oily ring",
        losesFlavor: "Loses flavor during smoking",
        negativeSensations: "Negative sensations while smoking",
        effectType: "Effect type",
        effectStrength: "Effect strength",
        direction: "Direction",
        mentalEffects: "Mental effects",
        bodyEffects: "Body effects",
        functionalEffects: "Functional effects",
        duration: "Duration",
        timeOfUse: "Time of use",
        recognizability: "Recognizability (blind recognition)",
        whoIsItFor: "Who is it for?",
        associationPositioning: "Association/positioning",
        strengths: "Strengths",
        preserve: "Preserve",
        downsides: "Downsides/deviations",
        commercialPotential: "Commercial potential",
        wouldBuyAgain: "Would buy again?",
        summaryStrongest: "Short summary: What is strongest?",
        summaryImprove: "Short summary: What to improve next batch?",
        summaryScores: {
          visual: "Visual",
          aroma: "Aroma",
          flavor: "Flavor",
          effect: "Effect",
          uniqueness: "Uniqueness",
        },
      },
      placeholders: {
        selectStrain: "Select strain",
        otherText: "Other (text)",
        allStrains: "All strains",
        effectStrength: "Effect strength",
        commercialPotential: "Commercial potential",
        recognizability: "Recognizability",
      },
      actions: {
        applyFilters: "Apply filters",
        clearFilters: "Clear filters",
        downloadPublic: "Download (Public)",
        downloadInternal: "Download (Internal)",
      },
      table: {
        strain: "Strain",
        date: "Date",
        author: "Author",
        effectStrength: "Effect strength",
        commercialPotential: "Commercial potential",
        recognizability: "Recognizability",
      },
      messages: {
        missingFieldsTitle: "Missing required fields",
        loadFailed: "Failed to load reviews",
        saveFailed: "Failed to save review",
        saved: "Saved",
        downloadFailed: "Download failed",
      },
      options: {
        scale: {
          "1": "1",
          "2": "2",
          "3": "3",
          "4": "4",
          "5": "5",
        },
        consumptionMethod: {
          joint: "Joint",
          bong: "Bong",
          pipe: "Pipe",
          vaporizer: "Vaporizer",
          other: "Other",
        },
        dosage: {
          low: "Low",
          medium: "Medium",
          high: "High",
        },
        context: {
          alone: "Alone",
          withCompany: "With company",
          beforeSleep: "Before sleep",
          beforeWork: "Before work",
          onWeekends: "On weekends",
        },
        budShape: {
          spherical: "Spherical",
          conical: "Conical",
          arrow: "Spike / arrow",
          clumpy: "Clumpy",
          wild: "Uneven / wild",
          commercial: "Commercial (even, neat)",
        },
        densityOnSqueeze: {
          stone: "Stone",
          veryDense: "Very dense",
          medium: "Medium",
          airy: "Airy",
          fluffy: "Fluffy",
        },
        trichomesCharacter: {
          dusty: "Dusty (crumbly)",
          greasy: "Greasy (sticky)",
          glassy: "Glassy (shiny)",
          matte: "Matte",
          speckled: "Speckled",
          even: "Even",
        },
        trichomeCoverage: {
          low: "Low",
          medium: "Medium",
          high: "High",
        },
        colorPalette: {
          lightGreen: "Light green",
          darkGreen: "Dark green",
          purple: "Purple",
          almostBlack: "Almost black",
          silver: "Silver",
          orangePistils: "Orange pistils",
          redPistils: "Red pistils",
          yellowPistils: "Yellow pistils",
        },
        firstVisualImpression: {
          wow: "Wow",
          veryInteresting: "Very interesting",
          normal: "Normal",
          nothingSpecial: "Nothing special",
        },
        aromaIntensity: {
          weak: "Weak",
          medium: "Medium",
          strong: "Strong",
        },
        aromaOpening: {
          noChange: "No change",
          increasesWhenBroken: "Increases when broken",
          increasesWhenGrinded: "Increases when grinded",
        },
        aromaProfile: {
          diesel: "Diesel",
          skunk: "Skunk",
          rubber: "Rubber",
          glue: "Glue",
          candy: "Candy",
          fruity: "Fruity",
          creamy: "Creamy",
          vanilla: "Vanilla",
          mint: "Mint",
          eucalyptus: "Eucalyptus",
          citrus: "Citrus",
          pine: "Pine",
          berries: "Berries",
          grape: "Grape",
          mango: "Mango",
          lychee: "Lychee",
          guava: "Guava",
          passionFruit: "Passion fruit",
          earthy: "Earthy",
          woody: "Woody",
          spicy: "Spicy",
          chemical: "Chemical",
          metal: "Metal",
          fermentation: "Fermentation",
          other: "Other",
        },
        flavorMatchesAroma: {
          yes: "Yes",
          partially: "Partially",
          no: "No",
        },
        flavorIntensity: {
          weak: "Weak",
          medium: "Medium",
          strong: "Strong",
        },
        flavorDynamics: {
          staysToEnd: "Stays until the end",
          fadesMiddle: "Fades in the middle",
          onlyBeginning: "Only at the beginning",
        },
        aftertaste: {
          sweet: "Sweet",
          sour: "Sour",
          gassy: "Gassy",
          earthy: "Earthy",
          minty: "Minty",
          metallic: "Metallic",
          other: "Other",
        },
        throatFeel: {
          smooth: "Smooth",
          normal: "Normal",
          harsh: "Harsh",
          burns: "Burns",
        },
        burn: {
          excellent: "Excellent",
          normal: "Normal",
          bad: "Bad",
        },
        ash: {
          white: "White",
          gray: "Gray",
          dark: "Dark",
        },
        tarOilyRing: {
          none: "None",
          slight: "Slight",
          strong: "Strong",
        },
        losesFlavor: {
          yes: "Yes",
          no: "No",
        },
        negativeSensations: {
          dries: "Dries",
          stings: "Stings",
          cough: "Cough",
          hardToDraw: "Hard to draw",
          none: "None",
        },
        effectType: {
          indica: "Indica",
          sativa: "Sativa",
          hybrid: "Hybrid",
        },
        effectStrength: {
          weak: "Weak",
          medium: "Medium",
          strong: "Strong",
        },
        direction: {
          up: "Up",
          neutral: "Neutral",
          down: "Down",
          wavy: "Wavy",
          slowOnset: "Slow onset",
          sharpStart: "Sharp start",
        },
        mentalEffects: {
          clarityFocus: "Clarity/focus",
          improvesMood: "Improves mood",
          calmsThoughts: "Calms thoughts",
          speedsThoughts: "Speeds thoughts",
          fog: "Fog",
          creativity: "Creativity",
          talkative: "Talkative",
          social: "Social",
          withdrawn: "Withdrawn",
        },
        bodyEffects: {
          bodyRelaxation: "Body relaxation",
          heaviness: "Heaviness",
          eyePressure: "Eye pressure",
          softWarmth: "Soft warmth",
          numbness: "Numbness",
          tingling: "Tingling",
          bodyEnergy: "Body energy",
        },
        functionalEffects: {
          painRelief: "Pain relief",
          antiStress: "Anti-stress",
          antiAnxiety: "Anti-anxiety",
          appetiteIncrease: "Appetite increase",
          helpsSleep: "Helps sleep",
          suitableForWork: "Suitable for work",
          suitableForRest: "Suitable for rest",
        },
        duration: {
          short: "Short",
          medium: "Medium",
          long: "Long",
        },
        timeOfUse: {
          day: "Day",
          evening: "Evening",
          night: "Night",
        },
        recognizability: {
          yes: "Yes",
          ratherYes: "Rather yes",
          ratherNo: "Rather no",
          no: "No",
        },
        whoIsItFor: {
          beginners: "Beginners",
          experienced: "Experienced",
          medicalUsers: "Medical users",
          dayUse: "Day use",
          eveningSleep: "Evening/sleep",
          social: "Social",
          solo: "Solo",
        },
        associationPositioning: {
          commercialHit: "Commercial hit",
          boutique: "Boutique (niche)",
          exotic: "Exotic",
          connoisseurs: "For connoisseurs",
          rawPotential: "Has potential but raw",
        },
        strengths: {
          visual: "Visual",
          smell: "Smell",
          taste: "Taste",
          effect: "Effect",
          uniqueness: "Uniqueness",
          commercialPotential: "Commercial potential",
        },
        preserve: {
          genetics: "Genetics",
          terpenes: "Terpenes",
          structure: "Structure",
          effect: "Effect",
          overallFeeling: "Overall feeling",
        },
        downsides: {
          weakTaste: "Weak taste",
          effectFadesQuickly: "Effect fades quickly",
          heavyOnset: "Heavy onset",
          irritatesThroat: "Irritates throat",
          tooSharp: "Too sharp",
          unclearEffect: "Unclear effect",
          noClearCharacter: "No clear character",
          other: "Other",
        },
        commercialPotential: {
          low: "Low",
          medium: "Medium",
          high: "High",
        },
        wouldBuyAgain: {
          yesDefinitely: "Yes, definitely",
          maybe: "Maybe",
          no: "No",
        },
      },
    },
    labelModes: {
      nameOnly: "Name only",
      numberOnly: "Number only",
      nameAndNumber: "Name + Number",
    },
    copyQrText: "Copy QR text",
    copyLabelText: "Copy label text",
    export: "Export",
    hide: "Hide",
    readMore: "Read more",
    currentReadings: "Current readings",
    thresholdValues: "Threshold values",
    day: "Day",
    night: "Night",
    dayNight: "Day/Night",
    preset: "Preset",
    socketStates: "Socket states",
    temperature: "Temperature",
    humidity: "Humidity",
    co2: "CO₂",
    vpd: "VPD",
    light: "PPFD",
    lightStatus: "Light Status",
    moisture: "Moisture",
    ec: "EC",
    ph: "pH",
    nitrogen: "N",
    phosphorus: "P",
    potassium: "K",
    inMedium: "In medium",
    inAir: "In air",
    search: "Search",
    all: "All",
    climate: "Climate",
    waterInAndOut: "WaterInAndOut",
    noDevicesFound: "No controllers found",
    waterDevices: "Water controllers",
    waterTrends: "Water trends",
    waterHistoryRange: "Water history range",
    noWaterHistory: "No water history yet.",
    waterTemperature: "Water temperature",
    waterLevel: "Water level",
    waterVolume: "Water volume",
    tank: "Tank",
    of: "of",
    maxHeight: "Max height",
    centimetersShort: "cm",
    noTankData: "No tank data",
    water: {
      inlet: "Inlet",
      outlet: "OUT",
      outletStatus: "Outlet",
      units: {
        litersShort: "L",
        centimetersShort: "cm",
      },
      hints: {
        ph_optimal: "Optimal range",
      },
      errors: {
        ph_out_of_range: "pH out of range",
        low_level: "Low water level",
        temp_high: "Warning: above 28°C",
      },
    },
    time: "Time",
    liters: "Liters (L)",
    summary: "Summary",
    autoRefresh: "Auto refresh",
    getConfig: "Get config",
    pushNow: "Push now",
    softResetConfirm: "Soft reset?",
    apply: "Apply",
    config: "Config",
    calibrateIn: "Calibrate In",
    calibrateOut: "Calibrate Out",
    inTotalDelta: "In total Δ",
    outTotalDelta: "Out total Δ",
    avg: "avg",
    displayIntervalLabel: "Display interval",
    serverIntervalLabel: "Server interval",
    lastTs: "Last ts",
    inPpl: "In ppl",
    outPpl: "Out ppl",
    unknown: "unknown",
    online: "online",
    date: "Date",
    marketName: "Market Name",
    internalNumber: "Internal #",
    firmwareUpdate: "Firmware Update",
    latest: "Latest",
    lastUploaded: "Last uploaded",
    flash: "Flash",
    photoUploading: "Uploading photo...",
    photoTooLarge: "File is too large (max 3MB)",
    unsupportedFormat: "Unsupported format",
    tooManyFiles: "Too many files",
    editPhoto: "Edit photo",
    rotateLeft: "Rotate left",
    rotateRight: "Rotate right",
    zoom: "Zoom",
    uploadFailed: "Upload failed",
    flashFailed: "Flash failed",
    invalidDeviceId:
      "Invalid controller ID format. Make sure it has no spaces and at least 24 characters.",
    getFreshData: "Get Fresh Data",
    notifyOnOffline: "Notify on electricity failure",
    powerLossAlerts: "Telegram power-loss alerts",
    telegramAlertsOn: "Telegram alerts: ON",
    telegramAlertsOff: "Telegram alerts: OFF",
    trendAlerts: "Trend alerts",
    climateCharts: "Climate Charts",
    climateNoData24h: "No data for the last 24 hours.",
    climateVpdProOnly: "VPD analysis is available on Pro and Enterprise plans.",
    climateExpandDetails: "Expand details",
    climateHideDetails: "Hide details",
    climateRelays: "Relays",
    climateSetpointsDay: "Setpoints: Day",
    climateSetpointsNight: "Setpoints: Night",
    climateSaveThresholds: "Save thresholds",
    climateSaving: "Saving...",
    climateLightStatus: "Light Status",
    climateNightPhaseInRoom: "🌙 NIGHT PHASE (IN ROOM)",
    last24Hours: "Last 24 hours",
    settings: "Settings",
    deviceSettings: "Controller settings",
    deviceSettingsBrandTitle: "Shefa Green Controller — settings",
    deviceSettingsBrandSubtitle: (deviceId) =>
      deviceId ? `Controller ID: ${deviceId}` : "",
    backToController: "Back to controller",
    backToControllers: "Back to controllers",
    selectFunction: "Select function",
    commandSuccess: "✅ Success",
    commandError: "❌ Error: check argument",
    particleAuthError:
      "Authorization error. Check access_token and controller ownership.",
    refreshReadings: "Refresh readings",
    refreshConfig: "Refresh config",
    displayInterval: "Display interval (min)",
    serverInterval: "Server interval (min)",
    saveConfig: "Save config",
    loadConfig: "Load config",
    factoryReset: "Factory reset",
    wipeEEPROM: "Wipe EEPROM",
    relayLabels: { A: 'AC', D: 'Dryer', C: 'CO₂', L: 'Lights', reserve: 'Reserve', mask: 'mask' },
    intervalError: "Interval must be >0 and <=1440 minutes",
    functions: {
      setHumidityThreshold: "💧 Humidity thresholds (%)",
      setTemperatureThreshold: "🌡 Temperature thresholds (°C)",
      setCO2Threshold: "CO₂ thresholds (ppm)",
      toggleSocket: "🔌 Sockets control",
      calibrateCO2ppm: "🔧 Calibrate CO₂",
      setDisplayInterval: "🕒 Display interval (minutes)",
      setServerInterval: "📤 Server interval (minutes)",
      syncTimeNow: "🕒 Sync time now",
      setTimezone: "🌍 Set timezone",
      setManualTime: "⌚ Set manual time",
      getReadings: "📈 Get readings",
      softReset: "🔄 Soft reset",
      setVPDType: "🌿 VPD type",
    },
    args: {
      min: "Min",
      max: "Max",
      ppm: "PPM",
      minutes: "Minutes",
      timezone: "Timezone",
      manualTimestamp: "Time",
      type: "Type",
      submit: "Submit",
    },
    enums: {
      air: "Air",
      leaf: "Leaf",
      "leaf+": "Leaf+",
      on: "On",
      off: "Off",
      auto: "Auto",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
    },
    hints: {
      setHumidityThreshold: "example: 40.0,60.0",
      setTemperatureThreshold: "example: 18.0,25.0",
      setCO2Threshold: "example: 400,1000",
      calibrateCO2ppm: "example: 400",
      setDisplayInterval: "Enter amount in minutes (e.g., 1).",
      setServerInterval: "Enter sending interval in minutes, minimum 1.",
      toggleSocket: "example: 1,on",
      setVPDType: "air | leaf | leaf+",
    },
  },
  ua: {
    language: "Українська",
    nav: {
      home: "Головна",
      dashboard: "Панель",
      profile: "Профіль",
      news: "Новини",
      bugs: "Баги",
      errors: "Помилки",
      tasks: "Завдання",
      devices: "Контролери",
      rooms: "Зони вирощування",
      strains: "Сорти",
      cuttings: "Живці",
      secondaryMenu: "Ще",
      adminMenu: "Адмін",
      about: "Про нас",
      manageCuttings: "Керування живцями",
      manageStrains: "Керування сортами",
      manageReviews: "Проф. огляди",
      manageNews: "Керування новинами",
      users: "Користувачі",
      adminPanel: "Панель адміністратора",
      manageFarms: "Керування об’єктами",
      unassignedDevices: "Вільні контролери",
      wifiBle: "Wi‑Fi BLE",
      farmGroup: "Об’єкт",
      farmsBreadcrumb: "Об’єкти",
      livePanel: "Жива панель",
      farm: "Об’єкт",
      geneticsGroup: "Генетика",
      engineeringPanel: "Інженерна панель",
      adminBreadcrumb: "Адмін",
      allControllers: "Усі контролери",
    },
    roleSwitcher: {
      label: "Тест ролі",
      reset: "Скинути",
    },
    role: {
      admin: "Адмін",
      superuser: "Суперкористувач",
      farmOwner: "Власник об’єкта",
      farmManager: "Менеджер об’єкта",
      worker: "Працівник",
    },
    planTier: {
      included: "Старт",
      basic: "Базовий",
      standard: "Стандарт",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    deviceTabs: {
      overview: "Огляд",
      settings: "Налаштування",
    },
    farmsAdmin: {
      title: "Керування об’єктами",
      colName: "Назва",
      colOwner: "Власник",
      colUsers: "Користувачів",
      colDevices: "Контролерів",
      colActions: "Дії",
      create: "Створити об’єкт",
      edit: "Редагувати",
      usersAction: "Користувачі",
      delete: "Видалити",
      editTitle: "Редагувати об’єкт",
      ownerUnassigned: "Без власника",
      saveChanges: "Зберегти зміни",
      savingChanges: "Збереження…",
      confirmDeleteTitle: "Видалення об’єкта",
      confirmDelete: (name) =>
        `Видалити об’єкт «${name}»? Можна видалити лише порожній об’єкт без користувачів, контролерів і даних.`,
      modalTitle: "Новий об’єкт",
      farmNamePlaceholder: "Назва об’єкта",
      createSubmit: "Створити",
      creating: "Створення…",
      deleting: "Видалення…",
      accessDenied: "Розділ лише для адміністраторів та суперкористувачів.",
      usersAccessDenied: "Користувачами можуть керувати лише адміністратори та суперкористувачі.",
      moveFarmLabel: "Перенести на інший об’єкт",
      apply: "Застосувати",
      unassigned: "Без прив’язки",
      freePoolOption: "-- Вільний пул (Відв’язати) --",
      freeStatus: "Статус: Вільний",
      moving: "Збереження…",
      farmUpdated: "Прив’язку контролера до об’єкта оновлено",
      deviceFreed: "Контролер звільнено та переведено у вільний пул",
      farmUpdateNoop: "Без змін",
      farmUpdateFailed: "Не вдалося змінити об’єкт",
      farmColumn: "Об’єкт",
      selectFarmNewUser: "Об’єкт",
      loading: "Завантаження…",
      empty: "Немає об’єктів",
      loadError: "Не вдалося завантажити список об’єктів",
      createFailed: "Не вдалося створити об’єкт",
      deleteFailed: "Не вдалося видалити об’єкт",
      updateFailed: "Не вдалося оновити об’єкт",
      planTypeLabel: "Тарифний план",
    },
    welcome: {
      title: "ЛАСКАВО ПРОСИМО ДО SHEFAGREENAPP",
      subtitle: "Shefa Green – розумне управління плантаціями та кліматом.",
    },
    home: {
      hero: {
        title: "Керуйте сортами й циклами вирощування з Shefa Green",
        subtitle: "Захищена платформа для сортів, розмноження, просторів вирощування, клімату, води, обладнання, щоденника гроувера, аналітики та майбутньої AI-підтримки рішень у вирощуванні.",
        slogan: "Сорти в центрі. Вирощування під контролем.",
        openDashboard: "Відкрити дашборд",
        about: "Про нас",
      },
      public: {
        eyebrow: "Робочий простір навколо сортів",
        valueEyebrow: "Для операцій гроувера",
        valueTitle: "Система навколо сортів, генетики та циклів вирощування, а не просто пристроїв",
        valueSubtitle:
          "Shefa Green поєднує розмноження, зони вирощування, пресети, щоденник, дії контролерів, задачі й аналітику в один операційний шар для професійних гроуверів.",
        title: "Shefa Green допомагає гроуверам керувати повним процесом вирощування навколо сортів.",
        body:
          "Shefa Green об'єднує сорти, цикли вирощування, простори вирощування, живці, клімат, воду, обладнання, щоденник гроувера, аналітику та майбутню AI-підтримку рішень в одному захищеному workspace. Кожен сорт можна вести через розмноження, переміщення між просторами вирощування, стадії росту, спостереження, обробки та історію вирощування, а ShefaGreen Controllers підтримують середовище навколо рослини.",
        notice:
          "Операційні дані доступні лише після входу. Публічні відвідувачі не бачать імена контролерів, ID контролерів, назви просторів вирощування, назви об’єктів, сенсорні значення, задачі, записи сортів, живці, щоденник вирощування або результати аналітики.",
        statsAria: "Ключові переваги платформи Shefa Green",
        stats: {
          strainCentered: {
            value: "100%",
            label: "Навколо сортів",
          },
          aiReady: {
            value: "AI-ready",
            label: "Roadmap підтримки рішень",
          },
          protected: {
            value: "Secure",
            label: "Захищений workspace",
          },
        },
        preview: {
          activeStrain: "Активний сорт",
          flowering: "Цвітіння",
          cycleProgress: "Прогрес циклу вирощування",
        },
        controllerPill: "ShefaGreen Controllers",
        controllersTitle: "Моніторинг і керування середовищем вирощування",
        controllersBody:
          "ShefaGreen Controllers — це обладнання для вирощування, яке може не лише моніторити, а й керувати кліматом, вентиляцією, охолодженням, опаленням, зрошенням, якістю води, потоком води та статусом обладнання в захищеному workspace.",
        ctaBadge: "Захищена професійна платформа",
        ctaTitle: "Один захищений робочий простір для управління вирощуванням",
        ctaBody:
          "Ведіть кожен сорт від генетики до врожаю, поки команда, контролери, щоденник, аналітика й майбутній AI-асистент залишаються пов'язаними в одному місці.",
        ctaButton: "Увійти в Shefa Green",
        pricing: {
          eyebrow: "Тарифи",
          title: "Плани підписки для об’єктів",
          subtitle:
            "Головна відмінність — глибина збереження та перегляду історії клімат-даних у хмарі та доступ до розширеного аналізу VPD (дефіциту тиску пари).",
          commonFeatures:
            "На будь-якому тарифі одна й та сама робоча область: сорти, зони вирощування, пресети, щоденник, контролери, задачі, клімат і вода — обмеження стосуються лише глибини телеметрії.",
          retentionNote:
            "Тривалість зберігання «сирої» клімат-телеметрії задає план; за межами вікна точки ротуються, графіки й вибірки не виходять далі зазначеного періоду.",
          footer:
            "Тариф призначає адміністратор Shefa Green. Для зміни плану об’єкти зверніться до підтримки.",
          plans: {
            included: {
              tagline: "Рівень за замовчуванням після підключення",
              bullets: [
                "1 день історії клімат-даних для графіків і огляду.",
                "Розширені режими аналізу з акцентом на VPD вимкнено; базові кліматичні метрики доступні.",
              ],
            },
            basic: {
              tagline: "Короткий операційний огляд",
              bullets: ["3 дні історії клімат-даних.", "Розширений аналіз VPD недоступний."],
            },
            standard: {
              tagline: "Тижневе вікно для звітів",
              bullets: ["7 днів історії клімат-даних.", "Розширений аналіз VPD недоступний."],
            },
            pro: {
              tagline: "Місяць історії та VPD",
              bullets: [
                "30 днів історії клімат-даних.",
                "Розширений аналіз VPD увімкнено (функція активується на рівні об’єкти в системі).",
              ],
            },
            enterprise: {
              tagline: "Довге сховище та VPD",
              bullets: ["365 днів історії клімат-даних.", "Розширений аналіз VPD увімкнено."],
            },
          },
        },
        capabilities: {
          temperature: "Температура",
          humidity: "Вологість",
          co2: "CO2",
          ventilation: "Вентиляція",
          cooling: "Охолодження",
          heating: "Опалення",
          irrigation: "Зрошення",
          waterFlow: "Потік води",
          ec: "EC",
          ph: "pH",
          equipmentStatus: "Статус обладнання",
        },
        modules: {
          strains: {
            title: "Сорти",
            description:
              "Зробіть генетику центром системи: ведіть кожен сорт через розмноження, переміщення, стадії росту, спостереження, обробки та історію вирощування.",
          },
          growthPresets: {
            title: "Пресети росту",
            description:
              "Плануйте налаштовувані пресети для стадій вирощування, щоб цілі просторів вирощування і робочі рутини відповідали життєвому циклу рослини.",
          },
          growerDiary: {
            title: "Щоденник гроувера",
            description:
              "Записуйте події вирощування, нотатки, спостереження, обробки, зміни стадій, історію просторів вирощування і рішення гроувера.",
          },
          analytics: {
            title: "Аналітика",
            description:
              "Порівнюйте сорти, зони вирощування, стадії росту, клімат, воду, цикли вирощування та операційні результати з часом.",
          },
          controllers: {
            title: "ShefaGreen Controllers",
            description:
              "Використовуйте ShefaGreen Controllers як обладнання для вирощування, що може моніторити й керувати середовищем у захищеному workspace.",
          },
          climateAutomation: {
            title: "Автоматизація клімату",
            description:
              "Підтримка температури, вологості, CO2, вентиляції, охолодження, опалення та налаштованих кліматичних виходів з одного workspace.",
          },
          waterControl: {
            title: "Контроль води",
            description:
              "Робота зі зрошенням, потоком води, EC, pH, ORP, рівнем води та температурою води як частиною циклу вирощування.",
          },
          cuttings: {
            title: "Живці",
            description:
              "Керуйте розмноженням і живцями як частиною повного життєвого циклу сорту — від вихідної генетики до призначення в простір вирощування.",
          },
          tasks: {
            title: "Обладнання та задачі",
            description:
              "Координуйте статус обладнання, обслуговування, follow-up, робочі задачі, проблеми та відповідальність команди з доступом за ролями.",
          },
          aiRoadmap: {
            title: "Аналітика та майбутній AI-асистент",
            description:
              "Планується майбутній інтелектуальний шар, який розумітиме історію вирощування, знаходитиме патерни, аналізуватиме результати й пропонуватиме можливі дії на основі зібраних даних.",
          },
        },
      },
      ticker: {
        ariaLabel: "Стрічка статусів пристроїв у реальному часі",
        online: (n) => `Онлайн: ${n}`,
        offline: (n) => `Офлайн: ${n}`,
        lastSeen: (time) => `останній раз ${time}`,
        empty: "Немає даних про контролери",
      },
      strains: {
        title: "Сорти сьогодні",
        viewAll: "Усі сорти",
        empty: "Немає сортів для показу",
        updated: (date) => `Оновлено ${date}`,
      },
      news: {
        title: "Новини",
        viewAll: "Усі новини",
      },
      tasks: {
        title: "Задачі",
        viewAll: "Усі задачі",
        empty: "Немає відкритих задач",
        superuserOnly: "Задачі доступні лише для суперкористувачів.",
      },
      quickActions: {
        title: "Швидкі дії",
        qr: "QR",
      },
      footer: {
        telegram: "Бот у Telegram",
      },
    },
    dashboard: {
      title: "Жива панель",
      lastUpdated: (time) => `Оновлено ${time}`,
      autoRefresh: {
        label: "Автооновлення",
        enabled: (seconds) => `Оновлення кожні ${seconds} сек`,
        disabled: "Автооновлення призупинено",
      },
      refreshNow: "Оновити зараз",
      errors: {
        farmContextRequired: "Оберіть об’єкт або увійдіть знову, щоб завантажити огляд.",
      },
      summary: {
        title: "Зведення системи",
        totalDevices: "Усього пристроїв",
        online: "Пристроїв онлайн",
        offline: "Пристроїв офлайн",
        lastData: "Останнє зчитування",
        totalStrains: "Усього сортів",
        noData: "Немає даних",
      },
      climate: {
        title: "Клімат по об’єкті (середнє)",
        temperature: "Середня температура",
        humidity: "Середня вологість",
        co2: "Середній CO₂",
        noData: "—",
      },
      alerts: {
        title: "Останні проблеми та попередження",
        viewAll: "Усі звернення",
        empty: "Немає критичних подій за останній період.",
        deviceOffline: "Контролер офлайн",
      },
      recentDevices: {
        title: "Останні контролери",
        viewAll: "Усі контролери",
        empty: "Немає останньої активності пристроїв",
        lastSeen: (time) => `останній раз ${time}`,
      },
      status: {
        online: "Онлайн",
        offline: "Офлайн",
      },
      strains: {
        title: "Останні сорти",
        viewAll: "Усі сорти",
        empty: "Немає оновлень сортів",
      },
      news: {
        title: "Останні новини",
        viewAll: "Усі новини",
        empty: "Новин ще немає",
      },
      tasks: {
        title: "Відкриті задачі",
        viewAll: "Усі задачі",
        empty: "Немає відкритих задач",
        superuserOnly: "Задачі доступні лише для суперкористувачів.",
      },
      rooms: {
        title: "Зони вирощування",
        heading: "Приміщення та клімат",
        subtitle:
          "Зони всередині об’єкти. Контролери та щоденник гровера прив'язані до обраної зони вирощування.",
        addRoom: "Додати простір вирощування",
        create: "Створити простір вирощування",
        namePlaceholder: "Назва (наприклад, Вегетарій)",
        typePlaceholder: "Тип (необов'язково)",
        empty: "Кімнат поки немає",
        loading: "Завантаження…",
        open: "Відкрити",
        delete: "Видалити",
        confirmDeleteRoom:
          "Видалити цей простір вирощування? Контролери залишаться на об’єкті, але без прив'язки до простору вирощування.",
      },
      planBadge: (label) => `План: ${label}`,
    },
    roomsPage: {
      breadcrumbInRoom: "Приміщення",
      back: "До живої панелі",
      backToRooms: "До списку просторів вирощування",
      backDashboard: "До живої панелі",
      diaryRoomHint: "Кожен запис зберігається для цієї зони вирощування та включає знімок датчиків приміщення.",
      devices: "Контролери в зоні вирощування",
      noDevices:
        "У цьому просторі вирощування поки немає контролерів. Призначте простір вирощування в налаштуваннях контролера.",
      diaryTitle: "Щоденник гровера",
      diaryNote: "Нотатка",
      diaryAdd: "Додати запис",
      diaryEmpty: "Записів поки немає",
      diarySensors: "Показники датчиків (авто)",
      sensorsNone: "Немає рядків датчиків",
      loadError: "Не вдалося завантажити простір вирощування",
      claimInvalidContext: "Не вдалося визначити ID пристрою або контекст зони вирощування.",
      claimFailed: "Не вдалося прив'язати контролер.",
      claimSuccess: "Зв'язок встановлено! Контролер активував простір вирощування.",
      notConnectedTitle: "Кімнату не підключено",
      notConnectedDescription:
        "Додайте новий Shefa Green Controller до цієї зони вирощування, щоб запустити телеметрію та керування.",
      connectArgon: "Підключити Shefa Green Controller",
      connectModalTitle: "Підключення Shefa Green Controller",
      step1: "Крок 1 з 3",
      step2: "Крок 2 з 3",
      step3: "Крок 3 з 3",
      prepareDeviceTitle: "Підготовка пристрою",
      prepareDeviceDescription:
        "Увімкніть Shefa Green Controller у розетку. Переконайтеся, що він блимає синім - це режим готовності до прив'язки.",
      searchingHint: "Shefa Green Controller шукає свій простір вирощування...",
      identifyDeviceTitle: "Ідентифікація пристрою",
      identifyDeviceDescription: "Скануйте QR-код пристрою або введіть deviceId вручну.",
      scanQr: "Сканувати QR",
      deviceIdLabel: "Controller ID",
      deviceIdPlaceholder: "наприклад, 34003f001447343339383037",
      connectDevice: "Підключити",
      claimingInProgress: "Шукаємо Shefa Green Controller і встановлюємо зв'язок...",
      updatingRoomData: "Оновлюємо дані зони вирощування...",
      next: "Далі",
      back: "Назад",
      close: "Закрити",
      statusOnline: "Online",
      statusOffline: "Offline",
      lightDay: "Світловий день",
      lightNight: "Ніч",
      lightAlertLampOff: "Увага: лампа не горить?",
      summaryTotalRooms: "Всього просторів вирощування",
      summaryActiveControllers: "Активних контролерів",
      summaryProblems: "Проблем",
      modeGrowing: "Ростемо",
      modeSleeping: "Спимо",
      modeNeedsSetup: "Потрібне налаштування",
      setupRequired: "Потрібне налаштування: підключіть контролер для запуску моніторингу.",
      kpiTemperature: "Температура",
      kpiHumidity: "Вологість",
      kpiConnection: "Зв'язок",
      kpiWifiRssi: "Wi-Fi (RSSI)",
      actionSettings: "Налаштування",
      actionCharts: "Графіки",
      actionBlink: "Блимнути",
      refreshingStatuses: "Оновлюємо статуси просторів вирощування...",
      breadcrumbOverview: "Огляд",
      kpiCo2: "CO₂",
      kpiDevicesOnline: "Контролери онлайн",
      growerDiarySummaryTitle: "Зведення по днях",
      growerDiarySummaryHint:
        "Періоди «День» і «Ніч» визначаються за статусом світла в телеметрії (day_status), а не за годинником.",
      growerDiaryTablePlaceholder:
        "Тут з’явиться таблиця мінімумів і максимумів за світловий день і ніч.",
      tableDate: "Дата",
      tableMetric: "Параметр",
      tablePeriodDay: "День (світло увімкнено)",
      tablePeriodNight: "Ніч (світло вимкнено)",
      tableMin: "Мін",
      tableMax: "Макс",
      diaryAnalyticsTitle: "Щоденник і Аналітика",
      analyticsLoading: "Завантаження зведення…",
      analyticsError: "Не вдалося завантажити зведення",
      analyticsTruncated: "Обрізка вибірки",
      analyticsTruncatedBanner: (cap) =>
        `У розрахунок потрапили лише останні ${cap} вимірів у вікні; старіші точки не враховано. За потреби звузьте період або набір пристроїв.`,
      analyticsSamples: "Точок у зведенні",
      colPeriod: "Період",
      colTempMm: "Темп. мін–макс",
      colRhMm: "Волог. мін–макс",
      colCo2Avg: "CO₂ сер.",
      colVpdAvg: "VPD сер.",
      colDailyNote: "Нотатка дня",
      dailyNoteSave: "Зберегти",
      dailyNoteSaved: "Збережено",
      periodDayShort: "День",
      periodNightShort: "Ніч",
    },
    invitePage: {
      title: "Приєднатися до об’єкти як працівник",
      invalidLink: "Посилання-запрошення недійсне.",
      missingToken: "Токен запрошення відсутній.",
      registrationFailed: "Не вдалося зареєструватися.",
      emailPlaceholder: "Email",
      passwordPlaceholder: "Пароль",
      creatingAccount: "Створюємо акаунт...",
      createAccount: "Створити акаунт",
    },
    unassignedPage: {
      title: "Вільні контролери",
      subtitle: "Призначте об’єкт кожному контролеру.",
      colDevice: "Контролер",
      colFarm: "Об’єкт",
      apply: "Призначити об’єкт",
      loading: "Завантаження…",
      empty: "Немає вільних контролерів",
      loadError: "Не вдалося завантажити",
      saveError: "Не вдалося призначити об’єкт",
      saved: "Ферму оновлено",
    },
    adminControllers: {
      title: "Усі контролери",
      subtitle: "Усі контролери на всіх об’єктах. Пошук і фільтри діють на цій сторінці.",
      searchLabel: "Пошук",
      searchPlaceholder: "ID, ім'я, об’єкт або зона вирощування…",
      statusFilter: "Статус",
      statusAll: "Усі",
      statusOnline: "Онлайн",
      statusOffline: "Офлайн",
      displayFilter: "Тип дисплея",
      displayAll: "Усі",
      colControllerId: "ID контролера",
      colDeviceName: "Ім'я",
      colFarm: "Об’єкт",
      colRoom: "Зона вирощування",
      colStatus: "Статус",
      colHardware: "Конфігурація",
      colLastSeen: "Остання активність",
      colActions: "Дії",
      badgeOnline: "Онлайн",
      badgeOffline: "Офлайн",
      viewLogs: "Логи",
      reboot: "Перезапуск",
      rebootConfirm: "Перезавантажити цей контролер (soft reset)?",
      rebootFailed: "Не вдалося перезавантажити",
      unbind: "Відв'язати",
      unbindTitle: "Відв'язати від об’єкта та простору вирощування",
      unbindModalTitle: "Відв'язати контролер",
      unbindModalBody:
        "Відв'язати контролер {{id}} від об’єкта «{{farm}}»? Він з'явиться у списку вільних контролерів.",
      unbindConfirm: "Відв'язати",
      unbindCancel: "Скасувати",
      unbindFailed: "Не вдалося відв'язати",
      assign: "До зони вирощування",
      assignTitle: "Прив'язати до об’єкта та простору вирощування",
      assignModalTitle: "Прив'язати до об’єкта та простору вирощування",
      assignModalHint: "Виберіть об’єкт і простір вирощування — у записі контролера оновляться farmId і roomId.",
      assignFarmLabel: "Об’єкт",
      assignFarmPlaceholder: "Виберіть об’єкт…",
      assignRoomLabel: "Зона вирощування",
      assignRoomPlaceholder: "Виберіть простір вирощування…",
      assignRoomLoading: "Завантаження просторів вирощування…",
      assignSubmit: "Прив'язати",
      assignCancel: "Скасувати",
      assignFailed: "Не вдалося прив'язати",
      assignNeedFarmRoom: "Вкажіть об’єкт і простір вирощування",
      controllerTypeWater: "Водяний контролер",
      controllerTypeClimate: "Клімат-контролер",
      controllerTypeGeneric: "Контролер",
      empty: "Немає пристроїв за фільтром",
      loading: "Завантаження…",
      loadError: "Не вдалося завантажити список",
    },
    deviceAssignment: {
      roomLabel: "Зона вирощування",
      roomNone: "Без зони вирощування",
      roomSave: "Зберегти простір вирощування",
      releaseDevice: "Звільнити контролер",
      releaseConfirm:
        "Зняти прив'язку до об’єкта та простору вирощування? Контролер стане доступним для призначення на інший об’єкт.",
      releaseDone: "Контролер звільнено",
      releaseError: "Не вдалося звільнити",
      releaseHint: "Лише суперкористувач — скидає об’єкт і простір вирощування.",
      unbindFarm: "Відв'язати від об’єкти",
      unbindConfirm:
        "Відв'язати контролер від об’єкти? Він з'явиться у реєстрі вільних до повторної прив'язки.",
      unbindDone: "Контролер відв'язано",
      unbindError: "Не вдалося відв'язати",
      superuserOnly: "Лише суперкористувач",
    },
    loading: "Завантаження...",
    noNews: "Новин поки що немає",
    updated: "Оновлено",
    refresh: "Оновити",
    toggleNavigation: "Перемкнути навігацію",
    retry: "Повторити",
    showOffline: (n) => `Показати офлайн контролери (${n})`,
    hideOffline: "Сховати офлайн контролери",
    noData: "Немає даних",
    no_data: "Немає даних",
    statusOn: "ON",
    statusOff: "OFF",
    co2CheckTank: "Перевірте балон",
    firmwareUpload: "Завантаження прошивки",
    upload: "Завантажити",
    cuttings: "Живці",
    news: "Новини",
    latestReadings: "Останні показники",
    loginFailed: "Не вдалося увійти",
    copyToClipboard: "Скопіювати в буфер обміну:",
    saveFailed: "Не вдалося зберегти",
    settingsSavedSuccess: "Налаштування успішно збережено",
    yes: "Так",
    no: "Ні",
    timeSyncTitle: "Синхронізація часу",
    refreshTimeSync: "Оновити",
    timeSyncHint: "Використовуйте NTP як основне джерело часу. Контролер має рахувати в UTC і застосовувати timezone тільки для логіки день/ніч.",
    serverTimeLabel: "Час сервера",
    controllerTimeLabel: "Час контролера",
    timeDriftLabel: "Різниця (drift)",
    timeUnknown: "Невідомо",
    farmTimezoneLabel: "Часовий пояс об’єкти",
    farmTimezoneHint: "Збережіть IANA timezone (наприклад, Asia/Jerusalem). Це значення повертається з /api/system/time і застосовується на контролері для режиму день/ніч.",
    saveTimezone: "Зберегти часовий пояс",
    co2ManagementTitle: "Керування CO2",
    co2OffsetLabel: "Значення калібрування CO2 (ppm)",
    co2OffsetHint: "Корекція показань відносно еталона VDL.",
    calibrationTargetPpmPlaceholder: "Стандарт: 400. Для Мертвого моря: 580",
    syncNow: "Синхронізувати зараз",
    forceCalibrateButton: "Почати калібрування",
    forceCalibrateHint: "Запускати тільки після 5 хв провітрювання.",
    calibrationStatus: {
      pending: "⏳ Очікування синхронізації з контролером...",
      started: (left) => `✅ Калібрування запущено (5 хв) ${left}`,
    },
    manualTimeLabel: "Ручний час",
    setManualTime: "Встановити час",
    manualTimeHint: "Використовуйте лише в ізольованих мережах без NTP. Команда надсилає Unix-time в UTC разом із timezone об’єкти.",
    waterPage: {
      idLabel: "ID",
      firmwareLabel: "Прошивка",
      range2h: "2 год",
      range24h: "24 год",
      range7d: "7 дн",
      rangeCustom: "Період",
      latestTitle: "Останні дані",
      noHistory: "Історії води ще немає.",
      inletLine: "Вхід",
      outletLine: "Вихід",
      litersPerMinuteShort: "л/хв",
      litersShort: "л",
      successOk: "ОК",
      genericError: "Помилка",
    },
    deviceSettingsPage: {
      calibrationVentilationConfirm: "Переконайтеся, що приміщення провітрювалось щонайменше 5 хвилин перед підтвердженням.",
      timezonePlaceholder: "Asia/Jerusalem",
      co2OffsetPlaceholder: "0",
      currentValue: "Поточне",
      co2OffsetSaveHint:
        "Зміщення CO₂ з блоку вище зберігається разом із часовим поясом кнопкою «Зберегти часовий пояс».",
      advancedFunctionsTitle: "Розширені команди Particle",
      vpdProPlanLock:
        "Аналіз VPD доступний у тарифах Pro та Enterprise. Оновіть план об’єкти, щоб змінювати тип VPD.",
      locked: "заблоковано",
    },
    deviceSettingsModal: {
      actionsTitle: "Дії контролера",
      actionsHint: "Ручні сервісні команди для вибраного контролера.",
      restartButton: "Перезапустити контролер",
      restartConfirmTitle: "Підтвердьте перезапуск контролера",
      restartWarning: "Контролер може бути недоступний близько 30-90 секунд під час перезавантаження. Реле, пороги, режими та EEPROM-налаштування не зміняться.",
      restartConfirmButton: "Так, перезапустити",
      restartSending: "Надсилаємо команду",
      restartAccepted: "Команду перезапуску прийнято. Контролер перезавантажується і може бути офлайн 30-90 секунд.",
      restartFailed: "Не вдалося перезапустити контролер.",
      restartNotReachable: "Перезапуск не виконано: контролер зараз недоступний.",
      modeAutoBadge: "A",
      modeManualOnBadge: "M:ON",
      modeManualOffBadge: "M:OFF",
      relayModeAria: (label) => `${label}: режим розетки`,
      modeAuto: "Авто",
      modeManualOn: "Ручне блокування: увімк.",
      modeManualOff: "Ручне блокування: вимк.",
      deviceNameTitle: "Назва пристрою",
      deviceNamePlaceholder: "Назва пристрою",
      telegramAlerts: "Сповіщення Telegram",
      powerLoss: "Втрата живлення",
      thresholdAlerts: "Сповіщення порогів",
      trendAlerts: "Сповіщення трендів",
      climateDeliveryOn: "Кліматичні сповіщення доставляються",
      climateDeliveryBlocked: "Кліматичні сповіщення не будуть доставлені",
      climateDeliveryOff: "Кліматичні сповіщення вимкнені",
      climateDeliveryCounts: (enabled, total) => `Отримувачі Climate alerts: ${enabled}/${total}`,
      climateDeliveryNoRecipients:
        "Сповіщення контролера увімкнені, але немає прив'язаних Telegram-отримувачів для кліматичних сповіщень.",
      climateDeliveryClimateDisabled:
        "Сповіщення контролера увімкнені, але в усіх доступних Telegram-отримувачів вимкнена категорія Climate alerts.",
      climateDeliveryTelegramSettingsHint:
        "Відкрийте налаштування сповіщень у Telegram-боті та увімкніть категорію Climate alerts.",
      enableClimateAlertsForMe: "Увімкнути Climate alerts для мене",
      enablingClimateAlerts: "Увімкнення Climate alerts",
      climateAlertsEnabledForMe: "Climate alerts увімкнено для вашого Telegram.",
      climateAlertsEnableFailed: "Не вдалося увімкнути Climate alerts.",
      co2Management: "Керування CO2",
      co2Offset: "Значення калібрування CO2 (ppm)",
      startCalibration: "Почати калібрування",
      calibrationTargetPpmLabel: "Значення калібрування (PPM)",
      calibrationTargetPpmPlaceholder: "Стандарт: 400. Для Мертвого моря: 580",
      calibrationHint: "Запускати лише після 5 хв провітрювання",
      calibrationConfirm: "Ви впевнені? Запускати лише після 5 хвилин провітрювання!",
      relayManagement: "Керування розетками",
      waterMetric: { ph: "pH", ec: "EC", orp: "ORP", temp: "Температура", level: "Рівень води", volume: "Об'єм" },
    },
    deviceCard: {
      unknownDeviceName: "Клімат-контролер",
      idLabel: "ID",
      idDialogTitle: "ID контролера",
      idPreviewAria: "Показати повний ID контролера та скопіювати його",
      copyId: "Скопіювати ID",
      copied: "Скопійовано",
      connectivityOnline: "Онлайн",
      connectivityNoRecentData: "Немає свіжих даних",
      connectivityOffline: "Офлайн",
      controllerOfflineCommands: "Контролер офлайн. Команди не виконуються.",
      syncTimeFailed: "Не вдалося синхронізувати час",
      calibrationFailed: "Калібрування не вдалося",
      climateParamsUpdated: "Параметри клімату оновлено",
      relayCommandTimeout: "Помилка: контролер не підтвердив виконання команди (таймаут)",
      relayCommandUnconfirmed:
        "Режим збережено, але контролер не надіслав підтвердження. Оновіть сторінку, якщо стан не збігається з пристроєм.",
      modeLabel: "режим",
      errorGeneric: "Помилка",
      invalidThreshold: "Невірний поріг: введіть числові Min/Max та переконайтесь, що Min <= Max.",
    },
    settingsApplyFailedPrefix: "Не вдалося застосувати",
    settingsProgressSavingDb: "Зберігаємо налаштування в базі...",
    settingsProgressApplyingController: "Застосовуємо пороги на контролер",
    settingsProgressControllerCheck: "Пороги збережено в базі, перевіряємо контролер...",
    settingsProgressSavingSystem: "Зберігаємо системні параметри...",
    settingsProgressSyncingView: "Синхронізуємо відображення...",
    co2OffsetNotSavedWarning: "CO2 Offset не збережено (timezone endpoint недоступний)",
    noCuttings: "Немає живців",
    noStrains: "Сорти не знайдені",
    noBugs: "Немає багів для відображення",
    login: "Увійти",
    logout: "Вийти",
    profile: {
      title: "Профіль",
      subtitle: "Дані облікового запису та прив’язка Telegram.",
      farm: "Об’єкт",
      linkTelegram: "Підключити Telegram",
      telegramLinked: "Telegram підключено",
      telegramNotLinked: "Telegram не підключено",
      openTelegramBot: "Відкрити Telegram-бота",
      disconnectTelegram: "Відключити Telegram",
      disconnectingTelegram: "Відключаємо Telegram…",
      disconnectError: "Не вдалося відключити Telegram. Спробуйте пізніше.",
      linkError: "Не вдалося отримати посилання. Спробуйте пізніше.",
      openingBot: "Відкриваємо бота…",
      refreshStatus: "Оновити статус",
      telegramHint:
        "Підключіть Telegram, щоб отримувати сповіщення. Після підключення тут можна відкрити бота або відключити Telegram.",
    },
    telegramConnect: {
      eyebrow: "Telegram",
      title: "Підключення облікового запису Telegram",
      description:
        "Підключіть обліковий запис Telegram, щоб отримати доступ до об’єктів, зон вирощування, сповіщень та інструментів контролерів.",
      button: "Підключити Telegram",
      connecting: "Підключаємо Telegram...",
      linkError: "Не вдалося створити посилання Telegram. Спробуйте пізніше.",
      signInAgain: "Щось пішло не так. Увійдіть ще раз.",
      rateLimit: "Зачекайте кілька секунд і спробуйте ще раз.",
      retryButton: "Повторити підключення",
      openTelegramButton: "Відкрити Telegram і завершити підключення",
      copyConnectCodeButton: "Скопіювати код підключення",
      copyConnectCodeDone: "Код підключення скопійовано",
      copyError: "Не вдалося скопіювати код. Скопіюйте його вручну.",
      widgetLoading: "Завантажуємо кнопку входу Telegram...",
      widgetLoadError: "Не вдалося завантажити кнопку входу Telegram. Оновіть сторінку або скористайтеся резервним способом.",
      manualFallbackHint: "Якщо Telegram відкриється без стартового коду, скопіюйте код підключення та вставте його в чат із ботом.",
      afterLoginHint: "Якщо ви ще не ввійшли, спочатку виконайте вхід — підключення продовжиться автоматично.",
    },
    rateLimitPage: {
      eyebrow: "Shefa Green",
      title: "Трохи повільше",
      body:
        "Ми тимчасово обмежили кількість запитів із вашого браузера через незвично високу активність. Так Shefa Green залишається стабільним для всіх.",
      hint: "Зачекайте кілька хвилин, оновіть сторінку або натисніть кнопку нижче, щоб спробувати ще раз.",
      ctaRetry: "Спробувати знову",
      ctaHome: "На головну",
      tabsHint:
        "Якщо відкрито кілька вкладок Shefa Green, закрийте зайві — тоді сторінка не надсилатиме зайві фонові запити.",
      retryIn: (totalSeconds) => {
        const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
        if (s <= 0) return "Можна спробувати знову.";
        if (s >= 120) {
          const m = Math.ceil(s / 60);
          return `Спробуйте ще раз приблизно через ${m} хв.`;
        }
        return `Спробуйте ще раз приблизно через ${s} с.`;
      },
    },
    theme: {
      dark: "Темна",
      light: "Світла",
      system: "Як у системі",
      toggleAria: "Перемкнути тему: темна, світла або як у системі",
      tapNext: "натисніть для зміни",
      farmMenu: "Контекст об’єкти",
      farmHomeToken: "Домашня об’єкт (токен)",
    },
    username: "Логін",
    email: "Email",
    password: "Пароль",
    users: "Користувачі",
    addUser: "Додати користувача",
    role: "Роль",
    delete: "Видалити",
    manageCuttings: "Керування живцями",
    byName: "За назвою",
    byCode: "За кодом",
    both: "Обидва",
    manageStrains: "Керування сортами",
    manageReviews: "Проф. огляди",
    manageNews: "Керування новинами",
    addCutting: "Додати живець",
    addStrain: "Додати сорт",
    addNews: "Додати новину",
    reportBug: "Повідомити про баг",
    strainsLoadError: "Не вдалося завантажити список сортів.",
    strainsAdminCrashed: "Сталася помилка під час завантаження сторінки керування сортами.",
    pin: "Закріпити",
    unpin: "Відкріпити",
    pinnedBugs: (n) => `\uD83D\uDCCC Закріплені баги (${n})`,
    recentlyCreated: "Створено нещодавно",
    recentUpdate: "Недавнє оновлення",
    closeBug: "Закрити",
    closedBugs: (n) => `Закриті баги (${n})`,
    showClosed: "Показати закриті баги",
    hideClosed: "Сховати закриті баги",
    showOnlyMine: "Лише мої баги",
    urgentOnly: "Тільки термінові",
    sort: "Сортування",
    sortByPriority: "За пріоритетом",
    sortByDateDesc: "Нові → старі",
    sortByDateAsc: "Старі → нові",
    sortByStatus: "За статусом",
    shownOfTotal: (s, t) => `Показано ${s} з ${t} багів`,
    addTask: "Додати задачу",
    addComment: "Додати коментар",
    priority: "Пріоритет",
    priorityLabel: (value) => `Пріоритет: ${value}`,
    priorityHigher: "Вище",
    priorityLower: "Нижче",
    lastActivity: "Остання активність",
    showAllComments: "Показати всі коментарі",
    noCommentsYet: "Коментарів поки немає",
    completeTask: "Завершити",
    showCompleted: "Показати виконані",
    hideCompleted: "Сховати виконані",
    tasksDashboard: {
      subtitle: "Щоденний центр керування",
      summaryUrgent: "Термінові",
      summaryToday: "Сьогодні",
      summaryOverdue: "Прострочено",
      summarySystem: "Від системи",
      summaryCompleted: "Виконано",
      sectionToday: "Завдання на сьогодні",
      sectionProblems: "Потребують уваги",
      sectionScheduled: "Заплановано",
      sectionHistory: "Історія та виконані",
      tabToday: "Сьогодні",
      tabProblems: "Проблеми",
      tabScheduled: "Розклад",
      tabHistory: "Історія",
      quickAddTitle: "Швидке додавання",
      recommendationsTitle: "Авторекомендації",
      recommendationsHint:
        "Коли увімкнуть автоматизацію за датчиками, тут з’являться завдання за аномаліями.",
      recommendationsEmptyPrimary: "Немає активних системних рекомендацій.",
      recommendationsEmptySecondary:
        "Рекомендації з’являться тут, коли дані про клімат, полив, підключення контролерів чи обладнання вийдуть за очікуваний діапазон.",
      noTasksInView: "У цьому розділі ще немає завдань.",
      emptyToday:
        "На сьогодні завдань немає. Тут лише активні: без терміну, на сьогодні або прострочені. З датою після сьогодні — у вкладці «Розклад».",
      emptyProblems:
        "Немає проблемних завдань. Тут збираються прострочені, терміновий пріоритет і статуси «необхідно» чи «відхилено».",
      emptyScheduled:
        "Немає завдань з датою після сьогодні. Вкажіть термін пізніше поточного дня або відкрийте «Сьогодні» для поточної роботи.",
      emptyHistory: "Виконаних завдань ще немає — вони з’являться після позначки «готово».",
      completedSectionTitle: "Виконані завдання",
      postpone: "Відкласти",
      postponeModalTitle: "Відкласти завдання",
      postponeLaterToday: "Пізніше сьогодні",
      postponeTomorrowMorning: "Завтра вранці",
      postponeTomorrowEvening: "Завтра ввечері",
      postponeChooseDateTime: "Обрати дату й час",
      postponeApply: "Застосувати",
      postponeFailed: "Не вдалося оновити термін. Спробуйте ще раз.",
      postponeInvalidDate: "Вкажіть коректну дату й час.",
      openDetails: "Деталі",
      dueLabel: "Термін (дата й час)",
      addPhotos: "Фото",
      roomOrLocation: "Зона вирощування / місце",
      optionalLinksHint: "За бажанням: прив’язка до об’єкта об’єкти (якщо списки завантажились).",
      linkRoom: "Зона вирощування",
      linkDevice: "Контролер / контролер",
      linkStrain: "Сорт",
      linkCutting: "Живець",
      linkNoneOption: "—",
      cuttingLabel: "Живець",
      groupNoRoom: "Без зони вирощування / не призначено",
      groupTaskCount: (n) => {
        const abs = Math.abs(Number(n)) || 0;
        const mod10 = abs % 10;
        const mod100 = abs % 100;
        if (mod100 >= 11 && mod100 <= 14) return `${abs} завдань`;
        if (mod10 === 1) return `${abs} завдання`;
        if (mod10 >= 2 && mod10 <= 4) return `${abs} завдання`;
        return `${abs} завдань`;
      },
      filterRoom: "Зона вирощування",
      filterTaskType: "Тип завдання",
      filterPriority: "Пріоритет",
      filterSource: "Джерело",
      filterSearch: "Пошук",
      filterSearchPlaceholder: "Опис, місце, зона вирощування, контролер, сорт, живець, коментарі…",
      filterAllRooms: "Усі зони вирощування",
      filterAllTypes: "Усі типи",
      filterAllPriorities: "Усі пріоритети",
      filterAllSources: "Усі джерела",
      clearFilters: "Скинути фільтри",
      emptyFiltered: "Немає завдань за цими фільтрами. Змініть умови або скиньте фільтри.",
      sourceManual: "Вручну",
      sourceSystem: "Система",
      pageTitle: "Задачі",
      completeTask: "Завершити",
      showCompleted: "Показати виконані",
      hideCompleted: "Сховати виконані",
      addTask: "Додати задачу",
      addComment: "Додати коментар",
      fieldDescription: "Опис",
      fieldPriority: "Пріоритет",
      fieldComment: "Коментар",
      statusInProgress: "В роботі",
      statusNeeds: "Потребує уваги",
      statusDone: "Готово",
      statusRejected: "Відхилено",
      completedAtLabel: "Виконано",
      lastActivity: "Остання активність",
      priorityUrgent: "Терміново",
      systemReasonSection: "Системна причина",
      systemReasonPrimaryLabel: "Причина",
      systemTriggerRange: (min, max) => `Допустимо ${min}–${max}`,
      systemTriggerMinOnly: (min) => `від ${min}`,
      systemTriggerMaxOnly: (max) => `до ${max}`,
      systemTriggeredAt: "Спрацювало",
      systemTriggerTypeLabel: "Тип спрацювання",
      systemTriggerMetricLabel: "Метрика",
      systemTriggerValueLabel: "Значення",
      systemAllowedRangeLabel: "Допустимий діапазон",
      taskType: "Тип завдання",
      systemTask: "Системне",
      reason: "Коментар",
      noDueDate: "Без терміну",
      priorityLow: "Низький",
      priorityMedium: "Середній",
      priorityHigh: "Високий",
      taskTypes: {
        climate: "Клімат",
        watering: "Полив",
        plant_inspection: "Огляд рослин",
        equipment: "Обладнання",
        cuttings: "Живці",
        nutrition: "Живлення",
        pests_disease: "Шкідники / хвороби",
        cleaning: "Прибирання",
        other: "Інше",
      },
    },
    openedBy: "Відкрив",
    details: "Деталі",
    tasks: "Задачі",
    bugs: "Баги",
    open: "Відкрито",
    inProgress: "В роботі",
    testing: "На тестуванні",
    urgent: "Терміново",
    veryUrgent: "Дуже терміново",
    needs: "Необхідно",
    done: "Готово",
    rejected: "Відхилено",
    fixed: "Виправлено",
    closed: "Закрито",
    edit: "Редагувати",
    update: "Оновити",
    cancel: "Скасувати",
    save: "Зберегти",
    title: "Заголовок",
    name: "Назва",
    content: "Текст",
    description: "Опис",
    strain: "Сорт",
    status: "Статус",
    location: "Локація",
    code: "Код",
    history: "Історія",
    statusHistory: "Історія статусів",
    statusHistoryEmpty: "Немає змін статусу",
    statusHistoryFrom: "З",
    statusHistoryTo: "У",
    statusHistoryBy: "Автор",
    statusHistoryAt: "Час",
    link: "Посилання",
    event: "Подія",
    comment: "Коментар",
    photo: "Фото",
    screenshot: "Скріншот",
    savePhoto: "Зберегти фото",
    photoSaved: "Фото збережено",
    photoShortName: "Коротка назва",
    photoDescriptionDetailed: "Опис (детально)",
    photoIncludeInDiary: "У щоденник",
    photoDiaryOnly: "Лише щоденник",
    photoStage: "Етап",
    photoStageFilter: "Фільтр за етапом",
    photoStageAll: "Усі етапи",
    photoTakenAt: "Часова мітка (таймлайн)",
    photoPosition: "Позиція",
    photoMove: "Переміщення у таймлайні",
    moveLeft: "Вліво",
    moveRight: "Вправо",
    downloadDiary: "Завантажити щоденник",
    diaryExportTooMany: (limit) => `Експорт обмежено ${limit} фото`,
    diaryExportNoPhotos: "Немає фото, позначених для щоденника",
    photoDetails: "Картка фото",
    photoStageLabels: {
      cut: "Зріз",
      veg: "Вега",
      flowering: "Цвітіння",
      harvest: "Збір врожаю",
      drying: "Сушіння",
      trim: "Трімінг",
      curing: "Витримка",
      lab_tests: "Лаб. тести",
      issue: "Проблема",
    },
    gardenerDiary: "Щоденник садівника",
    viewReviewsForStrain: "Переглянути огляди цього сорту",
    legacyDiaryReadOnly:
      "Застарілий щоденник лише для читання. Для нових оцінок використовуйте Професійні огляди.",
    goToReviews: "Перейти до оглядів",
    addLog: "Додати запис",
    confirmDelete: "Видалити?",
    author: "Автор",
    id: "ID",
    slug: "Ідентифікатор",
    genetics: "Генетика",
    geneticsType: "Тип генетики",
    geneticsDominance: "Домінування (%)",
    shortDescription: "Короткий опис",
    thcPercent: "THC %",
    cbdPercent: "CBD %",
    floweringWeeks: "Цвітіння (тижні)",
    floweringCategory: "Категорія цвітіння",
    floweringEarly: "Раннє",
    floweringMedium: "Середнє",
    floweringLate: "Пізнє",
    yield: "Урожайність",
    yieldLow: "Низька",
    yieldMedium: "Середня",
    yieldHigh: "Висока",
    yieldVeryHigh: "Дуже висока",
    plantHeight: "Висота рослини",
    heightLow: "Низька",
    heightMedium: "Середня",
    heightHigh: "Висока",
    terpenes: "Терпени",
    aromaFlavor: "Аромат/смак",
    effectDirection: "Напрям ефекту",
    effectUp: "Вгору",
    effectNeutral: "Нейтрально",
    effectDown: "Вниз",
    effectSensations: "Відчуття",
    uniqueTrait: "Унікальна риса",
    positioning: "Позиціонування",
    positioningCommercial: "Комерційний",
    positioningBoutique: "Бутік",
    positioningExotic: "Екзотик",
    positioningNiche: "Нішевий",
    recommendedTime: "Рекомендований час",
    recommendedDay: "День",
    recommendedEvening: "Вечір",
    recommendedNight: "Ніч",
    targetAudience: "Цільова аудиторія",
    audienceBeginners: "Початківці",
    audienceExperienced: "Досвідчені",
    audienceUniversal: "Універсально",
    passportCore: "CORE",
    passportCorePlus: "CORE+",
    passportExtended: "EXTENDED",
    tagLimit: "Максимум 6 тегів (ефекти — 5).",
    imageLimit: "Максимум 5 зображень.",
    maxImages: "Досягнуто ліміту зображень.",
    extendedLabReports: "Лабораторні PDF/посилання (через кому)",
    extendedFullCannabinoidProfile: "Повний профіль канабіноїдів",
    extendedSecondaryTerpenes: "Вторинні терпени (через кому)",
    extendedCultivationNotes: "Нотатки з вирощування",
    extendedInternalCommercialNotes: "Внутрішні комерційні нотатки",
    statusRd: "R&D",
    statusTest: "Тест",
    statusPreCommercial: "Передкомерційний",
    statusCommercial: "Комерційний",
    weeks: "тиж.",
    images: "Зображення",
    publicUrl: "Публічний URL",
    transplant: "пересадка",
    treatment: "обробка",
    move: "переміщення",
    viewMore: "Докладніше",
    collapse: "Згорнути",
    duplicate: "Дублювати",
    downloadQrPdf: "Завантажити QR (PDF)",
    downloadAllQrPdf: "Завантажити всі QR (PDF)",
    downloadExcel: "Завантажити всі QR (Excel)",
    downloadExcelReport: "Завантажити Excel",
    downloadReadings: "Показники…",
    downloadSettingsChanges: "Зміни налаштувань…",
    from: "Від",
    to: "До",
    download: "Завантажити",
    downloadFailed: "Не вдалося завантажити файл",

    labelModes: {
      nameOnly: "Лише назва",
      numberOnly: "Лише номер",
      nameAndNumber: "Назва + номер",
    },
    copyQrText: "Скопіювати текст QR",
    copyLabelText: "Скопіювати текст етикетки",
    export: "Експорт",
    hide: "Сховати",
    readMore: "Читати далі",
    currentReadings: "Поточні показники",
    thresholdValues: "Порогові значення",
    day: "День",
    night: "Ніч",
    dayNight: "День/Ніч",
    preset: "Пресет",
    socketStates: "Стани розеток",
    temperature: "Температура",
    humidity: "Вологість",
    co2: "CO₂",
    vpd: "VPD",
    light: "PPFD",
    lightStatus: "Статус світла",
    moisture: "Вологість ґрунту",
    ec: "EC",
    ph: "pH",
    nitrogen: "N",
    phosphorus: "P",
    potassium: "K",
    inMedium: "У середовищі",
    inAir: "У повітрі",
    search: "Пошук",
    all: "Усі",
    climate: "Клімат",
    waterInAndOut: "Вода вхід/вихід",
    noDevicesFound: "Контролери не знайдено",
    waterDevices: "Водні контролери",
    waterTrends: "Тренди води",
    waterHistoryRange: "Діапазон історії води",
    noWaterHistory: "Історії води поки немає.",
    waterTemperature: "Температура води",
    waterLevel: "Рівень води",
    waterVolume: "Об'єм води",
    tank: "Бак",
    of: "з",
    maxHeight: "Макс. висота",
    centimetersShort: "см",
    noTankData: "Немає даних по баку",
    water: {
      inlet: "Вхід",
      outlet: "Вихід",
      outletStatus: "Вихід",
      units: {
        litersShort: "л",
        centimetersShort: "см",
      },
      hints: {
        ph_optimal: "Оптимальний діапазон",
      },
      errors: {
        ph_out_of_range: "pH поза діапазоном",
        low_level: "Низький рівень води",
        temp_high: "Увага: температура вище 28°C",
      },
    },
    time: "Час",
    liters: "Літри (л)",
    summary: "Зведення",
    autoRefresh: "Автооновлення",
    getConfig: "Отримати конфігурацію",
    pushNow: "Надіслати зараз",
    softResetConfirm: "Перезавантажити контролер?",
    apply: "Застосувати",
    config: "Конфігурація",
    calibrateIn: "Калібрувати вхід",
    calibrateOut: "Калібрувати вихід",
    inTotalDelta: "Вхід усього Δ",
    outTotalDelta: "Вихід усього Δ",
    avg: "середнє",
    displayIntervalLabel: "Інтервал дисплея",
    serverIntervalLabel: "Інтервал сервера",
    lastTs: "Остання мітка часу",
    inPpl: "Вхід ppl",
    outPpl: "Вихід ppl",
    unknown: "невідомо",
    online: "онлайн",
    date: "Дата",
    marketName: "Ринкова назва",
    internalNumber: "Внутрішній #",
    firmwareUpdate: "Оновлення прошивки",
    latest: "Остання",
    lastUploaded: "Завантажено",
    flash: "Прошити",
    photoUploading: "Завантаження фото...",
    photoTooLarge: "Перевищено розмір (макс. 3 MB)",
    unsupportedFormat: "Непідтримуваний формат",
    tooManyFiles: "Забагато файлів",
    editPhoto: "Редагувати фото",
    rotateLeft: "Повернути ліворуч",
    rotateRight: "Повернути праворуч",
    zoom: "Збільшити",
    uploadFailed: "Помилка завантаження",
    flashFailed: "Помилка прошивки",
    invalidDeviceId:
      "Невірний формат ID пристрою. Переконайтеся, що він без пробілів та має щонайменше 24 символи.",
    getFreshData: "Оновити дані",
    notifyOnOffline: "Повідомляти про вимкнення живлення",
    powerLossAlerts: "Сповіщення в Telegram про зникнення живлення",
    telegramAlertsOn: "Telegram-сповіщення: УВІМК",
    telegramAlertsOff: "Telegram-сповіщення: ВИМК",
    trendAlerts: "Тенденція сповіщень",
    climateCharts: "Графіки клімату",
    climateNoData24h: "Немає даних за останні 24 години.",
    climateVpdProOnly: "Аналіз VPD доступний у тарифах Pro та Enterprise.",
    climateExpandDetails: "Розгорнути деталі",
    climateHideDetails: "Сховати деталі",
    climateRelays: "Реле",
    climateSetpointsDay: "Пороги: День",
    climateSetpointsNight: "Пороги: Ніч",
    climateSaveThresholds: "Зберегти пороги",
    climateSaving: "Збереження...",
    climateLightStatus: "Освітлення",
    climateNightPhaseInRoom: "🌙 НІЧНА ФАЗА (У КІМНАТІ)",
    last24Hours: "останні 24 години",
    settings: "Налаштування",
    deviceSettings: "Налаштування контролера",
    deviceSettingsBrandTitle: "Shefa Green Controller — налаштування",
    deviceSettingsBrandSubtitle: (deviceId) =>
      deviceId ? `ID контролера: ${deviceId}` : "",
    backToController: "Назад до контролера",
    backToControllers: "Назад до контролерів",
    selectFunction: "Виберіть функцію",
    commandSuccess: "✅ Команду виконано успішно",
    commandError: "❌ Помилка: перевірте аргумент",
    particleAuthError:
      "Помилка авторизації. Перевірте access_token та належність пристрою вашому акаунту.",
    refreshReadings: "Оновити показники",
    refreshConfig: "Оновити конфігурацію",
    displayInterval: "Інтервал дисплея (хв)",
    serverInterval: "Інтервал сервера (хв)",
    saveConfig: "Зберегти конфігурацію",
    loadConfig: "Завантажити конфігурацію",
    factoryReset: "Заводське скидання",
    wipeEEPROM: "Очистити EEPROM",
    relayLabels: { A: "Кондиціонер", D: "Сушка", C: "CO₂", L: "Світло", reserve: "Резерв", mask: "маска" },
    intervalError: "Інтервал має бути >0 і <=1440 хвилин",
    functions: {
      setHumidityThreshold: "💧 Вологість: пороги (%)",
      setTemperatureThreshold: "🌡 Температура: пороги (°C)",
      setCO2Threshold: "CO₂: пороги (ppm)",
      toggleSocket: "🔌 Керування розетками",
      calibrateCO2ppm: "🔧 Калібрувати CO₂",
      setDisplayInterval: "🕒 Інтервал дисплея (хв)",
      setServerInterval: "📤 Інтервал сервера (хв)",
      getReadings: "📈 Отримати показники",
      softReset: "🔄 М'яке перезавантаження",
      setVPDType: "🌿 Тип VPD",
    },
    args: {
      min: "Мін",
      max: "Макс",
      ppm: "PPM",
      minutes: "Хвилини",
      type: "Тип",
      submit: "Надіслати",
    },
    enums: {
      air: "Повітря",
      leaf: "Лист",
      "leaf+": "Лист+",
      on: "Увімкнено",
      off: "Вимкнено",
      auto: "Авто",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
    },
    hints: {
      setHumidityThreshold: "приклад: 40.0,60.0",
      setTemperatureThreshold: "приклад: 18.0,25.0",
      setCO2Threshold: "приклад: 400,1000",
      calibrateCO2ppm: "приклад: 400",
      setDisplayInterval: "Введіть кількість хвилин (наприклад, 1).",
      setServerInterval: "Введіть інтервал відправки у хвилинах, мінімум 1.",
      toggleSocket: "приклад: 1,on",
      setVPDType: "air | leaf | leaf+",
    },
  },
  he: {
    language: "עברית",
    nav: {
      home: "דף הבית",
      dashboard: "לוח בקרה",
      profile: "פרופיל",
      news: "חדשות",
      bugs: "באגים",
      errors: "שגיאות",
      tasks: "משימות",
      devices: "בקרים",
      rooms: "חללי גידול",
      strains: "זנים",
      cuttings: "ייחורים",
      secondaryMenu: "עוד",
      adminMenu: "ניהול",
      about: "אודות",
      manageCuttings: "ניהול ייחורים",
      manageStrains: "ניהול זנים",
      manageReviews: "סקירות מקצועיות",
      manageNews: "ניהול חדשות",
      users: "משתמשים",
      adminPanel: "לוח ניהול",
      manageFarms: "ניהול מתקני",
      unassignedDevices: "בקרים לא משויכים",
      wifiBle: "Wi‑Fi BLE",
      farmGroup: "מתקן",
      farmsBreadcrumb: "מתקני",
      livePanel: "לוח חי",
      farm: "מתקן",
      geneticsGroup: "גנטיקה",
      engineeringPanel: "פאנל הנדסי",
      adminBreadcrumb: "ניהול",
      allControllers: "כל הבקרים",
    },
    roleSwitcher: {
      label: "תפקיד בדיקה",
      reset: "איפוס",
    },
    role: {
      admin: "מנהל",
      superuser: "סופר יוזר",
      farmOwner: "בעל מתקן",
      farmManager: "מנהל מתקן",
      worker: "עובד",
    },
    planTier: {
      included: "כלול",
      basic: "בסיסי",
      standard: "סטנדרט",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    deviceTabs: {
      overview: "סקירה",
      settings: "הגדרות",
    },
    farmsAdmin: {
      title: "ניהול מתקני",
      colName: "שם",
      colOwner: "בעלים",
      colUsers: "משתמשים",
      colDevices: "בקרים",
      colActions: "פעולות",
      create: "מתקן חדשה",
      edit: "עריכה",
      usersAction: "משתמשים",
      delete: "מחיקה",
      editTitle: "עריכת מתקן",
      ownerUnassigned: "ללא בעלים",
      saveChanges: "שמירת שינויים",
      savingChanges: "שומר…",
      confirmDeleteTitle: "מחיקת מתקן",
      confirmDelete: (name) =>
        `למחוק את המתקן «${name}»? ניתן למחוק רק מתקן ריקה (ללא משתמשים, בקרים או נתונים).`,
      modalTitle: "מתקן חדשה",
      farmNamePlaceholder: "שם המתקן",
      createSubmit: "יצירה",
      creating: "יוצר…",
      deleting: "מוחק…",
      accessDenied: "המקטע זמין למנהלים ולמשתמשי־על בלבד.",
      usersAccessDenied: "ניהול משתמשים זמין למנהלים ולמשתמשי־על בלבד.",
      moveFarmLabel: "העברה למתקן אחרת",
      apply: "החלה",
      unassigned: "לא משויך",
      freePoolOption: "-- מאגר חופשי (ניתוק) --",
      freeStatus: "סטטוס: פנוי",
      moving: "שומר…",
      farmUpdated: "המתקן של הבקר עודכנה",
      deviceFreed: "הבקר שוחרר והועבר למאגר החופשי",
      farmUpdateNoop: "ללא שינוי",
      farmUpdateFailed: "לא ניתן לעדכן מתקן",
      farmColumn: "מתקן",
      selectFarmNewUser: "מתקן",
      loading: "טוען…",
      empty: "אין מתקני",
      loadError: "טעינת המתקני נכשלה",
      createFailed: "יצירת המתקן נכשלה",
      deleteFailed: "מחיקת המתקן נכשלה",
      updateFailed: "עדכון המתקן נכשל",
      planTypeLabel: "תוכנית מנוי",
    },
    welcome: {
      title: "ברוכים הבאים ל־ShefaGreenApp",
      subtitle: "Shefa Green – ניהול חכם של מטעים ואקלים."
    },
    home: {
      hero: {
        title: "נהלו זנים ומחזורי גידול עם Shefa Green",
        subtitle: "פלטפורמה מוגנת לזנים, ריבוי, חללי גידול, אקלים, מים, ציוד, יומן מגדל, אנליטיקה ותמיכת AI עתידית בהחלטות גידול.",
        slogan: "גידול ממוקד זנים. סביבה בשליטה.",
        openDashboard: "פתח לוח בקרה",
        about: "אודות",
      },
      public: {
        eyebrow: "מרחב עבודה ממוקד זנים",
        valueEyebrow: "נבנה לתפעול מגדלים",
        valueTitle: "מערכת סביב זנים, גנטיקה ומחזורי גידול, לא רק סביב בקרים",
        valueSubtitle:
          "Shefa Green מחברת ריבוי, חללי גידול, פריסטים, יומן, פעולות בקרים, משימות ואנליטיקה לשכבת תפעול אחת למגדלים מקצועיים.",
        title: "Shefa Green עוזרת למגדלים לנהל את כל תהליך הגידול סביב הזנים.",
        body:
          "Shefa Green מאחדת זנים, מחזורי גידול, חללי גידול, ייחורים, אקלים, מים, ציוד, יומן מגדל, אנליטיקה ושכבת AI עתידית לתמיכה בהחלטות בתוך מרחב עבודה מוגן אחד. ניתן לעקוב אחרי כל זן דרך ריבוי, מעבר בין חללי גידול, שלבי צמיחה, תצפיות, טיפולים והיסטוריית גידול, בזמן ש-ShefaGreen Controllers תומכים בסביבה שהמגדלים יוצרים סביב הצמח.",
        notice:
          "נתונים תפעוליים זמינים רק לאחר התחברות. מבקרים ציבוריים אינם רואים שמות בקרים, מזהי בקרים, חללי גידול, מתקני, ערכי חיישנים, משימות, רשומות זנים, ייחורים, יומן גידול או תוצאות אנליטיקה.",
        statsAria: "יתרונות מרכזיים של פלטפורמת Shefa Green",
        stats: {
          strainCentered: {
            value: "100%",
            label: "ממוקד זנים",
          },
          aiReady: {
            value: "AI-ready",
            label: "מפת דרכים לתמיכת החלטות",
          },
          protected: {
            value: "Secure",
            label: "מרחב עבודה מוגן",
          },
        },
        preview: {
          activeStrain: "זן פעיל",
          flowering: "פריחה",
          cycleProgress: "התקדמות מחזור הגידול",
        },
        controllerPill: "ShefaGreen Controllers",
        controllersTitle: "ניטור ושליטה בסביבת הגידול",
        controllersBody:
          "ShefaGreen Controllers הם ציוד גידול שיכול גם לנטר וגם לשלוט באקלים, אוורור, קירור, חימום, השקיה, איכות מים, זרימת מים וסטטוס ציוד מתוך מרחב העבודה המוגן.",
        ctaBadge: "פלטפורמה מקצועית מוגנת",
        ctaTitle: "מרחב עבודה מוגן אחד לניהול גידול",
        ctaBody:
          "הובילו כל זן מהגנטיקה ועד הקציר בזמן שהצוות, הבקרים, היומן, האנליטיקה ועוזר ה-AI העתידי נשארים מחוברים במקום אחד.",
        ctaButton: "כניסה ל-Shefa Green",
        pricing: {
          eyebrow: "תוכניות מנוי",
          title: "תוכניות מנוי למתקני",
          subtitle:
            "ההבדל העיקרי הוא עומק שמירה וצפייה בהיסטוריית נתוני אקלים בענן, וגישה לניתוח מורחב של לחץ אדים (VPD).",
          commonFeatures:
            "בכל הרמות זמין אותו מרחב עבודה — זנים, חללי גידול, פריסטים, יומן, בקרים, משימות, כלי אקלים ומים — וההגבלה היא בעומק הטלמטריה כמפורט להלן.",
          retentionNote:
            "משך שמירה של טלמטריית אקלים גולמית נקבע לפי התוכנית; מעבר לחלון נקודות הופכות למיושנות והגרפים לא חורגים ממסגרת ההיסטוריה.",
          footer:
            "הקצאת התוכנית מתבצעת על ידי מנהלי Shefa Green. לשדרוג או שינוי רמת המתקן פנו לתמיכה.",
          plans: {
            included: {
              tagline: "רמת ברירת מחדל אחרי חיבור",
              bullets: [
                "יום אחד של היסטוריית טלמטריית אקלים לגרפים ולסקירה.",
                "מצבי ניתוח ממוקדי VPD כבויים; מדדי אקלים בסיסיים זמינים.",
              ],
            },
            basic: {
              tagline: "חלון הפעלה קצר",
              bullets: ["3 ימים של היסטוריית טלמטריית אקלים.", "ניתוח VPD מורחב לא כלול."],
            },
            standard: {
              tagline: "חלון שבועי לדוחות",
              bullets: ["7 ימים של היסטוריית טלמטריית אקלים.", "ניתוח VPD מורחב לא כלול."],
            },
            pro: {
              tagline: "חודש היסטוריה ו‑VPD",
              bullets: [
                "30 ימים של היסטוריית טלמטריית אקלים.",
                "ניתוח VPD מורחב מופעל (מוצמד להגדרת התכונה ברמת המתקן במערכת).",
              ],
            },
            enterprise: {
              tagline: "שמירה ארוכת טווח ו‑VPD",
              bullets: ["365 ימים של היסטוריית טלמטריית אקלים.", "ניתוח VPD מורחב מופעל."],
            },
          },
        },
        capabilities: {
          temperature: "טמפרטורה",
          humidity: "לחות",
          co2: "CO2",
          ventilation: "אוורור",
          cooling: "קירור",
          heating: "חימום",
          irrigation: "השקיה",
          waterFlow: "זרימת מים",
          ec: "EC",
          ph: "pH",
          equipmentStatus: "סטטוס ציוד",
        },
        modules: {
          strains: {
            title: "זנים",
            description:
              "הפכו את הגנטיקה למרכז המערכת: עקבו אחרי כל זן דרך ריבוי, מעבר חללי גידול, שלבי צמיחה, תצפיות, טיפולים והיסטוריית גידול.",
          },
          growthPresets: {
            title: "פריסטים לשלבי גידול",
            description:
              "תכננו פריסטים ניתנים להגדרה לשלבי גידול שונים כדי להתאים חללי גידול, יעדים ושגרות למחזור החיים של הצמח.",
          },
          growerDiary: {
            title: "יומן מגדל",
            description:
              "תעדו אירועי גידול, הערות, תצפיות, טיפולים, שינויי שלב, היסטוריית חללי גידול והחלטות של המגדל בהקשר הנכון.",
          },
          analytics: {
            title: "אנליטיקה",
            description:
              "השוו זנים, חללי גידול, שלבי צמיחה, תנאי אקלים, תנאי מים, מחזורי גידול ותוצאות תפעוליות לאורך זמן.",
          },
          controllers: {
            title: "ShefaGreen Controllers",
            description:
              "השתמשו ב-ShefaGreen Controllers כציוד גידול שיכול לנטר ולשלוט בסביבת הגידול מתוך מרחב העבודה המוגן.",
          },
          climateAutomation: {
            title: "אוטומציית אקלים",
            description:
              "תמיכה בטמפרטורה, לחות, CO2, אוורור, קירור, חימום ויציאות אקלים מוגדרות מתוך אותו מרחב גידול.",
          },
          waterControl: {
            title: "בקרת מים",
            description:
              "עבודה עם השקיה, זרימת מים, EC, pH, ORP, מפלס מים וטמפרטורת מים כחלק ממחזור הגידול.",
          },
          cuttings: {
            title: "ייחורים",
            description:
              "נהלו ריבוי וייחורים כחלק ממחזור החיים המלא של הזן — מהגנטיקה המקורית ועד שיוך לחלל גידול.",
          },
          tasks: {
            title: "ציוד ומשימות",
            description:
              "תאמו סטטוס ציוד, תחזוקה, מעקב תפעולי, פריטי עבודה, בעיות ואחריות צוות עם הרשאות מבוססות תפקיד.",
          },
          aiRoadmap: {
            title: "אנליטיקה ועוזר AI עתידי",
            description:
              "מתוכננת שכבת בינה עתידית שתבין היסטוריית גידול, תזהה דפוסים, תנתח תוצאות ותציע פעולות אפשריות על בסיס הנתונים שנאספו.",
          },
        },
      },
      ticker: {
        ariaLabel: "פס מצב חי של הבקרים",
        online: (n) => `מקוונים: ${n}`,
        offline: (n) => `מנותקים: ${n}`,
        lastSeen: (time) => `נראה לאחרונה ${time}`,
        empty: "אין נתוני בקרים",
      },
      strains: {
        title: "הזנים של היום",
        viewAll: "כל הזנים",
        empty: "אין זנים להצגה",
        updated: (date) => `עודכן ${date}`,
      },
      news: {
        title: "חדשות",
        viewAll: "כל החדשות",
      },
      tasks: {
        title: "משימות",
        viewAll: "כל המשימות",
        empty: "אין משימות פתוחות",
        superuserOnly: "משימות זמינות רק לסופר־יוזר.",
      },
      quickActions: {
        title: "פעולות מהירות",
        qr: "QR",
      },
      footer: {
        telegram: "בוט הטלגרם",
      },
    },
    dashboard: {
      title: "סקירה חיה",
      lastUpdated: (time) => `עודכן ${time}`,
      autoRefresh: {
        label: "רענון אוטומטי",
        enabled: (seconds) => `מתעדכן כל ${seconds} שניות`,
        disabled: "הרענון האוטומטי מושהה",
      },
      refreshNow: "רענן עכשיו",
      errors: {
        farmContextRequired: "בחרו מתקן או התחברו שוב לטעינת התצוגה.",
      },
      summary: {
        title: "תקציר מערכת",
        totalDevices: "סה״כ בקרים",
        online: "בקרים מחוברים",
        offline: "בקרים מנותקים",
        lastData: "קריאה אחרונה",
        totalStrains: "סך הזנים",
        noData: "אין נתונים",
      },
      climate: {
        title: "אקלים במתקן (ממוצע)",
        temperature: "טמפרטורה ממוצעת",
        humidity: "לחות ממוצעת",
        co2: "CO₂ ממוצע",
        noData: "—",
      },
      alerts: {
        title: "התראות אחרונות",
        viewAll: "כל הפניות",
        empty: "אין אירועים קריטיים בחלון האחרון.",
        deviceOffline: "בקר מנותק",
      },
      recentDevices: {
        title: "בקרים אחרונים",
        viewAll: "כל הבקרים",
        empty: "אין פעילות בקרים אחרונה",
        lastSeen: (time) => `נראה לאחרונה ${time}`,
      },
      status: {
        online: "מחובר",
        offline: "מנותק",
      },
      strains: {
        title: "זנים אחרונים",
        viewAll: "כל הזנים",
        empty: "אין עדכוני זנים",
      },
      news: {
        title: "חדשות אחרונות",
        viewAll: "כל החדשות",
        empty: "אין חדשות עדיין",
      },
      tasks: {
        title: "משימות פתוחות",
        viewAll: "כל המשימות",
        empty: "אין משימות פתוחות",
        superuserOnly: "המשימות זמינות רק לסופר-משתמשים.",
      },
      rooms: {
        title: "חללי גידול",
        heading: "מרחבים ואקלים",
        subtitle: "אזורים בתוך המתקן. בקרים ויומן המגדל משויכים לחלל גידול הנבחר.",
        addRoom: "הוסף חלל גידול",
        create: "צור חלל גידול",
        namePlaceholder: "שם (לדוגמה: וגטציה)",
        typePlaceholder: "סוג (אופציונלי)",
        empty: "עדיין אין חללי גידול",
        loading: "טוען…",
        open: "פתח",
        delete: "מחק",
        confirmDeleteRoom:
          "למחוק את החלל גידול הזה? הבקרים יישארו במתקן אך ללא שיוך לחלל גידול.",
      },
      planBadge: (label) => `תוכנית: ${label}`,
    },
    roomsPage: {
      breadcrumbInRoom: "חלל גידול",
      back: "חזרה ללוח החי",
      backToRooms: "חזרה לרשימת החללי גידול",
      backDashboard: "חזרה ללוח החי",
      diaryRoomHint: "כל רשומה נשמרת לחלל גידול זה וכוללת צילום מצב של חיישני החלל גידול.",
      devices: "בקרים בחלל גידול",
      noDevices: "עדיין אין בקרים בחלל גידול הזה. יש לשייך חלל גידול בהגדרות הבקר.",
      diaryTitle: "יומן מגדל",
      diaryNote: "הערה",
      diaryAdd: "הוסף רשומה",
      diaryEmpty: "עדיין אין רשומות",
      diarySensors: "מדדי חיישנים (אוטומטי)",
      sensorsNone: "אין שורות חיישנים",
      loadError: "טעינת החלל גידול נכשלה",
      claimInvalidContext: "לא ניתן לזהות מזהה בקר או הקשר חלל גידול.",
      claimFailed: "שיוך הבקר נכשל.",
      claimSuccess: "החיבור הוקם! הבקר החיה את החלל גידול.",
      notConnectedTitle: "החלל גידול לא מחובר",
      notConnectedDescription:
        "הוסף Shefa Green Controller חדש לחלל גידול הזה כדי להתחיל טלמטריה ובקרה.",
      connectArgon: "חבר Shefa Green Controller",
      connectModalTitle: "חיבור Shefa Green Controller",
      step1: "שלב 1 מתוך 3",
      step2: "שלב 2 מתוך 3",
      step3: "שלב 3 מתוך 3",
      prepareDeviceTitle: "הכנת הבקר",
      prepareDeviceDescription:
        "חבר את Shefa Green Controller לחשמל. ודא שהוא מהבהב בכחול - זה מצב מוכן לשיוך.",
      searchingHint: "Shefa Green Controller מחפש את החלל גידול שלו...",
      identifyDeviceTitle: "זיהוי בקר",
      identifyDeviceDescription: "סרוק קוד QR של הבקר או הזן deviceId ידנית.",
      scanQr: "סרוק QR",
      deviceIdLabel: "Controller ID",
      deviceIdPlaceholder: "לדוגמה 34003f001447343339383037",
      connectDevice: "חבר",
      claimingInProgress: "מאתרים את Shefa Green Controller ומקימים חיבור...",
      updatingRoomData: "מעדכנים נתוני חלל גידול...",
      next: "המשך",
      back: "חזרה",
      close: "סגור",
      statusOnline: "Online",
      statusOffline: "Offline",
      lightDay: "יום תאורה",
      lightNight: "לילה",
      lightAlertLampOff: "אזהרה: ייתכן שהמנורה כבויה?",
      summaryTotalRooms: "סך החללי גידול",
      summaryActiveControllers: "בקרים פעילים",
      summaryProblems: "בעיות",
      modeGrowing: "גדלים",
      modeSleeping: "ישנים",
      modeNeedsSetup: "נדרשת הגדרה",
      setupRequired: "נדרשת הגדרה: חבר בקר כדי להפעיל ניטור.",
      kpiTemperature: "טמפרטורה",
      kpiHumidity: "לחות",
      kpiConnection: "חיבור",
      kpiWifiRssi: "Wi-Fi (RSSI)",
      actionSettings: "הגדרות",
      actionCharts: "גרפים",
      actionBlink: "הבהב",
      refreshingStatuses: "מרעננים סטטוס חללי גידול...",
      breadcrumbOverview: "סקירה",
      kpiCo2: "CO₂",
      kpiDevicesOnline: "בקרים מקוונים",
      growerDiarySummaryTitle: "סיכום יומי",
      growerDiarySummaryHint:
        "תקופות «יום» ו«לילה» נקבעות לפי מצב התאורה בטלמטריה (day_status), לא לפי השעה.",
      growerDiaryTablePlaceholder:
        "כאן תופיע טבלת מינימום/מקסימום לתקופות אור דולק וכבוי.",
      tableDate: "תאריך",
      tableMetric: "מדד",
      tablePeriodDay: "יום (תאורה דלוקה)",
      tablePeriodNight: "לילה (תאורה כבויה)",
      tableMin: "מינ׳",
      tableMax: "מקס׳",
      diaryAnalyticsTitle: "יומן ואנליטיקה",
      analyticsLoading: "טוען סיכום…",
      analyticsError: "טעינת הסיכום נכשלה",
      analyticsTruncated: "דגימה קוצצה",
      analyticsTruncatedBanner: (cap) =>
        `רק ${cap} המדידות האחרונות בחלון נכללו; נקודות ישנות יותר הושמטו. צמצמו את החלון או את מספר הבקרים לכיסוי מלא.`,
      analyticsSamples: "מדידות בחישוב",
      colPeriod: "תקופה",
      colTempMm: "טמפ׳ מינ׳–מקס׳",
      colRhMm: "לחות מינ׳–מקס׳",
      colCo2Avg: "CO₂ ממוצע",
      colVpdAvg: "VPD ממוצע",
      colDailyNote: "הערת יום",
      dailyNoteSave: "שמור",
      dailyNoteSaved: "נשמר",
      periodDayShort: "יום",
      periodNightShort: "לילה",
    },
    invitePage: {
      title: "הצטרפות למתקן כעובד",
      invalidLink: "קישור ההזמנה אינו תקין.",
      missingToken: "טוקן ההזמנה חסר.",
      registrationFailed: "ההרשמה נכשלה.",
      emailPlaceholder: "Email",
      passwordPlaceholder: "סיסמה",
      creatingAccount: "יוצר חשבון...",
      createAccount: "צור חשבון",
    },
    unassignedPage: {
      title: "בקרים לא משויכים",
      subtitle: "שייכו מתקן לכל בקר.",
      colDevice: "בקר",
      colFarm: "מתקן",
      apply: "שייך מתקן",
      loading: "טוען…",
      empty: "אין בקרים לא משויכים",
      loadError: "הטעינה נכשלה",
      saveError: "שמירת שיוך המתקן נכשלה",
      saved: "שיוך המתקן עודכן",
    },
    adminControllers: {
      title: "כל הבקרים",
      subtitle: "כל הבקרים בכל המתקני. חיפוש ומסננים חלים בעמוד זה.",
      searchLabel: "חיפוש",
      searchPlaceholder: "מזהה בקר, שם, מתקן או חלל גידול…",
      statusFilter: "סטטוס",
      statusAll: "הכל",
      statusOnline: "מקוון",
      statusOffline: "לא מקוון",
      displayFilter: "סוג תצוגה",
      displayAll: "הכל",
      colControllerId: "מזהה בקר",
      colDeviceName: "שם",
      colFarm: "מתקן",
      colRoom: "חלל גידול",
      colStatus: "סטטוס",
      colHardware: "חומרה",
      colLastSeen: "נראה לאחרונה",
      colActions: "פעולות",
      badgeOnline: "מקוון",
      badgeOffline: "לא מקוון",
      viewLogs: "לוגים",
      reboot: "אתחול",
      rebootConfirm: "לבצע איתחול רך (soft reset) לבקר הזה?",
      rebootFailed: "האיתחול נכשל",
      unbind: "נתק",
      unbindTitle: "נתק ממתקן וחלל גידול",
      unbindModalTitle: "נתק בקר",
      unbindModalBody: "לנתק את הבקר {{id}} ממתקן \"{{farm}}\"? הבקר יעבור לרשימת הבקרים הלא משויכים.",
      unbindConfirm: "נתק",
      unbindCancel: "ביטול",
      unbindFailed: "הניתוק נכשל",
      assign: "קישור לחלל גידול",
      assignTitle: "שיוך בקר למתקן ולחלל גידול",
      assignModalTitle: "שיוך למתקן ולחלל גידול",
      assignModalHint: "בחרו מתקן וחלל גידול. זה מעדכן את farmId ו-roomId ברשומת הבקר.",
      assignFarmLabel: "מתקן",
      assignFarmPlaceholder: "בחרו מתקן…",
      assignRoomLabel: "חלל גידול",
      assignRoomPlaceholder: "בחרו חלל גידול…",
      assignRoomLoading: "טוען חללי גידול…",
      assignSubmit: "שייך",
      assignCancel: "ביטול",
      assignFailed: "השיוך נכשל",
      assignNeedFarmRoom: "נדרשת מתקן וחלל גידול",
      controllerTypeWater: "בקר מים",
      controllerTypeClimate: "בקר אקלים",
      controllerTypeGeneric: "בקר",
      empty: "אין התאמות למסננים",
      loading: "טוען…",
      loadError: "טעינת הרשימה נכשלה",
    },
    deviceAssignment: {
      roomLabel: "חלל גידול",
      roomNone: "ללא חלל גידול",
      roomSave: "שמור חלל גידול",
      releaseDevice: "שחרר בקר",
      releaseConfirm:
        "להסיר שיוך למתקן ולחלל גידול? הבקר יהיה זמין לשיוך למתקן אחרת (מרשם סופר-יוזר).",
      releaseDone: "הבקר שוחרר",
      releaseError: "שחרור נכשל",
      releaseHint: "סופר-יוזר בלבד — מאפס מתקן וחלל גידול.",
      unbindFarm: "נתק מהמתקן",
      unbindConfirm: "לנתק את הבקר מהמתקן? הוא יופיע כלא משויך עד לשיוך מחדש.",
      unbindDone: "הבקר נותק",
      unbindError: "הניתוק נכשל",
      superuserOnly: "סופר-יוזר בלבד",
    },
    loading: "טוען...",
    noNews: "אין חדשות",
    updated: "עודכן",
    refresh: "רענן",
    toggleNavigation: "פתח או סגור ניווט",
    retry: "נסה שוב",
    showOffline: (n) => `הצג בקרים לא מקוונים (${n})`,
    hideOffline: "הסתר בקרים לא מקוונים",
    noData: "אין נתונים",
    no_data: "אין נתונים",
    statusOn: "ON",
    statusOff: "OFF",
    co2CheckTank: "בדקו את הבלון",
    firmwareUpload: "טעינת קושחה",
    upload: "העלה",
    cuttings: "ייחורים",
    news: "חדשות",
    latestReadings: "קריאות אחרונות",
    loginFailed: "ההתחברות נכשלה",
    copyToClipboard: "העתקה ללוח:",
    saveFailed: "השמירה נכשלה",
    settingsSavedSuccess: "ההגדרות נשמרו בהצלחה",
    yes: "כן",
    no: "לא",
    timeSyncTitle: "סנכרון זמן",
    refreshTimeSync: "רענון",
    timeSyncHint: "השתמשו ב-NTP כמקור זמן ראשי. הבקר צריך לחשב ב-UTC ולהחיל אזור זמן רק ללוגיקת יום/לילה.",
    serverTimeLabel: "זמן שרת",
    controllerTimeLabel: "זמן בקר",
    timeDriftLabel: "פער זמן",
    timeUnknown: "לא ידוע",
    farmTimezoneLabel: "אזור זמן של המתקן",
    farmTimezoneHint: "שמרו אזור זמן בפורמט IANA כמו Asia/Jerusalem. הערך מוחזר מ-/api/system/time ומוחל בבקר ללוגיקת יום/לילה.",
    saveTimezone: "שמור אזור זמן",
    co2ManagementTitle: "ניהול CO2",
    co2OffsetLabel: "ערך כיול CO2 (ppm)",
    co2OffsetHint: "תיקון קריאה מול חיישן ייחוס VDL.",
    calibrationTargetPpmPlaceholder: "סטנדרט: 400. ים המלח: 580",
    syncNow: "סנכרן עכשיו",
    forceCalibrateButton: "התחל כיול",
    forceCalibrateHint: "להפעיל רק אחרי 5 דקות אוורור.",
    calibrationStatus: {
      pending: "⏳ ממתין לסנכרון עם הבקר...",
      started: (left) => `✅ הכיול הופעל (5 דק') ${left}`,
    },
    manualTimeLabel: "זמן ידני",
    setManualTime: "קבע זמן",
    manualTimeHint: "השתמשו רק ברשתות מבודדות ללא NTP. הפקודה שולחת Unix-time ב-UTC יחד עם אזור הזמן של המתקן.",
    waterPage: {
      idLabel: "מזהה",
      firmwareLabel: "קושחה",
      range2h: "שעתיים",
      range24h: "24 שעות",
      range7d: "7 ימים",
      rangeCustom: "טווח מותאם",
      latestTitle: "נתונים אחרונים",
      noHistory: "עדיין אין היסטוריית מים.",
      inletLine: "כניסה",
      outletLine: "יציאה",
      litersPerMinuteShort: "ל׳/דק׳",
      litersShort: "ל׳",
      successOk: "אישור",
      genericError: "שגיאה",
    },
    deviceSettingsPage: {
      calibrationVentilationConfirm: "ודאו שהחלל גידול אוורר לפחות 5 דקות לפני האישור.",
      timezonePlaceholder: "Asia/Jerusalem",
      co2OffsetPlaceholder: "0",
      currentValue: "נוכחי",
      co2OffsetSaveHint:
        "קיזוז ה-CO₂ למעלה נשמר יחד עם אזור הזמן של המתקן בלחיצה על «שמור אזור זמן».",
      advancedFunctionsTitle: "פקודות Particle מתקדמות",
      vpdProPlanLock:
        "ניתוח VPD זמין בתוכניות Pro ו-Enterprise. שדרגו את תוכנית המתקן כדי לשנות את סוג ה-VPD.",
      locked: "נעול",
    },
    deviceSettingsModal: {
      actionsTitle: "פעולות בקר",
      actionsHint: "פקודות שירות ידניות לבקר שנבחר.",
      restartButton: "הפעל מחדש את הבקר",
      restartConfirmTitle: "אשרו הפעלה מחדש של הבקר",
      restartWarning: "הבקר עשוי להיות לא זמין למשך כ-30-90 שניות בזמן ההפעלה מחדש. ממסרים, ספים, מצבים והגדרות EEPROM לא ישתנו.",
      restartConfirmButton: "כן, הפעל מחדש",
      restartSending: "שולח פקודה",
      restartAccepted: "פקודת ההפעלה מחדש התקבלה. הבקר מופעל מחדש ועשוי להיות לא מקוון 30-90 שניות.",
      restartFailed: "הפעלת הבקר מחדש נכשלה.",
      restartNotReachable: "ההפעלה מחדש נכשלה כי הבקר אינו זמין.",
      modeAutoBadge: "A",
      modeManualOnBadge: "M:ON",
      modeManualOffBadge: "M:OFF",
      relayModeAria: (label) => `מצב שקע ${label}`,
      modeAuto: "אוטומטי",
      modeManualOn: "הפעלה (ידני)",
      modeManualOff: "כיבוי (ידני)",
      deviceNameTitle: "שם בקר",
      deviceNamePlaceholder: "שם בקר",
      telegramAlerts: "התראות Telegram",
      powerLoss: "נפילת מתח",
      thresholdAlerts: "התראות ספים",
      trendAlerts: "התראות מגמה",
      climateDeliveryOn: "התראות אקלים ניתנות לשליחה",
      climateDeliveryBlocked: "התראות אקלים לא יישלחו",
      climateDeliveryOff: "התראות אקלים כבויות",
      climateDeliveryCounts: (enabled, total) => `מקבלי Climate alerts: ${enabled}/${total}`,
      climateDeliveryNoRecipients:
        "התראות הבקר פעילות, אבל אין נמעני Telegram מקושרים להתראות אקלים.",
      climateDeliveryClimateDisabled:
        "התראות הבקר פעילות, אבל כל נמעני Telegram הזכאים כיבו את קטגוריית Climate alerts.",
      climateDeliveryTelegramSettingsHint:
        "פתחו את הגדרות ההתראות בבוט Telegram והפעילו את קטגוריית Climate alerts.",
      enableClimateAlertsForMe: "הפעלת Climate alerts עבורי",
      enablingClimateAlerts: "מפעיל Climate alerts",
      climateAlertsEnabledForMe: "Climate alerts הופעלו לחשבון Telegram שלך.",
      climateAlertsEnableFailed: "הפעלת Climate alerts נכשלה.",
      co2Management: "ניהול CO2",
      co2Offset: "ערך כיול CO2 (ppm)",
      startCalibration: "התחל כיול",
      calibrationTargetPpmLabel: "ערך כיול (PPM)",
      calibrationTargetPpmPlaceholder: "סטנדרט: 400. ים המלח: 580",
      calibrationHint: "להפעיל רק אחרי 5 דקות אוורור",
      calibrationConfirm: "בטוחים? להפעיל רק אחרי 5 דקות אוורור!",
      relayManagement: "ניהול שקעים",
      waterMetric: { ph: "pH", ec: "EC", orp: "ORP", temp: "טמפרטורה", level: "מפלס מים", volume: "נפח" },
    },
    deviceCard: {
      unknownDeviceName: "בקר אקלים",
      idLabel: "ID",
      idDialogTitle: "מזהה הבקר",
      idPreviewAria: "הצג את מזהה הבקר המלא והעתק אותו",
      copyId: "העתק ID",
      copied: "הועתק",
      connectivityOnline: "מקוון",
      connectivityNoRecentData: "אין נתונים עדכניים",
      connectivityOffline: "לא מקוון",
      controllerOfflineCommands: "הבקר לא מחובר. פקודות לא מתבצעות.",
      syncTimeFailed: "סנכרון הזמן נכשל",
      calibrationFailed: "הכיול נכשל",
      climateParamsUpdated: "פרמטרי האקלים עודכנו",
      relayCommandTimeout: "שגיאה: הבקר לא אישר ביצוע פקודה (timeout)",
      relayCommandUnconfirmed:
        "המצב נשמר, אך הבקר לא שלח אישור. רעננו את הדף אם המצב לא תואם לבקר.",
      modeLabel: "מצב",
      errorGeneric: "שגיאה",
      invalidThreshold: "סף לא תקין: הזינו Min ו-Max מספריים וודאו ש-Min <= Max.",
    },
    settingsApplyFailedPrefix: "ההחלה נכשלה",
    settingsProgressSavingDb: "שומרים הגדרות במסד הנתונים...",
    settingsProgressApplyingController: "מחילים ספים על הבקר",
    settingsProgressControllerCheck: "הספים נשמרו במסד הנתונים, בודקים את הבקר...",
    settingsProgressSavingSystem: "שומרים פרמטרי מערכת...",
    settingsProgressSyncingView: "מסנכרנים תצוגה...",
    co2OffsetNotSavedWarning: "CO2 Offset לא נשמר (נקודת timezone לא זמינה)",
    noCuttings: "אין ייחורים",
    noStrains: "לא נמצאו זנים",
    noBugs: "אין באגים להצגה",
    login: "התחברות",
    logout: "התנתקות",
    profile: {
      title: "פרופיל",
      subtitle: "פרטי חשבון וקישור לטלגרם.",
      farm: "מתקן",
      linkTelegram: "חיבור Telegram",
      telegramLinked: "Telegram מחובר",
      telegramNotLinked: "Telegram לא מחובר",
      openTelegramBot: "פתיחת הבוט ב-Telegram",
      disconnectTelegram: "ניתוק Telegram",
      disconnectingTelegram: "מנתקים את Telegram…",
      disconnectError: "לא ניתן לנתק את Telegram. נסו שוב מאוחר יותר.",
      linkError: "לא ניתן לקבל קישור. נסו שוב מאוחר יותר.",
      openingBot: "פותחים את הבוט…",
      refreshStatus: "רענון סטטוס",
      telegramHint: "חברו את Telegram כדי לקבל התראות. לאחר החיבור אפשר לפתוח את הבוט או לנתק את Telegram מכאן.",
    },
    telegramConnect: {
      eyebrow: "Telegram",
      title: "חיבור חשבון Telegram",
      description:
        "חברו את חשבון Telegram כדי לגשת למתקנים, חללי גידול, התראות וכלי בקרים.",
      button: "חיבור Telegram",
      connecting: "מחברים את Telegram...",
      linkError: "לא ניתן ליצור קישור Telegram. נסו שוב מאוחר יותר.",
      signInAgain: "משהו השתבש. התחברו שוב.",
      rateLimit: "המתינו כמה שניות ונסו שוב.",
      retryButton: "נסו להתחבר שוב",
      openTelegramButton: "פתיחת Telegram וסיום החיבור",
      copyConnectCodeButton: "העתקת קוד חיבור",
      copyConnectCodeDone: "קוד החיבור הועתק",
      copyError: "לא ניתן להעתיק את הקוד. העתיקו אותו ידנית.",
      widgetLoading: "טוענים את כפתור ההתחברות של Telegram...",
      widgetLoadError: "לא ניתן לטעון את כפתור ההתחברות של Telegram. רעננו את העמוד או השתמשו בחלופה.",
      manualFallbackHint: "אם Telegram נפתח בלי קוד ההפעלה, העתיקו את קוד החיבור והדביקו אותו בצ'אט עם הבוט.",
      afterLoginHint: "אם עדיין לא התחברתם, התחברו תחילה — החיבור ימשיך אוטומטית לאחר ההתחברות.",
    },
    rateLimitPage: {
      eyebrow: "Shefa Green",
      title: "רגע לאט",
      body:
        "הגבלנו זמנית את מספר הבקשות מהדפדפן שלכם כי זוהתה פעילות גבוהה מהרגיל. זה עוזר לשמור על Shefa Green יציב לכולם.",
      hint: "המתינו מספר דקות, רעננו את הדף או השתמשו בכפתור למטה כדי לנסות שוב.",
      ctaRetry: "נסו שוב",
      ctaHome: "חזרה לדף הבית",
      tabsHint:
        "אם יש כמה לשוניות של Shefa Green פתוחות, סגרו מיותרות — כך הדף לא ימשיך לשלוח בקשות רקע מיותרות.",
      retryIn: (totalSeconds) => {
        const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
        if (s <= 0) return "אפשר לנסות שוב עכשיו.";
        if (s >= 120) {
          const m = Math.ceil(s / 60);
          return `אפשר לנסות שוב בעוד כ-${m} דקות.`;
        }
        return `אפשר לנסות שוב בעוד כ-${s} שניות.`;
      },
    },
    theme: {
      dark: "כהה",
      light: "בהיר",
      system: "לפי המערכת",
      toggleAria: "החלפת ערכת נושא: כהה, בהיר או לפי המערכת",
      tapNext: "לחצו להחלפה",
      farmMenu: "הקשר מתקן",
      farmHomeToken: "מתקן בית (אסימון)",
    },
    username: "שם משתמש",
    email: "אימייל",
    password: "סיסמה",
    users: "משתמשים",
    addUser: "הוסף משתמש",
    role: "תפקיד",
    delete: "מחיקה",
    manageCuttings: "ניהול ייחורים",
    byName: "הצג לפי שם",
    byCode: "הצג לפי קוד",
    both: "הכל",
    manageStrains: "ניהול זנים",
    manageReviews: "סקירות מקצועיות",
    manageNews: "ניהול חדשות",
    addCutting: "הוסף ייחור",
    addStrain: "הוסף זן",
    addNews: "הוסף חדשות",
    reportBug: "דווח על באג",
    strainsLoadError: "לא ניתן לטעון את רשימת הזנים.",
    strainsAdminCrashed: "אירעה שגיאה בעת טעינת ניהול הזנים.",
    pin: "נעל",
    unpin: "הסר נעיצה",
    pinnedBugs: (n) => `\uD83D\uDCCC באגים נעוצים (${n})`,
    recentlyCreated: "נוצר לאחרונה",
    recentUpdate: "עודכן לאחרונה",
    closeBug: "סגור",
    closedBugs: (n) => `באגים סגורים (${n})`,
    showClosed: "הצג באגים סגורים",
    hideClosed: "הסתר באגים סגורים",
    showOnlyMine: "רק הבאגים שלי",
    urgentOnly: "רק דחופים",
    sort: "מיון",
    sortByPriority: "לפי עדיפות",
    sortByDateDesc: "מהחדש לישן",
    sortByDateAsc: "מהישן לחדש",
    sortByStatus: "לפי סטטוס",
    shownOfTotal: (s, t) => `מוצגים ${s} מתוך ${t} באגים`,
    addTask: "הוסף משימה",
    addComment: "הוסף תגובה",
    priority: "עדיפות",
    priorityLabel: (value) => `עדיפות: ${value}`,
    priorityHigher: "הגבר עדיפות",
    priorityLower: "הפחת עדיפות",
    lastActivity: "פעילות אחרונה",
    showAllComments: "הצג את כל התגובות",
    noCommentsYet: "אין תגובות עדיין",
    completeTask: "סיום",
    showCompleted: "הצג משימות שהושלמו",
    hideCompleted: "הסתר משימות שהושלמו",
    tasksDashboard: {
      subtitle: "מוקד בקרה יומי",
      summaryUrgent: "דחופות",
      summaryToday: "היום",
      summaryOverdue: "באיחור",
      summarySystem: "מהמערכת",
      summaryCompleted: "הושלמו",
      sectionToday: "משימות היום",
      sectionProblems: "דורשות טיפול",
      sectionScheduled: "מתוזמנות",
      sectionHistory: "היסטוריה והושלמו",
      tabToday: "היום",
      tabProblems: "בעיות",
      tabScheduled: "לוח זמנים",
      tabHistory: "היסטוריה",
      quickAddTitle: "הוספה מהירה",
      recommendationsTitle: "המלצות אוטומטיות",
      recommendationsHint: "כשיהיה מופעל אוטומציה מחיישנים, משימות לפי חריגות יופיעו כאן.",
      recommendationsEmptyPrimary: "אין המלצות מערכת פעילות.",
      recommendationsEmptySecondary:
        "המלצות יופיעו כאן כשנתוני אקלים, השקיה, חיבור בקר או ציוד יחרגו מהטווח הצפוי.",
      noTasksInView: "אין עדיין משימות בתצוגה זו.",
      emptyToday:
        "אין משימות להיום. כאן מוצגות משימות פעילות בלי מועד, עם מועד להיום (יום בלוח השנה), או באיחור. משימות עם מועד אחרי היום נמצאות בלשונית «מתוזמנות».",
      emptyProblems:
        "אין משימות בעייתיות. הלשונית כוללת משימות באיחור, בעדיפות דחופה, או בסטטוס «חסר» / «נדחה».",
      emptyScheduled:
        "אין משימות מתוזמנות אחרי היום. הגדירו מועד אחרי היום הנוכחי, או עברו ל«היום» לעבודה הנוכחית.",
      emptyHistory: "אין עדיין משימות שהושלמו — הן יופיעו כאן אחרי סימון כהושלם.",
      completedSectionTitle: "משימות שהושלמו",
      postpone: "דחייה",
      postponeModalTitle: "דחיית משימה",
      postponeLaterToday: "מאוחר יותר היום",
      postponeTomorrowMorning: "מחר בבוקר",
      postponeTomorrowEvening: "מחר בערב",
      postponeChooseDateTime: "בחירת תאריך ושעה",
      postponeApply: "החל",
      postponeFailed: "לא ניתן לעדכן את מועד היעד. נסה שוב.",
      postponeInvalidDate: "הזן תאריך ושעה תקינים.",
      openDetails: "פרטים",
      dueLabel: "תאריך / שעת יעד",
      addPhotos: "תמונות",
      roomOrLocation: "חלל גידול / מיקום",
      optionalLinksHint: "אופציונלי: קישור לישות במתקן (כשהרשימות נטענות).",
      linkRoom: "חלל גידול",
      linkDevice: "בקר / בקר",
      linkStrain: "זן",
      linkCutting: "ייחור",
      linkNoneOption: "—",
      cuttingLabel: "ייחור",
      groupNoRoom: "ללא חלל גידול / לא משויך",
      groupTaskCount: (n) => (Number(n) === 1 ? 'משימה אחת' : `${n} משימות`),
      filterRoom: "חלל גידול",
      filterTaskType: "סוג משימה",
      filterPriority: "עדיפות",
      filterSource: "מקור",
      filterSearch: "חיפוש",
      filterSearchPlaceholder: "תיאור, מיקום, חלל גידול, בקר, זן, ייחור, הערות…",
      filterAllRooms: "כל החללי גידול",
      filterAllTypes: "כל סוגי המשימות",
      filterAllPriorities: "כל העדיפויות",
      filterAllSources: "כל המקורות",
      clearFilters: "נקה מסננים",
      emptyFiltered: "אין משימות שמתאימות למסננים. שנה או נקה מסננים.",
      sourceManual: "ידני",
      sourceSystem: "מערכת",
      pageTitle: "משימות",
      completeTask: "סיום",
      showCompleted: "הצג משימות שהושלמו",
      hideCompleted: "הסתר משימות שהושלמו",
      addTask: "הוסף משימה",
      addComment: "הוסף תגובה",
      fieldDescription: "תיאור",
      fieldPriority: "עדיפות",
      fieldComment: "תגובה",
      statusInProgress: "בתהליך",
      statusNeeds: "חסר",
      statusDone: "הושלם",
      statusRejected: "נדחה",
      completedAtLabel: "הושלם",
      lastActivity: "פעילות אחרונה",
      priorityUrgent: "דחוף",
      systemReasonSection: "סיבת מערכת",
      systemReasonPrimaryLabel: "סיבה",
      systemTriggerRange: (min, max) => `מותר ${min}–${max}`,
      systemTriggerMinOnly: (min) => `מ-${min}`,
      systemTriggerMaxOnly: (max) => `עד ${max}`,
      systemTriggeredAt: "הופעל ב",
      systemTriggerTypeLabel: "סוג ההפעלה",
      systemTriggerMetricLabel: "מדד",
      systemTriggerValueLabel: "ערך",
      systemAllowedRangeLabel: "טווח מותר",
      taskType: "סוג משימה",
      systemTask: "מערכת",
      reason: "הערה",
      noDueDate: "ללא מועד",
      priorityLow: "נמוך",
      priorityMedium: "בינוני",
      priorityHigh: "גבוה",
      taskTypes: {
        climate: "אקלים",
        watering: "השקיה",
        plant_inspection: "בדיקת צמחים",
        equipment: "ציוד",
        cuttings: "ייחורים",
        nutrition: "תזונה",
        pests_disease: "מזיקים / מחלות",
        cleaning: "ניקיון",
        other: "אחר",
      },
    },
    openedBy: "נפתח על ידי",
    details: "פרטים",
    tasks: "משימות",
    bugs: "באגים",
    open: "פתוח",
    inProgress: "בתהליך",
    testing: "בבדיקה",
    urgent: "דחוף",
    veryUrgent: "דחוף מאוד",
    needs: "חסר",
    done: "הושלם",
    rejected: "נדחה",
    fixed: "תוקן",
    closed: "סגור",
    edit: "ערוך",
    update: "עדכן",
    cancel: "בטל",
    save: "שמור",
    title: "כותרת",
    name: "שם",
    content: "תוכן",
    description: "תיאור",
    strain: "זן",
    status: "סטטוס",
    location: "מיקום",
    code: "קוד",
    history: "היסטוריה",
    statusHistory: "היסטוריית סטטוסים",
    statusHistoryEmpty: "אין שינויים בסטטוס",
    statusHistoryFrom: "מ",
    statusHistoryTo: "אל",
    statusHistoryBy: "על ידי",
    statusHistoryAt: "בתאריך",
    link: "קישור",
    event: "אירוע",
    comment: "הערה",
    photo: "תמונה",
    screenshot: "צילום מסך",
    savePhoto: "שמור תמונה",
    photoSaved: "התמונה נשמרה",
    photoShortName: "שם קצר",
    photoDescriptionDetailed: "תיאור מפורט",
    photoIncludeInDiary: "כלול ביומן",
    photoDiaryOnly: "יומן בלבד",
    photoStage: "שלב",
    photoStageFilter: "סינון לפי שלב",
    photoStageAll: "כל השלבים",
    photoTakenAt: "חותמת זמן (ציר זמן)",
    photoPosition: "מיקום ברצף",
    photoMove: "שינוי מיקום בציר הזמן",
    moveLeft: "הזזה שמאלה",
    moveRight: "הזזה ימינה",
    downloadDiary: "הורדת יומן",
    diaryExportTooMany: (limit) => `הייצוא מוגבל ל-${limit} תמונות`,
    diaryExportNoPhotos: "אין תמונות מסומנות ליומן",
    photoDetails: "פרטי תמונה",
    photoStageLabels: {
      cut: "חיתוך",
      veg: "צמיחה",
      flowering: "פריחה",
      harvest: "קציר",
      drying: "ייבוש",
      trim: "גיזום",
      curing: "יישון",
      lab_tests: "בדיקות מעבדה",
      issue: "בעיה",
    },
    gardenerDiary: "יומן גנן",
    viewReviewsForStrain: "צפה בביקורות לזן זה",
    legacyDiaryReadOnly:
      "יומן מדור קודם הוא לקריאה בלבד. לצורך הערכות חדשות השתמשו במערכת הביקורות המקצועיות.",
    goToReviews: "לעבור לביקורות",
    addLog: "הוסף רישום",
    confirmDelete: "למחוק?",
    author: "כותב",
    id: "מזהה",
    slug: "סלאג",
    genetics: "גנטיקה",
    geneticsType: "סוג גנטיקה",
    geneticsDominance: "דומיננטיות (%)",
    shortDescription: "תיאור קצר",
    thcPercent: "THC %",
    cbdPercent: "CBD %",
    floweringWeeks: "פריחה (שבועות)",
    floweringCategory: "קטגוריית פריחה",
    floweringEarly: "מוקדם",
    floweringMedium: "בינוני",
    floweringLate: "מאוחר",
    yield: "תנובה",
    yieldLow: "נמוכה",
    yieldMedium: "בינונית",
    yieldHigh: "גבוהה",
    yieldVeryHigh: "גבוהה מאוד",
    plantHeight: "גובה הצמח",
    heightLow: "נמוך",
    heightMedium: "בינוני",
    heightHigh: "גבוה",
    terpenes: "טרפנים",
    aromaFlavor: "ארומה/טעם",
    effectDirection: "כיוון ההשפעה",
    effectUp: "למעלה",
    effectNeutral: "ניטרלי",
    effectDown: "למטה",
    effectSensations: "תחושות",
    uniqueTrait: "תכונה ייחודית",
    positioning: "מיצוב",
    positioningCommercial: "מסחרי",
    positioningBoutique: "בוטיק",
    positioningExotic: "אקזוטי",
    positioningNiche: "נישתי",
    recommendedTime: "זמן מומלץ",
    recommendedDay: "יום",
    recommendedEvening: "ערב",
    recommendedNight: "לילה",
    targetAudience: "קהל יעד",
    audienceBeginners: "מתחילים",
    audienceExperienced: "מנוסים",
    audienceUniversal: "אוניברסלי",
    passportCore: "CORE",
    passportCorePlus: "CORE+",
    passportExtended: "EXTENDED",
    tagLimit: "מקסימום 6 תגיות (אפקטים — 5).",
    imageLimit: "מקסימום 5 תמונות.",
    maxImages: "הגעת למגבלת התמונות.",
    extendedLabReports: "דוחות מעבדה/קישורים (מופרדים בפסיקים)",
    extendedFullCannabinoidProfile: "פרופיל קנבינואידים מלא",
    extendedSecondaryTerpenes: "טרפנים משניים (מופרדים בפסיקים)",
    extendedCultivationNotes: "הערות גידול",
    extendedInternalCommercialNotes: "הערות מסחריות פנימיות",
    statusRd: "מו\"פ",
    statusTest: "בדיקה",
    statusPreCommercial: "טרום-מסחרי",
    statusCommercial: "מסחרי",
    weeks: "שבועות",
    images: "תמונות",
    publicUrl: "קישור ציבורי",
    transplant: "העתקה",
    treatment: "טיפול",
    move: "העברה",
    viewMore: "פרטים",
    collapse: "סגור",
    duplicate: "שכפל",
    downloadQrPdf: "הורד QR (PDF)",
    downloadAllQrPdf: "הורד את כל ה-QR (PDF)",
    downloadExcel: "הורד את כל ה-QR (Excel)",
    downloadExcelReport: "הורדת Excel",
    downloadReadings: "מדידות…",
    downloadSettingsChanges: "שינויים בהגדרות…",
    from: "מאת",
    to: "עד",
    download: "הורד",
    downloadFailed: "ההורדה נכשלה",

    labelModes: {
      nameOnly: "שם בלבד",
      numberOnly: "מספר בלבד",
      nameAndNumber: "שם + מספר",
    },
    copyQrText: "העתק טקסט QR",
    copyLabelText: "העתק טקסט תווית",
    export: "יצוא",
    hide: "הסתר",
    readMore: "קרא עוד",
    currentReadings: "נתונים נוכחיים",
    thresholdValues: "ערכי סף",
    day: "יום",
    night: "לילה",
    dayNight: "יום/לילה",
    preset: "תצורה",
    socketStates: "מצב שקעים",
    temperature: "טמפרטורה",
    humidity: "לחות",
    co2: "CO₂",
    vpd: "VPD",
    light: "PPFD",
    lightStatus: "מצב תאורה",
    moisture: "לחות קרקע",
    ec: "EC",
    ph: "pH",
    nitrogen: "N",
    phosphorus: "P",
    potassium: "K",
    inMedium: "במצע",
    inAir: "באוויר",
    search: "חיפוש",
    all: "הכול",
    climate: "אקלים",
    waterInAndOut: "מים כניסה/יציאה",
    noDevicesFound: "לא נמצאו בקרים",
    waterDevices: "בקרי מים",
    waterTrends: "מגמות מים",
    waterHistoryRange: "טווח היסטוריית מים",
    noWaterHistory: "עדיין אין היסטוריית מים.",
    waterTemperature: "טמפרטורת מים",
    waterLevel: "מפלס מים",
    waterVolume: "נפח מים",
    tank: "מיכל",
    of: "מתוך",
    maxHeight: "גובה מרבי",
    centimetersShort: "ס״מ",
    noTankData: "אין נתוני מיכל",
    water: {
      inlet: "כניסה",
      outlet: "יציאה",
      outletStatus: "יציאה",
      units: {
        litersShort: "L",
        centimetersShort: "ס״מ",
      },
      hints: {
        ph_optimal: "טווח אופטימלי",
      },
      errors: {
        ph_out_of_range: "pH מחוץ לטווח",
        low_level: "מפלס מים נמוך",
        temp_high: "אזהרה: מעל 28°C",
      },
    },
    time: "זמן",
    liters: "ליטרים (L)",
    summary: "סיכום",
    autoRefresh: "רענון אוטומטי",
    getConfig: "קבל תצורה",
    pushNow: "שלח עכשיו",
    softResetConfirm: "לאתחל את הבקר?",
    apply: "החל",
    config: "תצורה",
    calibrateIn: "כיול כניסה",
    calibrateOut: "כיול יציאה",
    inTotalDelta: "כניסה סה\"כ Δ",
    outTotalDelta: "יציאה סה\"כ Δ",
    avg: "ממוצע",
    displayIntervalLabel: "מרווח תצוגה",
    serverIntervalLabel: "מרווח שרת",
    lastTs: "חותמת זמן אחרונה",
    inPpl: "כניסה ppl",
    outPpl: "יציאה ppl",
    unknown: "לא ידוע",
    online: "מחובר",
    date: "תאריך",
    marketName: "שם מסחרי",
    internalNumber: "מספר פנימי",
    firmwareUpdate: "עדכון קושחה",
    latest: "אחרון",
    lastUploaded: "הועלה לאחרונה",
    flash: "צריבה",
    photoUploading: "מעלה תמונה...",
    photoTooLarge: "הקובץ גדול מדי (עד 3 MB)",
    unsupportedFormat: "פורמט לא נתמך",
    tooManyFiles: "יותר מדי קבצים",
    editPhoto: "ערוך תמונה",
    rotateLeft: "סובב שמאלה",
    rotateRight: "סובב ימינה",
    zoom: "זום",
    uploadFailed: "ההעלאה נכשלה",
    flashFailed: "הצריבה נכשלה",
    invalidDeviceId:
      "פורמט מזהה בקר שגוי. ודא שאין רווחים והוא באורך של לפחות 24 תווים.",
    getFreshData: "רענן נתונים",
    notifyOnOffline: "התראה על הפסקת חשמל",
    powerLossAlerts: "התראות טלגרם על הפסקת חשמל",
    telegramAlertsOn: "התראות Telegram: פועל",
    telegramAlertsOff: "התראות Telegram: כבוי",
    trendAlerts: "התראות מגמה",
    climateCharts: "גרפי אקלים",
    climateNoData24h: "אין נתונים עבור 24 השעות האחרונות.",
    climateVpdProOnly: "ניתוח VPD זמין בתוכניות Pro ו-Enterprise.",
    climateExpandDetails: "הרחב פרטים",
    climateHideDetails: "הסתר פרטים",
    climateRelays: "ממסרים",
    climateSetpointsDay: "ספי יום",
    climateSetpointsNight: "ספי לילה",
    climateSaveThresholds: "שמור ספים",
    climateSaving: "שומר...",
    climateLightStatus: "סטטוס תאורה",
    climateNightPhaseInRoom: "🌙 שלב לילה (בחלל גידול)",
    last24Hours: "24 השעות האחרונות",
    settings: "הגדרות",
    deviceSettings: "הגדרות בקר",
    deviceSettingsBrandTitle: "Shefa Green Controller — הגדרות",
    deviceSettingsBrandSubtitle: (deviceId) =>
      deviceId ? `מזהה בקר: ${deviceId}` : "",
    backToController: "חזרה לבקר",
    backToControllers: "חזרה לרשימת הבקרים",
    selectFunction: "בחר פונקציה",
    commandSuccess: "✅ בוצע בהצלחה",
    commandError: "❌ שגיאה: בדוק את הפרמטר",
    particleAuthError:
      "שגיאת הרשאה. בדוק את ה-access_token ושייך את הבקר לחשבונך.",
    refreshReadings: "רענן קריאות",
    refreshConfig: "רענן תצורה",
    displayInterval: "מרווח תצוגה (דקות)",
    serverInterval: "מרווח שרת (דקות)",
    saveConfig: "שמור תצורה",
    loadConfig: "טען תצורה",
    factoryReset: "איפוס להגדרות יצרן",
    wipeEEPROM: "נקה EEPROM",
    relayLabels: { A: "מזגן", D: "מייבש", C: "CO₂", L: "תאורה", reserve: "רזרבה", mask: "מסכה" },
    intervalError: "המרווח חייב להיות >0 ו-<=1440 דקות",
    functions: {
      setHumidityThreshold: "💧 גבולות לחות",
      setTemperatureThreshold: "🌡 גבולות טמפ׳",
      setCO2Threshold: "גבולות CO₂",
      toggleSocket: "שקעים",
      calibrateCO2ppm: "🔧 כיול CO₂",
      setDisplayInterval: "🕒 זמן מסך (דקות)",
      setServerInterval: "📤 שליחת נתונים (דקות)",
      getReadings: "📈 רענון נתונים",
      softReset: "🔄 אתחול",
      setVPDType: "🌿 סוג VPD",
    },
    args: {
      min: "מינ",
      max: "מקס",
      ppm: "PPM",
      minutes: "דקות",
      type: "סוג",
      submit: "שלח",
    },
    enums: {
      air: "אוויר",
      leaf: "עלה",
      "leaf+": "עלה+",
      on: "דולק",
      off: "כבוי",
      auto: "אוטומטי",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
    },
    hints: {
      setHumidityThreshold: "דוגמה: 40.0,60.0",
      setTemperatureThreshold: "דוגמה: 18.0,25.0",
      setCO2Threshold: "דוגמה: 400,1000",
      calibrateCO2ppm: "דוגמה: 400",
      setDisplayInterval: "הכנס מספר בדקות (לדוגמה, 1).",
      setServerInterval: "הכנס מרווח שליחה בדקות, מינימום 1.",
      toggleSocket: "דוגמה: 1,on",
      setVPDType: "air | leaf | leaf+",
    },
  },
  es: {
    language: "Español",
    nav: {
      home: "Inicio",
      dashboard: "Panel",
      profile: "Perfil",
      news: "Noticias",
      bugs: "Bugs",
      errors: "Errores",
      tasks: "Tareas",
      devices: "Controladores",
      rooms: "Espacios de cultivo",
      strains: "Variedades",
      cuttings: "Esquejes",
      secondaryMenu: "Más",
      adminMenu: "Admin",
      about: "Acerca de",
      manageCuttings: "Gestionar esquejes",
      manageStrains: "Gestionar variedades",
      manageReviews: "Resenas profesionales",
      manageNews: "Gestionar noticias",
      users: "Usuarios",
      adminPanel: "Panel de administración",
      manageFarms: "Gestionar instalaciones",
      unassignedDevices: "Controladores sin asignar",
      wifiBle: "Wi‑Fi BLE",
      farmGroup: "Instalación",
      farmsBreadcrumb: "Instalaciones",
      livePanel: "Panel en vivo",
      farm: "Instalación",
      geneticsGroup: "Genética",
      engineeringPanel: "Panel de ingeniería",
      adminBreadcrumb: "Admin",
      allControllers: "Todos los controladores",
    },
    roleSwitcher: {
      label: "Rol de prueba",
      reset: "Restablecer",
    },
    role: {
      admin: "Administrador",
      superuser: "Superusuario",
      farmOwner: "Propietario de instalación",
      farmManager: "Gerente de instalación",
      worker: "Trabajador",
    },
    planTier: {
      included: "Incluido",
      basic: "Básico",
      standard: "Estándar",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    deviceTabs: {
      overview: "Resumen",
      settings: "Ajustes",
    },
    farmsAdmin: {
      title: "Gestión de instalaciones",
      colName: "Nombre",
      colOwner: "Propietario",
      colUsers: "Usuarios",
      colDevices: "Controladores",
      colActions: "Acciones",
      create: "Crear instalación",
      edit: "Editar",
      usersAction: "Usuarios",
      delete: "Eliminar",
      editTitle: "Editar instalación",
      ownerUnassigned: "Sin propietario",
      saveChanges: "Guardar cambios",
      savingChanges: "Guardando…",
      confirmDeleteTitle: "Eliminar instalación",
      confirmDelete: (name) =>
        `¿Eliminar la instalación «${name}»? Solo se puede si está vacía (sin usuarios, controladores ni datos).`,
      modalTitle: "Nueva instalación",
      farmNamePlaceholder: "Nombre de la instalación",
      createSubmit: "Crear",
      creating: "Creando…",
      deleting: "Eliminando…",
      accessDenied: "Esta sección es solo para administradores y superusuarios.",
      usersAccessDenied: "La gestión de usuarios solo está disponible para administradores y superusuarios.",
      moveFarmLabel: "Mover a otra instalación",
      apply: "Aplicar",
      unassigned: "Sin asignar",
      freePoolOption: "-- Pool libre (Desvincular) --",
      freeStatus: "Estado: Libre",
      moving: "Guardando…",
      farmUpdated: "Instalación del controlador actualizada",
      deviceFreed: "Controlador liberado y movido al pool libre",
      farmUpdateNoop: "Sin cambios",
      farmUpdateFailed: "No se pudo actualizar la instalación",
      farmColumn: "Instalación",
      selectFarmNewUser: "Instalación",
      loading: "Cargando…",
      empty: "No hay instalaciones",
      loadError: "Error al cargar instalaciones",
      createFailed: "Error al crear la instalación",
      deleteFailed: "Error al eliminar la instalación",
      updateFailed: "Error al actualizar la instalación",
      planTypeLabel: "Plan de suscripción",
    },
    welcome: {
      title: "BIENVENIDO A SHEFAGREENAPP",
      subtitle: "Shefa Green – gestión inteligente de plantaciones y clima."
    },
    home: {
      hero: {
        title: "Gestiona variedades y ciclos de cultivo con Shefa Green",
        subtitle: "Una plataforma protegida para variedades, propagación, espacios de cultivo, clima, agua, equipos, diario del grower, analítica y futuras decisiones de cultivo asistidas por AI.",
        slogan: "Cultivo centrado en variedades. Ambientes bajo control.",
        openDashboard: "Abrir panel",
        about: "Acerca de",
      },
      public: {
        eyebrow: "Espacio de cultivo centrado en variedades",
        valueEyebrow: "Diseñado para operaciones de growers",
        valueTitle: "Creado alrededor de variedades, genética y ciclos de cultivo, no solo controladores",
        valueSubtitle:
          "Shefa Green conecta propagación, espacios de cultivo, presets, diario, acciones de controladores, tareas y analítica en una capa operativa para growers profesionales.",
        title: "Shefa Green ayuda a los growers a gestionar todo el proceso de cultivo alrededor de las variedades.",
        body:
          "Shefa Green reúne variedades, ciclos de cultivo, espacios de cultivo, esquejes, clima, agua, equipos, diario del grower, analítica y una futura capa de asistencia con AI en un espacio protegido. Cada variedad puede seguirse por propagación, movimiento entre espacios de cultivo, etapas de crecimiento, observaciones, tratamientos e historial de cultivo, mientras ShefaGreen Controllers apoyan el ambiente que los growers crean alrededor de la planta.",
        notice:
          "Los datos operativos están disponibles solo después de iniciar sesión. Los visitantes públicos no ven nombres de controladores, IDs de controladores, espacios de cultivo, instalaciones, valores de sensores, tareas, registros de variedades, esquejes, diario de cultivo ni resultados analíticos.",
        statsAria: "Puntos destacados de la plataforma Shefa Green",
        stats: {
          strainCentered: {
            value: "100%",
            label: "Centrada en variedades",
          },
          aiReady: {
            value: "AI-ready",
            label: "Roadmap de soporte a decisiones",
          },
          protected: {
            value: "Secure",
            label: "Espacio protegido",
          },
        },
        preview: {
          activeStrain: "Variedad activa",
          flowering: "Floración",
          cycleProgress: "Progreso del ciclo de cultivo",
        },
        controllerPill: "ShefaGreen Controllers",
        controllersTitle: "Monitorea y controla el ambiente de cultivo",
        controllersBody:
          "ShefaGreen Controllers son equipos de cultivo para monitoreo y control: clima, ventilación, refrigeración, calefacción, riego, calidad del agua, flujo de agua y estado de equipos pueden formar parte del espacio protegido.",
        ctaBadge: "Plataforma profesional protegida",
        ctaTitle: "Un espacio protegido para gestionar el cultivo",
        ctaBody:
          "Guía cada variedad desde la genética hasta la cosecha mientras el equipo, los controladores, el diario, la analítica y el futuro asistente AI permanecen conectados en un solo lugar.",
        ctaButton: "Entrar a Shefa Green",
        pricing: {
          eyebrow: "Planes de suscripción",
          title: "Planes para instalaciones",
          subtitle:
            "Lo que cambia entre planes es la profundidad de retención y consulta del historial de telemetría climática en la nube, y el acceso al análisis ampliado de déficit de presión de vapor (VPD).",
          commonFeatures:
            "En todos los niveles tienes los mismos módulos — variedades, espacios de cultivo, presets, diario, controladores, tareas, clima y agua — y los límites aplican solo a la profundidad de telemetría.",
          retentionNote:
            "La ventana de retención de muestras crudas depende del plan; fuera de ese horizonte las muestras rotan y los gráficos no pueden ir más atrás.",
          footer:
            "Los planes los asignan administradores de Shefa Green. Para cambiar el plan de tu instalación, contacta con soporte.",
          plans: {
            included: {
              tagline: "Nivel predeterminado al conectar",
              bullets: [
                "1 día de historial de telemetría climática para gráficos y revisión.",
                "Los modos de análisis centrados en VPD están desactivados; siguen disponibles métricas climáticas básicas.",
              ],
            },
            basic: {
              tagline: "Ventana operativa corta",
              bullets: ["3 días de historial de telemetría climática.", "El análisis VPD ampliado no está incluido."],
            },
            standard: {
              tagline: "Ventana semanal para informes",
              bullets: ["7 días de historial de telemetría climática.", "El análisis VPD ampliado no está incluido."],
            },
            pro: {
              tagline: "Un mes de historial más VPD",
              bullets: [
                "30 días de historial de telemetría climática.",
                "Análisis VPD ampliado habilitado (según la configuración de la instalación).",
              ],
            },
            enterprise: {
              tagline: "Retención larga más VPD",
              bullets: ["365 días de historial de telemetría climática.", "Análisis VPD ampliado habilitado."],
            },
          },
        },
        capabilities: {
          temperature: "Temperatura",
          humidity: "Humedad",
          co2: "CO2",
          ventilation: "Ventilación",
          cooling: "Refrigeración",
          heating: "Calefacción",
          irrigation: "Riego",
          waterFlow: "Flujo de agua",
          ec: "EC",
          ph: "pH",
          equipmentStatus: "Estado de equipos",
        },
        modules: {
          strains: {
            title: "Variedades",
            description:
              "Haz que la genética sea el centro del sistema: sigue cada variedad por propagación, movimiento de espacios de cultivo, etapas de crecimiento, observaciones, tratamientos e historial de cultivo.",
          },
          growthPresets: {
            title: "Presets de crecimiento",
            description:
              "Planifica presets configurables para distintas etapas de cultivo, alineando espacios de cultivo, objetivos y rutinas con el ciclo de vida de la planta.",
          },
          growerDiary: {
            title: "Diario del grower",
            description:
              "Registra eventos de cultivo, notas, observaciones, tratamientos, cambios de etapa, historial de espacios de cultivo y decisiones del grower en contexto.",
          },
          analytics: {
            title: "Analítica",
            description:
              "Compara variedades, espacios de cultivo, etapas de crecimiento, condiciones climáticas, agua, ciclos de cultivo y resultados operativos a lo largo del tiempo.",
          },
          controllers: {
            title: "ShefaGreen Controllers",
            description:
              "Usa ShefaGreen Controllers como equipos de cultivo capaces de monitorear y controlar el ambiente de crecimiento dentro del espacio protegido.",
          },
          climateAutomation: {
            title: "Automatización climática",
            description:
              "Soporta temperatura, humedad, CO2, ventilación, refrigeración, calefacción y salidas climáticas configuradas desde el mismo espacio de cultivo.",
          },
          waterControl: {
            title: "Control de agua",
            description:
              "Trabaja con riego, flujo de agua, EC, pH, ORP, nivel de agua y temperatura del agua como parte del ciclo de crecimiento.",
          },
          cuttings: {
            title: "Esquejes",
            description:
              "Gestiona propagación y esquejes como parte del ciclo completo de la variedad, desde la genética de origen hasta la asignación a espacio de cultivo.",
          },
          tasks: {
            title: "Equipos y tareas",
            description:
              "Coordina estado de equipos, mantenimiento, seguimiento operativo, trabajos, incidencias y responsabilidades del equipo con acceso basado en roles.",
          },
          aiRoadmap: {
            title: "Analítica y futuro asistente AI",
            description:
              "Se planifica una futura capa inteligente para entender el historial de cultivo, detectar patrones, analizar resultados y sugerir posibles acciones según los datos recopilados.",
          },
        },
      },
      ticker: {
        ariaLabel: "Marcador en vivo del estado de los controladores",
        online: (n) => `En línea: ${n}`,
        offline: (n) => `Fuera de línea: ${n}`,
        lastSeen: (time) => `última vez ${time}`,
        empty: "Sin actividad de controladores",
      },
      strains: {
        title: "Variedades de hoy",
        viewAll: "Todas las variedades",
        empty: "No hay variedades disponibles",
        updated: (date) => `Actualizado ${date}`,
      },
      news: {
        title: "Noticias",
        viewAll: "Todas las noticias",
      },
      tasks: {
        title: "Tareas",
        viewAll: "Todas las tareas",
        empty: "No hay tareas abiertas",
        superuserOnly: "Las tareas solo están visibles para superusuarios.",
      },
      quickActions: {
        title: "Accesos rápidos",
        qr: "QR",
      },
      footer: {
        telegram: "Bot de Telegram",
      },
    },
    dashboard: {
      title: "Vista en vivo",
      lastUpdated: (time) => `Última actualización ${time}`,
      autoRefresh: {
        label: "Actualización automática",
        enabled: (seconds) => `Se actualiza cada ${seconds} s`,
        disabled: "Actualización automática en pausa",
      },
      refreshNow: "Actualizar ahora",
      errors: {
        farmContextRequired: "Elija una instalación o vuelva a iniciar sesión para cargar esta vista.",
      },
      summary: {
        title: "Resumen del sistema",
        totalDevices: "Controladores totales",
        online: "Controladores en línea",
        offline: "Controladores fuera de línea",
        lastData: "Última lectura",
        totalStrains: "Total de variedades",
        noData: "Sin datos aún",
      },
      climate: {
        title: "Clima de la instalación (promedio)",
        temperature: "Temperatura media",
        humidity: "Humedad media",
        co2: "CO₂ media",
        noData: "—",
      },
      alerts: {
        title: "Problemas y alertas recientes",
        viewAll: "Todas las incidencias",
        empty: "Sin sucesos críticos en el último periodo.",
        deviceOffline: "Controlador fuera de línea",
      },
      recentDevices: {
        title: "Controladores recientes",
        viewAll: "Todos los controladores",
        empty: "Sin actividad reciente de controladores",
        lastSeen: (time) => `Visto por última vez ${time}`,
      },
      status: {
        online: "En línea",
        offline: "Fuera de línea",
      },
      strains: {
        title: "Últimas variedades",
        viewAll: "Todas las variedades",
        empty: "Sin actualizaciones de variedades",
      },
      news: {
        title: "Últimas noticias",
        viewAll: "Todas las noticias",
        empty: "Sin noticias publicadas",
      },
      tasks: {
        title: "Tareas abiertas",
        viewAll: "Todas las tareas",
        empty: "Sin tareas abiertas",
        superuserOnly: "Las tareas solo están visibles para superusuarios.",
      },
      rooms: {
        title: "Espacios de cultivo",
        heading: "Espacios y clima",
        subtitle:
          "Zonas dentro de la instalación. Los controladores y el diario del cultivador se vinculan al espacio de cultivo seleccionado.",
        addRoom: "Añadir espacio de cultivo",
        create: "Crear espacio de cultivo",
        namePlaceholder: "Nombre (por ejemplo, Vegetativo)",
        typePlaceholder: "Tipo (opcional)",
        empty: "Aún no hay espacios de cultivo",
        loading: "Cargando…",
        open: "Abrir",
        delete: "Eliminar",
        confirmDeleteRoom:
          "¿Eliminar este espacio de cultivo? Los controladores seguirán en la instalación, pero sin un espacio de cultivo asignado.",
      },
      planBadge: (label) => `Plan: ${label}`,
    },
    roomsPage: {
      breadcrumbInRoom: "Espacio de cultivo",
      back: "Volver al panel en vivo",
      backToRooms: "Volver a la lista de espacios de cultivo",
      backDashboard: "Volver al panel en vivo",
      diaryRoomHint:
        "Cada registro se guarda para este espacio de cultivo e incluye una instantánea de los sensores del entorno.",
      devices: "Controladores en el espacio de cultivo",
      noDevices:
        "Aún no hay controladores en este espacio de cultivo. Asigna el espacio de cultivo en la configuración del controlador.",
      diaryTitle: "Diario del cultivador",
      diaryNote: "Nota",
      diaryAdd: "Añadir registro",
      diaryEmpty: "Aún no hay registros",
      diarySensors: "Lecturas de sensores (auto)",
      sensorsNone: "No hay filas de sensores",
      loadError: "No se pudo cargar la espacio de cultivo",
      claimInvalidContext: "No se pudo detectar el ID del controlador o el contexto de la espacio de cultivo.",
      claimFailed: "No se pudo vincular el controlador.",
      claimSuccess: "Conexion establecida. El controlador activo la espacio de cultivo.",
      notConnectedTitle: "La espacio de cultivo no esta conectada",
      notConnectedDescription:
        "Agrega un nuevo Shefa Green Controller a esta espacio de cultivo para iniciar telemetria y control.",
      connectArgon: "Conectar Shefa Green Controller",
      connectModalTitle: "Conexion de Shefa Green Controller",
      step1: "Paso 1 de 3",
      step2: "Paso 2 de 3",
      step3: "Paso 3 de 3",
      prepareDeviceTitle: "Preparar controlador",
      prepareDeviceDescription:
        "Enchufa Shefa Green Controller a la corriente. Asegurate de que parpadea en azul, eso indica que esta listo para emparejar.",
      searchingHint: "Shefa Green Controller esta buscando su espacio de cultivo...",
      identifyDeviceTitle: "Identificacion del controlador",
      identifyDeviceDescription: "Escanea el codigo QR del controlador o introduce el deviceId manualmente.",
      scanQr: "Escanear QR",
      deviceIdLabel: "Controller ID",
      deviceIdPlaceholder: "p. ej. 34003f001447343339383037",
      connectDevice: "Conectar",
      claimingInProgress: "Buscando Shefa Green Controller y estableciendo conexion...",
      updatingRoomData: "Actualizando datos de la espacio de cultivo...",
      next: "Continuar",
      back: "Atras",
      close: "Cerrar",
      statusOnline: "Online",
      statusOffline: "Offline",
      lightDay: "Periodo de luz",
      lightNight: "Noche",
      lightAlertLampOff: "Atencion: la lampara puede estar apagada?",
      summaryTotalRooms: "Total de espacios de cultivo",
      summaryActiveControllers: "Controladores activos",
      summaryProblems: "Problemas",
      modeGrowing: "Creciendo",
      modeSleeping: "Durmiendo",
      modeNeedsSetup: "Requiere configuracion",
      setupRequired: "Se requiere configuracion: conecta un controlador para activar el monitoreo.",
      kpiTemperature: "Temperatura",
      kpiHumidity: "Humedad",
      kpiConnection: "Conexion",
      kpiWifiRssi: "Wi-Fi (RSSI)",
      actionSettings: "Ajustes",
      actionCharts: "Graficos",
      actionBlink: "Parpadear",
      refreshingStatuses: "Actualizando estados de espacios de cultivo...",
      breadcrumbOverview: "Resumen",
      kpiCo2: "CO₂",
      kpiDevicesOnline: "Controladores en linea",
      growerDiarySummaryTitle: "Resumen diario",
      growerDiarySummaryHint:
        "Los periodos «Dia» y «Noche» siguen el estado de la luz en telemetria (day_status), no el reloj.",
      growerDiaryTablePlaceholder:
        "Aqui aparecera una tabla de minimos y maximos para luz encendida y apagada.",
      tableDate: "Fecha",
      tableMetric: "Metrica",
      tablePeriodDay: "Dia (luz encendida)",
      tablePeriodNight: "Noche (luz apagada)",
      tableMin: "Min",
      tableMax: "Max",
      diaryAnalyticsTitle: "Diario y Analitica",
      analyticsLoading: "Cargando resumen…",
      analyticsError: "No se pudo cargar el resumen",
      analyticsTruncated: "Muestra limitada",
      analyticsTruncatedBanner: (cap) =>
        `Solo se incluyeron las últimas ${cap} lecturas en la ventana; se omitieron puntos más antiguos. Reduzca el periodo o los controladores para cobertura completa.`,
      analyticsSamples: "Lecturas en el resumen",
      colPeriod: "Periodo",
      colTempMm: "Temp min–max",
      colRhMm: "HR min–max",
      colCo2Avg: "CO₂ prom.",
      colVpdAvg: "VPD prom.",
      colDailyNote: "Nota del dia",
      dailyNoteSave: "Guardar",
      dailyNoteSaved: "Guardado",
      periodDayShort: "Dia",
      periodNightShort: "Noche",
    },
    invitePage: {
      title: "Unirse a la instalación como trabajador",
      invalidLink: "El enlace de invitacion no es valido.",
      missingToken: "Falta el token de invitacion.",
      registrationFailed: "No se pudo completar el registro.",
      emailPlaceholder: "Email",
      passwordPlaceholder: "Contrasena",
      creatingAccount: "Creando cuenta...",
      createAccount: "Crear cuenta",
    },
    unassignedPage: {
      title: "Controladores sin asignar",
      subtitle: "Asigna una instalación a cada controlador.",
      colDevice: "Controlador",
      colFarm: "Instalación",
      apply: "Asignar instalación",
      loading: "Cargando…",
      empty: "No hay controladores sin asignar",
      loadError: "No se pudo cargar",
      saveError: "No se pudo asignar la instalación",
      saved: "Instalación actualizada",
    },
    adminControllers: {
      title: "Todos los controladores",
      subtitle: "Todos los controladores en todas las instalaciones. La búsqueda y los filtros aplican en esta página.",
      searchLabel: "Buscar",
      searchPlaceholder: "ID, nombre, instalación o espacio de cultivo…",
      statusFilter: "Estado",
      statusAll: "Todos",
      statusOnline: "En línea",
      statusOffline: "Fuera de línea",
      displayFilter: "Tipo de pantalla",
      displayAll: "Todos",
      colControllerId: "ID del controlador",
      colDeviceName: "Nombre",
      colFarm: "Instalación",
      colRoom: "Espacio de cultivo",
      colStatus: "Estado",
      colHardware: "Hardware",
      colLastSeen: "Última actividad",
      colActions: "Acciones",
      badgeOnline: "En línea",
      badgeOffline: "Fuera de línea",
      viewLogs: "Ver registros",
      reboot: "Reiniciar",
      rebootConfirm: "¿Reinicio suave (soft reset) de este controlador?",
      rebootFailed: "No se pudo reiniciar",
      unbind: "Desvincular",
      unbindTitle: "Desvincular de instalación y espacio de cultivo",
      unbindModalTitle: "Desvincular controlador",
      unbindModalBody:
        "¿Seguro que desea desvincular el controlador {{id}} de la instalación «{{farm}}»? Volverá a la lista de controladores sin asignar.",
      unbindConfirm: "Desvincular",
      unbindCancel: "Cancelar",
      unbindFailed: "No se pudo desvincular",
      assign: "Vincular a espacio de cultivo",
      assignTitle: "Vincular controlador a instalación y espacio de cultivo",
      assignModalTitle: "Vincular a instalación y espacio de cultivo",
      assignModalHint: "Elija instalación y espacio de cultivo. Se actualizan farmId y roomId del controlador (como al registrar).",
      assignFarmLabel: "Instalación",
      assignFarmPlaceholder: "Elija una instalación…",
      assignRoomLabel: "Espacio de cultivo",
      assignRoomPlaceholder: "Elija una espacio de cultivo…",
      assignRoomLoading: "Cargando espacios de cultivo…",
      assignSubmit: "Vincular",
      assignCancel: "Cancelar",
      assignFailed: "No se pudo vincular",
      assignNeedFarmRoom: "Seleccione instalación y espacio de cultivo",
      controllerTypeWater: "Controlador de agua",
      controllerTypeClimate: "Controlador de clima",
      controllerTypeGeneric: "Controlador",
      empty: "No hay controladores que coincidan",
      loading: "Cargando…",
      loadError: "No se pudo cargar la lista",
    },
    deviceAssignment: {
      roomLabel: "Espacio de cultivo",
      roomNone: "Sin espacio de cultivo",
      roomSave: "Guardar espacio de cultivo",
      releaseDevice: "Liberar controlador",
      releaseConfirm:
        "¿Quitar la asignación de instalación y espacio de cultivo? El controlador quedará disponible para asignarlo a otra instalación (registro de superusuario).",
      releaseDone: "Controlador liberado",
      releaseError: "No se pudo liberar",
      releaseHint: "Solo superusuario: restablece instalación y espacio de cultivo.",
      unbindFarm: "Desvincular de la instalación",
      unbindConfirm:
        "¿Desvincular el controlador de la instalación? Aparecerá como no asignado hasta volver a vincularlo.",
      unbindDone: "Controlador desvinculado",
      unbindError: "No se pudo desvincular",
      superuserOnly: "Solo superusuario",
    },
    loading: "Cargando...",
    noNews: "Aún no hay noticias",
    updated: "Actualizado",
    refresh: "Actualizar",
    toggleNavigation: "Cambiar navegación",
    retry: "Reintentar",
    showOffline: (n) => `Mostrar controladores desconectados (${n})`,
    hideOffline: "Ocultar controladores desconectados",
    noData: "Sin datos",
    no_data: "Sin datos",
    statusOn: "ON",
    statusOff: "OFF",
    co2CheckTank: "Revisa el tanque",
    firmwareUpload: "Carga de firmware",
    upload: "Subir",
    cuttings: "Esquejes",
    news: "Noticias",
    latestReadings: "Últimas lecturas",
    loginFailed: "Error de inicio de sesión",
    copyToClipboard: "Copiar al portapapeles:",
    saveFailed: "No se pudo guardar",
    settingsSavedSuccess: "Configuración guardada correctamente",
    yes: "Sí",
    no: "No",
    timeSyncTitle: "Sincronización de hora",
    refreshTimeSync: "Actualizar",
    timeSyncHint: "Usa NTP como fuente principal de hora. El controlador debe calcular en UTC y aplicar la zona horaria solo para la lógica día/noche.",
    serverTimeLabel: "Hora del servidor",
    controllerTimeLabel: "Hora del controlador",
    timeDriftLabel: "Diferencia (drift)",
    timeUnknown: "Desconocido",
    farmTimezoneLabel: "Zona horaria de la instalación",
    farmTimezoneHint: "Guarda una zona IANA como Asia/Jerusalem. Este valor se devuelve desde /api/system/time y se aplica en el controlador para el modo día/noche.",
    saveTimezone: "Guardar zona horaria",
    co2ManagementTitle: "Gestión de CO2",
    co2OffsetLabel: "Valor de calibracion de CO2 (ppm)",
    co2OffsetHint: "Corrección de lectura frente a un sensor de referencia VDL.",
    calibrationTargetPpmPlaceholder: "Estandar: 400. Mar Muerto: 580",
    syncNow: "Sincronizar ahora",
    forceCalibrateButton: "Iniciar calibracion",
    forceCalibrateHint: "Ejecutar solo tras 5 minutos de ventilacion.",
    calibrationStatus: {
      pending: "⏳ Esperando sincronizacion con el controlador...",
      started: (left) => `✅ Calibracion iniciada (5 min) ${left}`,
    },
    manualTimeLabel: "Hora manual",
    setManualTime: "Establecer hora",
    manualTimeHint: "Úsalo solo en redes aisladas sin NTP. El comando envía Unix-time en UTC junto con la zona horaria de la instalación.",
    waterPage: {
      idLabel: "ID",
      firmwareLabel: "Firmware",
      range2h: "2 h",
      range24h: "24 h",
      range7d: "7 d",
      rangeCustom: "Personalizado",
      latestTitle: "Últimos datos",
      noHistory: "Todavía no hay historial de agua.",
      inletLine: "Entrada",
      outletLine: "Salida",
      litersPerMinuteShort: "l/min",
      litersShort: "L",
      successOk: "OK",
      genericError: "Error",
    },
    deviceSettingsPage: {
      calibrationVentilationConfirm: "Asegurate de que la espacio de cultivo haya sido ventilada al menos 5 minutos antes de confirmar.",
      timezonePlaceholder: "Asia/Jerusalem",
      co2OffsetPlaceholder: "0",
      currentValue: "Actual",
      co2OffsetSaveHint:
        "El desplazamiento de CO₂ de la sección superior se guarda junto con la zona horaria de la instalación al pulsar «Guardar zona horaria».",
      advancedFunctionsTitle: "Comandos avanzados de Particle",
      vpdProPlanLock:
        "El análisis VPD está disponible en los planes Pro y Enterprise. Actualice el plan de la instalación para cambiar el tipo de VPD.",
      locked: "bloqueado",
    },
    deviceSettingsModal: {
      actionsTitle: "Acciones del controlador",
      actionsHint: "Comandos manuales de servicio para el controlador seleccionado.",
      restartButton: "Reiniciar controlador",
      restartConfirmTitle: "Confirmar reinicio del controlador",
      restartWarning: "El controlador puede estar fuera de línea unos 30-90 segundos durante el reinicio. Reles, umbrales, modos y configuración EEPROM no cambiarán.",
      restartConfirmButton: "Sí, reiniciar",
      restartSending: "Enviando comando",
      restartAccepted: "Comando de reinicio aceptado. El controlador se está reiniciando y puede estar fuera de línea 30-90 segundos.",
      restartFailed: "No se pudo reiniciar el controlador.",
      restartNotReachable: "El reinicio falló porque el controlador no está disponible.",
      modeAutoBadge: "A",
      modeManualOnBadge: "M:ON",
      modeManualOffBadge: "M:OFF",
      relayModeAria: (label) => `modo de control de salida ${label}`,
      modeAuto: "Auto",
      modeManualOn: "Bloqueo manual: encendido",
      modeManualOff: "Bloqueo manual: apagado",
      deviceNameTitle: "Nombre del controlador",
      deviceNamePlaceholder: "Nombre del controlador",
      telegramAlerts: "Alertas de Telegram",
      powerLoss: "Corte de energía",
      thresholdAlerts: "Alertas de umbral",
      trendAlerts: "Alertas de tendencia",
      climateDeliveryOn: "Las alertas de clima se pueden entregar",
      climateDeliveryBlocked: "Las alertas de clima no se entregarán",
      climateDeliveryOff: "Las alertas de clima están apagadas",
      climateDeliveryCounts: (enabled, total) => `Destinatarios de Climate alerts: ${enabled}/${total}`,
      climateDeliveryNoRecipients:
        "Las notificaciones del controlador están activadas, pero no hay destinatarios de Telegram vinculados para alertas de clima.",
      climateDeliveryClimateDisabled:
        "Las notificaciones del controlador están activadas, pero todos los destinatarios elegibles tienen Climate alerts desactivadas.",
      climateDeliveryTelegramSettingsHint:
        "Abra los ajustes de notificaciones en el bot de Telegram y active la categoría Climate alerts.",
      enableClimateAlertsForMe: "Activar Climate alerts para mí",
      enablingClimateAlerts: "Activando Climate alerts",
      climateAlertsEnabledForMe: "Climate alerts está activado para su cuenta de Telegram.",
      climateAlertsEnableFailed: "No se pudo activar Climate alerts.",
      co2Management: "Gestión de CO2",
      co2Offset: "Valor de calibracion de CO2 (ppm)",
      startCalibration: "Iniciar calibracion",
      calibrationTargetPpmLabel: "Valor de calibracion (PPM)",
      calibrationTargetPpmPlaceholder: "Estandar: 400. Mar Muerto: 580",
      calibrationHint: "Ejecutar solo tras 5 minutos de ventilacion",
      calibrationConfirm: "Seguro? Ejecutar solo tras 5 minutos de ventilacion.",
      relayManagement: "Overrides de salidas",
      waterMetric: { ph: "pH", ec: "EC", orp: "ORP", temp: "Temperatura", level: "Nivel de agua", volume: "Volumen" },
    },
    deviceCard: {
      unknownDeviceName: "Controlador de clima",
      idLabel: "ID",
      idDialogTitle: "ID del controlador",
      idPreviewAria: "Ver el ID completo del controlador y copiarlo",
      copyId: "Copiar ID",
      copied: "Copiado",
      connectivityOnline: "En línea",
      connectivityNoRecentData: "Sin datos recientes",
      connectivityOffline: "Fuera de línea",
      controllerOfflineCommands: "El controlador está fuera de línea. Los comandos no se ejecutan.",
      syncTimeFailed: "No se pudo sincronizar la hora",
      calibrationFailed: "La calibración falló",
      climateParamsUpdated: "Parámetros de clima actualizados",
      relayCommandTimeout: "Error: el controlador no confirmó la ejecución del comando (timeout)",
      relayCommandUnconfirmed:
        "El modo se guardó, pero el controlador no envió confirmación. Actualice la página si el estado no coincide con el controlador.",
      modeLabel: "modo",
      errorGeneric: "Error",
      invalidThreshold: "Umbral inválido: ingresa Min y Max numéricos y asegúrate de que Min <= Max.",
    },
    settingsApplyFailedPrefix: "No se pudo aplicar",
    settingsProgressSavingDb: "Guardando configuración en la base de datos...",
    settingsProgressApplyingController: "Aplicando umbrales al controlador",
    settingsProgressControllerCheck: "Umbrales guardados en la base, verificando controlador...",
    settingsProgressSavingSystem: "Guardando parámetros del sistema...",
    settingsProgressSyncingView: "Sincronizando visualización...",
    co2OffsetNotSavedWarning: "No se guardó CO2 Offset (endpoint de zona horaria no disponible)",
    noCuttings: "Aún no hay esquejes",
    noStrains: "No se encontraron variedades",
    noBugs: "No hay bugs para mostrar",
    login: "Iniciar sesión",
    logout: "Cerrar sesión",
    profile: {
      title: "Perfil",
      subtitle: "Datos de la cuenta y enlace con Telegram.",
      farm: "Instalación",
      linkTelegram: "Conectar Telegram",
      telegramLinked: "Telegram conectado",
      telegramNotLinked: "Telegram no conectado",
      openTelegramBot: "Abrir bot de Telegram",
      disconnectTelegram: "Desconectar Telegram",
      disconnectingTelegram: "Desconectando Telegram…",
      disconnectError: "No se pudo desconectar Telegram. Inténtelo más tarde.",
      linkError: "No se pudo obtener el enlace. Inténtelo más tarde.",
      openingBot: "Abriendo el bot…",
      refreshStatus: "Actualizar estado",
      telegramHint:
        "Conecta Telegram para recibir alertas. Una vez conectado, puedes abrir el bot o desconectar Telegram aquí.",
    },
    telegramConnect: {
      eyebrow: "Telegram",
      title: "Conexión de cuenta de Telegram",
      description:
        "Conecta tu cuenta de Telegram para acceder a instalaciones, espacios de cultivo, alertas y herramientas de controladores.",
      button: "Conectar Telegram",
      connecting: "Conectando Telegram...",
      linkError: "No se pudo crear el enlace de Telegram. Inténtalo más tarde.",
      signInAgain: "Algo salió mal. Inicia sesión de nuevo.",
      rateLimit: "Espera unos segundos e inténtalo de nuevo.",
      retryButton: "Reintentar conexión",
      openTelegramButton: "Abrir Telegram y finalizar conexión",
      copyConnectCodeButton: "Copiar código de conexión",
      copyConnectCodeDone: "Código de conexión copiado",
      copyError: "No se pudo copiar el código. Cópialo manualmente.",
      widgetLoading: "Cargando el botón de inicio de sesión de Telegram...",
      widgetLoadError: "No se pudo cargar el botón de inicio de sesión de Telegram. Actualiza la página o usa el método alternativo.",
      manualFallbackHint: "Si Telegram se abre sin el código de inicio, copia el código de conexión y pégalo en el chat del bot.",
      afterLoginHint: "Si aún no has iniciado sesión, inicia sesión primero; la vinculación continuará automáticamente.",
    },
    rateLimitPage: {
      eyebrow: "Shefa Green",
      title: "Vamos más despacio un momento",
      body:
        "Hemos limitado temporalmente las solicitudes desde su navegador porque el tráfico fue inusualmente alto. Así mantenemos Shefa Green fiable para todos.",
      hint: "Espere unos minutos, actualice la página o use el botón de abajo para intentarlo de nuevo.",
      ctaRetry: "Intentar de nuevo",
      ctaHome: "Ir al inicio",
      tabsHint:
        "Si tiene varias pestañas de Shefa Green abiertas, cierre las que no necesite para evitar solicitudes automáticas en segundo plano.",
      retryIn: (totalSeconds) => {
        const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
        if (s <= 0) return "Ya puede intentarlo de nuevo.";
        if (s >= 120) {
          const m = Math.ceil(s / 60);
          return `Puede intentarlo de nuevo en unos ${m} minutos.`;
        }
        return `Puede intentarlo de nuevo en unos ${s} segundos.`;
      },
    },
    theme: {
      dark: "Oscuro",
      light: "Claro",
      system: "Sistema",
      toggleAria: "Cambiar tema: oscuro, claro o según el sistema",
      tapNext: "pulse para alternar",
      farmMenu: "Contexto de finca",
      farmHomeToken: "Finca principal (token)",
    },
    username: "Usuario",
    email: "Correo",
    password: "Contraseña",
    users: "Usuarios",
    addUser: "Agregar usuario",
    role: "Rol",
    delete: "Eliminar",
    manageCuttings: "Gestionar esquejes",
    byName: "Mostrar por nombre",
    byCode: "Mostrar por código",
    both: "Ambos",
    manageStrains: "Gestionar variedades",
    manageReviews: "Resenas profesionales",
    manageNews: "Gestionar noticias",
    addCutting: "Agregar esqueje",
    addStrain: "Agregar variedad",
    addNews: "Agregar noticia",
    reportBug: "Reportar bug",
    strainsLoadError: "No se pudo cargar la lista de variedades.",
    strainsAdminCrashed: "Se produjo un error al cargar la página de administración de variedades.",
    pin: "Fijar",
    unpin: "Desfijar",
    pinnedBugs: (n) => `\uD83D\uDCCC Bugs fijados (${n})`,
    recentlyCreated: "Creado recientemente",
    recentUpdate: "Actualización reciente",
    closeBug: "Cerrar",
    closedBugs: (n) => `Bugs cerrados (${n})`,
    showClosed: "Mostrar cerrados",
    hideClosed: "Ocultar cerrados",
    showOnlyMine: "Solo mis bugs",
    urgentOnly: "Solo urgentes",
    sort: "Ordenar por",
    sortByPriority: "Prioridad",
    sortByDateDesc: "Nuevos primero",
    sortByDateAsc: "Antiguos primero",
    sortByStatus: "Estado",
    shownOfTotal: (s, t) => `Mostrando ${s} de ${t} bugs`,
    addTask: "Agregar tarea",
    addComment: "Agregar comentario",
    priority: "Prioridad",
    priorityLabel: (value) => `Prioridad: ${value}`,
    priorityHigher: "Más alta",
    priorityLower: "Más baja",
    lastActivity: "Última actividad",
    showAllComments: "Mostrar todos los comentarios",
    noCommentsYet: "Aún no hay comentarios",
    completeTask: "Completar",
    showCompleted: "Mostrar completadas",
    hideCompleted: "Ocultar completadas",
    tasksDashboard: {
      subtitle: "Tu centro de control diario",
      summaryUrgent: "Urgentes",
      summaryToday: "Hoy",
      summaryOverdue: "Atrasadas",
      summarySystem: "Del sistema",
      summaryCompleted: "Completadas",
      sectionToday: "Tareas de hoy",
      sectionProblems: "Requieren atención",
      sectionScheduled: "Programadas",
      sectionHistory: "Historial y completadas",
      tabToday: "Hoy",
      tabProblems: "Problemas",
      tabScheduled: "Programadas",
      tabHistory: "Historial",
      quickAddTitle: "Alta rápida",
      recommendationsTitle: "Recomendaciones automáticas",
      recommendationsHint:
        "Cuando la automatización por sensores esté activa, aquí aparecerán tareas por anomalías.",
      recommendationsEmptyPrimary: "No hay recomendaciones activas del sistema.",
      recommendationsEmptySecondary:
        "Las recomendaciones aparecerán aquí cuando los datos de clima, riego, conexión del controlador o equipamiento salgan del rango esperado.",
      noTasksInView: "Aún no hay tareas en esta vista.",
      emptyToday:
        "Nada para hoy. Esta pestaña muestra tareas activas sin fecha, con vencimiento hoy (día calendario) o atrasadas. Lo programado después de hoy está en Programadas.",
      emptyProblems:
        "No hay tareas problemáticas. Aquí aparecen atrasadas, prioridad urgente y estados «falta» o «rechazado».",
      emptyScheduled:
        "No hay tareas con fecha después de hoy. Establezca un vencimiento posterior a este día o revise Hoy para lo que corresponde ahora.",
      emptyHistory: "Aún no hay tareas completadas; aparecerán aquí al marcarlas hechas.",
      completedSectionTitle: "Tareas completadas",
      postpone: "Posponer",
      postponeModalTitle: "Posponer tarea",
      postponeLaterToday: "Más tarde hoy",
      postponeTomorrowMorning: "Mañana por la mañana",
      postponeTomorrowEvening: "Mañana por la tarde",
      postponeChooseDateTime: "Elegir fecha y hora",
      postponeApply: "Aplicar",
      postponeFailed: "No se pudo actualizar la fecha límite. Inténtelo de nuevo.",
      postponeInvalidDate: "Introduzca una fecha y hora válidas.",
      openDetails: "Detalles",
      dueLabel: "Fecha / hora límite",
      addPhotos: "Fotos",
      roomOrLocation: "Espacio de cultivo / ubicación",
      optionalLinksHint: "Opcional: vincular a una entidad de la finca (si se cargan las listas).",
      linkRoom: "Espacio de cultivo",
      linkDevice: "Controlador / controlador",
      linkStrain: "Variedad",
      linkCutting: "Esqueje",
      linkNoneOption: "—",
      cuttingLabel: "Esqueje",
      groupNoRoom: "Sin espacio de cultivo / sin asignar",
      groupTaskCount: (n) => (Number(n) === 1 ? '1 tarea' : `${n} tareas`),
      filterRoom: "Espacio de cultivo",
      filterTaskType: "Tipo de tarea",
      filterPriority: "Prioridad",
      filterSource: "Origen",
      filterSearch: "Buscar",
      filterSearchPlaceholder: "Descripción, ubicación, espacio de cultivo, controlador, variedad, esqueje, comentarios…",
      filterAllRooms: "Todas las espacios de cultivo",
      filterAllTypes: "Todos los tipos",
      filterAllPriorities: "Todas las prioridades",
      filterAllSources: "Todas las fuentes",
      clearFilters: "Limpiar filtros",
      emptyFiltered: "Ninguna tarea coincide con los filtros. Ajústelos o límpielos.",
      sourceManual: "Manual",
      sourceSystem: "Sistema",
      pageTitle: "Tareas",
      completeTask: "Completar",
      showCompleted: "Mostrar completadas",
      hideCompleted: "Ocultar completadas",
      addTask: "Agregar tarea",
      addComment: "Agregar comentario",
      fieldDescription: "Descripción",
      fieldPriority: "Prioridad",
      fieldComment: "Comentario",
      statusInProgress: "En progreso",
      statusNeeds: "Falta atención",
      statusDone: "Hecho",
      statusRejected: "Rechazado",
      completedAtLabel: "Completada",
      lastActivity: "Última actividad",
      priorityUrgent: "Urgente",
      systemReasonSection: "Motivo del sistema",
      systemReasonPrimaryLabel: "Motivo",
      systemTriggerRange: (min, max) => `Permitido ${min}–${max}`,
      systemTriggerMinOnly: (min) => `Mín. ${min}`,
      systemTriggerMaxOnly: (max) => `Máx. ${max}`,
      systemTriggeredAt: "Activado el",
      systemTriggerTypeLabel: "Tipo de activación",
      systemTriggerMetricLabel: "Métrica",
      systemTriggerValueLabel: "Valor",
      systemAllowedRangeLabel: "Rango permitido",
      taskType: "Tipo de tarea",
      systemTask: "Sistema",
      reason: "Nota",
      noDueDate: "Sin fecha límite",
      priorityLow: "Baja",
      priorityMedium: "Media",
      priorityHigh: "Alta",
      taskTypes: {
        climate: "Clima",
        watering: "Riego",
        plant_inspection: "Inspección de plantas",
        equipment: "Equipamiento",
        cuttings: "Esquejes",
        nutrition: "Nutrición",
        pests_disease: "Plagas o enfermedades",
        cleaning: "Limpieza",
        other: "Otro",
      },
    },
    openedBy: "Abierto por",
    details: "Detalles",
    tasks: "Tareas",
    bugs: "Bugs",
    open: "Abierto",
    inProgress: "En progreso",
    testing: "En pruebas",
    urgent: "Urgente",
    veryUrgent: "Muy urgente",
    needs: "Falta",
    done: "Hecho",
    rejected: "Rechazado",
    fixed: "Corregido",
    closed: "Cerrado",
    edit: "Editar",
    update: "Actualizar",
    cancel: "Cancelar",
    save: "Guardar",
    title: "Título",
    name: "Nombre",
    content: "Contenido",
    description: "Descripción",
    strain: "Variedad",
    status: "Estado",
    location: "Ubicación",
    code: "Código",
    history: "Historial",
    statusHistory: "Historial de estados",
    statusHistoryEmpty: "Sin cambios de estado",
    statusHistoryFrom: "De",
    statusHistoryTo: "A",
    statusHistoryBy: "Por",
    statusHistoryAt: "En",
    link: "Enlace",
    event: "Evento",
    comment: "Comentario",
    photo: "Foto",
    screenshot: "Captura",
    savePhoto: "Guardar foto",
    photoSaved: "Foto guardada",
    photoShortName: "Nombre corto",
    photoDescriptionDetailed: "Descripción (detallada)",
    photoIncludeInDiary: "Incluir en diario",
    photoDiaryOnly: "Solo diario",
    photoStage: "Etapa",
    photoStageFilter: "Filtrar por etapa",
    photoStageAll: "Todas las etapas",
    photoTakenAt: "Marca temporal (timeline)",
    photoPosition: "Posición",
    photoMove: "Mover en la cronología",
    moveLeft: "Mover a la izquierda",
    moveRight: "Mover a la derecha",
    downloadDiary: "Descargar diario",
    diaryExportTooMany: (limit) => `La exportación se limita a ${limit} fotos`,
    diaryExportNoPhotos: "No hay fotos marcadas para el diario",
    photoDetails: "Detalles de la foto",
    photoStageLabels: {
      cut: "Corte",
      veg: "Vegetativo",
      flowering: "Floración",
      harvest: "Cosecha",
      drying: "Secado",
      trim: "Manicura",
      curing: "Curado",
      lab_tests: "Análisis de laboratorio",
      issue: "Problema",
    },
    gardenerDiary: "Diario del jardinero",
    viewReviewsForStrain: "Ver resenas de esta variedad",
    legacyDiaryReadOnly:
      "El diario heredado es solo de lectura. Para nuevas evaluaciones use el sistema de resenas profesionales.",
    goToReviews: "Ir a resenas",
    addLog: "Agregar registro",
    confirmDelete: "¿Eliminar?",
    author: "Autor",
    id: "ID",
    slug: "Slug",
    genetics: "Genética",
    geneticsType: "Tipo de genética",
    geneticsDominance: "Dominancia (%)",
    shortDescription: "Descripción corta",
    thcPercent: "THC %",
    cbdPercent: "CBD %",
    floweringWeeks: "Floración (semanas)",
    floweringCategory: "Categoría de floración",
    floweringEarly: "Temprana",
    floweringMedium: "Media",
    floweringLate: "Tardía",
    yield: "Rendimiento",
    yieldLow: "Bajo",
    yieldMedium: "Medio",
    yieldHigh: "Alto",
    yieldVeryHigh: "Muy alto",
    plantHeight: "Altura de la planta",
    heightLow: "Baja",
    heightMedium: "Media",
    heightHigh: "Alta",
    terpenes: "Terpenos",
    aromaFlavor: "Aroma/sabor",
    effectDirection: "Dirección del efecto",
    effectUp: "Arriba",
    effectNeutral: "Neutro",
    effectDown: "Abajo",
    effectSensations: "Sensaciones",
    uniqueTrait: "Rasgo único",
    positioning: "Posicionamiento",
    positioningCommercial: "Comercial",
    positioningBoutique: "Boutique",
    positioningExotic: "Exótico",
    positioningNiche: "Nicho",
    recommendedTime: "Horario recomendado",
    recommendedDay: "Día",
    recommendedEvening: "Tarde",
    recommendedNight: "Noche",
    targetAudience: "Público objetivo",
    audienceBeginners: "Principiantes",
    audienceExperienced: "Experimentados",
    audienceUniversal: "Universal",
    passportCore: "CORE",
    passportCorePlus: "CORE+",
    passportExtended: "EXTENDED",
    tagLimit: "Máximo 6 etiquetas (efectos — 5).",
    imageLimit: "Máximo 5 imágenes.",
    maxImages: "Se alcanzó el límite de imágenes.",
    extendedLabReports: "PDF/enlaces de laboratorio (separados por comas)",
    extendedFullCannabinoidProfile: "Perfil completo de cannabinoides",
    extendedSecondaryTerpenes: "Terpenos secundarios (separados por comas)",
    extendedCultivationNotes: "Notas de cultivo",
    extendedInternalCommercialNotes: "Notas comerciales internas",
    statusRd: "I+D",
    statusTest: "Prueba",
    statusPreCommercial: "Precomercial",
    statusCommercial: "Comercial",
    weeks: "semanas",
    images: "Imágenes",
    publicUrl: "URL pública",
    transplant: "trasplante",
    treatment: "tratamiento",
    move: "movimiento",
    viewMore: "Ver más",
    collapse: "Cerrar",
    duplicate: "Duplicar",
    downloadQrPdf: "Descargar QR (PDF)",
    downloadAllQrPdf: "Descargar todos los QR (PDF)",
    downloadExcel: "Descargar todos los QR (Excel)",
    downloadExcelReport: "Descargar Excel",
    downloadReadings: "Lecturas…",
    downloadSettingsChanges: "Cambios de configuración…",
    from: "Desde",
    to: "Hasta",
    download: "Descargar",
    downloadFailed: "Error al descargar el archivo",

    labelModes: {
      nameOnly: "Solo nombre",
      numberOnly: "Solo número",
      nameAndNumber: "Nombre + número",
    },
    copyQrText: "Copiar texto QR",
    copyLabelText: "Copiar texto de etiqueta",
    export: "Exportar",
    hide: "Ocultar",
    readMore: "Leer más",
    currentReadings: "Lecturas actuales",
    thresholdValues: "Valores límite",
    day: "Día",
    night: "Noche",
    dayNight: "Día/Noche",
    preset: "Preajuste",
    socketStates: "Estado de tomas",
    temperature: "Temperatura",
    humidity: "Humedad",
    co2: "CO₂",
    vpd: "VPD",
    light: "PPFD",
    lightStatus: "Estado de la luz",
    moisture: "Humedad del suelo",
    ec: "EC",
    ph: "pH",
    nitrogen: "N",
    phosphorus: "P",
    potassium: "K",
    inMedium: "En medio",
    inAir: "En aire",
    search: "Buscar",
    all: "Todo",
    climate: "Clima",
    waterInAndOut: "Agua entrada/salida",
    noDevicesFound: "No se encontraron controladores",
    waterDevices: "Controladores de agua",
    waterTrends: "Tendencias del agua",
    waterHistoryRange: "Rango del historial de agua",
    noWaterHistory: "Aún no hay historial de agua.",
    waterTemperature: "Temperatura del agua",
    waterLevel: "Nivel de agua",
    waterVolume: "Volumen de agua",
    tank: "Tanque",
    of: "de",
    maxHeight: "Altura máx.",
    centimetersShort: "cm",
    noTankData: "No hay datos del tanque",
    water: {
      inlet: "Entrada",
      outlet: "Salida",
      outletStatus: "Salida",
      units: {
        litersShort: "L",
        centimetersShort: "cm",
      },
      hints: {
        ph_optimal: "Rango óptimo",
      },
      errors: {
        ph_out_of_range: "pH fuera de rango",
        low_level: "Nivel de agua bajo",
        temp_high: "Advertencia: por encima de 28°C",
      },
    },
    time: "Hora",
    liters: "Litros (L)",
    summary: "Resumen",
    autoRefresh: "Actualización automática",
    getConfig: "Obtener configuración",
    pushNow: "Enviar ahora",
    softResetConfirm: "¿Reiniciar el controlador?",
    apply: "Aplicar",
    config: "Configuración",
    calibrateIn: "Calibrar entrada",
    calibrateOut: "Calibrar salida",
    inTotalDelta: "Entrada total Δ",
    outTotalDelta: "Salida total Δ",
    avg: "promedio",
    displayIntervalLabel: "Intervalo de pantalla",
    serverIntervalLabel: "Intervalo del servidor",
    lastTs: "Última marca de tiempo",
    inPpl: "Entrada ppl",
    outPpl: "Salida ppl",
    unknown: "desconocido",
    online: "en línea",
    date: "Fecha",
    marketName: "Nombre comercial",
    internalNumber: "N.º interno",
    firmwareUpdate: "Actualización de firmware",
    latest: "Última",
    lastUploaded: "Subido",
    flash: "Flashear",
    photoUploading: "Subiendo foto...",
    photoTooLarge: "Archivo demasiado grande (máx. 3 MB)",
    unsupportedFormat: "Formato no soportado",
    tooManyFiles: "Demasiados archivos",
    editPhoto: "Editar foto",
    rotateLeft: "Girar a la izquierda",
    rotateRight: "Girar a la derecha",
    zoom: "Zoom",
    uploadFailed: "Error de carga",
    flashFailed: "Error de flasheo",
    invalidDeviceId:
      "Formato de ID de controlador inválido. Asegúrate de que no tenga espacios y al menos 24 caracteres.",
    getFreshData: "Actualizar datos",
    notifyOnOffline: "Notificar si se corta la energía",
    powerLossAlerts: "Alertas de Telegram por corte de energía",
    telegramAlertsOn: "Alertas de Telegram: ON",
    telegramAlertsOff: "Alertas de Telegram: OFF",
    trendAlerts: "Alertas de tendencia",
    climateCharts: "Gráficos de clima",
    climateNoData24h: "No hay datos de las últimas 24 horas.",
    climateVpdProOnly: "El análisis VPD está disponible en los planes Pro y Enterprise.",
    climateExpandDetails: "Expandir detalles",
    climateHideDetails: "Ocultar detalles",
    climateRelays: "Relés",
    climateSetpointsDay: "Setpoints: Día",
    climateSetpointsNight: "Setpoints: Noche",
    climateSaveThresholds: "Guardar umbrales",
    climateSaving: "Guardando...",
    climateLightStatus: "Estado de luz",
    climateNightPhaseInRoom: "🌙 FASE NOCTURNA (EN LA SALA)",
    last24Hours: "últimas 24 horas",
    settings: "Configuración",
    deviceSettings: "Configuración del controlador",
    deviceSettingsBrandTitle: "Shefa Green Controller — ajustes",
    deviceSettingsBrandSubtitle: (deviceId) =>
      deviceId ? `ID del controlador: ${deviceId}` : "",
    backToController: "Volver al controlador",
    backToControllers: "Volver a los controladores",
    selectFunction: "Seleccionar función",
    commandSuccess: "✅ Comando ejecutado correctamente",
    commandError: "❌ Error: verifica el argumento",
    particleAuthError:
      "Error de autorización. Verifica el access_token y que el controlador pertenezca a tu cuenta.",
    refreshReadings: "Actualizar lecturas",
    refreshConfig: "Actualizar configuración",
    displayInterval: "Intervalo de pantalla (min)",
    serverInterval: "Intervalo del servidor (min)",
    saveConfig: "Guardar configuración",
    loadConfig: "Cargar configuración",
    factoryReset: "Restablecimiento de fábrica",
    wipeEEPROM: "Borrar EEPROM",
    relayLabels: { A: "Aire acondicionado", D: "Secador", C: "CO₂", L: "Luces", reserve: "Reserva", mask: "máscara" },
    intervalError: "El intervalo debe ser >0 y <=1440 minutos",
    functions: {
      setHumidityThreshold: "💧 Humedad: umbrales (%)",
      setTemperatureThreshold: "🌡 Temperatura: umbrales (°C)",
      setCO2Threshold: "CO₂: umbrales (ppm)",
      toggleSocket: "🔌 Control de tomas",
      calibrateCO2ppm: "🔧 Calibrar CO₂",
      setDisplayInterval: "🕒 Intervalo de pantalla (min)",
      setServerInterval: "📤 Intervalo del servidor (min)",
      getReadings: "📈 Obtener lecturas",
      softReset: "🔄 Reinicio suave",
      setVPDType: "🌿 Tipo VPD",
    },
    args: {
      min: "Mín",
      max: "Máx",
      ppm: "PPM",
      minutes: "Minutos",
      type: "Tipo",
      submit: "Enviar",
    },
    enums: {
      air: "Aire",
      leaf: "Hoja",
      "leaf+": "Hoja+",
      on: "Encendido",
      off: "Apagado",
      auto: "Auto",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
    },
    hints: {
      setHumidityThreshold: "ejemplo: 40.0,60.0",
      setTemperatureThreshold: "ejemplo: 18.0,25.0",
      setCO2Threshold: "ejemplo: 400,1000",
      calibrateCO2ppm: "ejemplo: 400",
      setDisplayInterval: "Introduzca la cantidad en minutos (por ejemplo, 1).",
      setServerInterval:
        "Introduzca el intervalo de envío en minutos, mínimo 1.",
      toggleSocket: "ejemplo: 1,on",
      setVPDType: "air | leaf | leaf+",
    },
  },
};

for (const code of SUPPORTED_LANGS) {
  if (translations[code]?.home) {
    translations[code].home.landing = HOME_PAGE_LANDING[code] || HOME_PAGE_LANDING.en;
  }
}

const languageLabels = {
  ua: "Українська",
};

const TERMINOLOGY_REPLACEMENTS = {
  en: [
    [/\bFarms\b/g, "Facilities"],
    [/\bFarm\b/g, "Facility"],
    [/\bfarms\b/g, "facilities"],
    [/\bfarm\b/g, "facility"],
    [/\bRooms\b/g, "Growing Spaces"],
    [/\bRoom\b/g, "Growing Space"],
    [/\brooms\b/g, "growing spaces"],
    [/\broom\b/g, "growing space"],
    [/\bDevices\b/g, "Controllers"],
    [/\bDevice\b/g, "Controller"],
    [/\bdevices\b/g, "controllers"],
    [/\bdevice\b/g, "controller"],
  ],
  he: [
    [/חוות/g, "מתקנים"],
    [/חווה/g, "מתקן"],
    [/חדרים/g, "חללי גידול"],
    [/חדר/g, "חלל גידול"],
    [/התקנים/g, "בקרים"],
    [/התקן/g, "בקר"],
    [/מכשירים/g, "בקרים"],
    [/מכשיר/g, "בקר"],
  ],
  ru: [
    [/фермами/gi, "объектами"],
    [/фермах/gi, "объектах"],
    [/фермы/gi, "объекты"],
    [/ферму/gi, "объект"],
    [/ферме/gi, "объекте"],
    [/ферма/gi, "объект"],
    [/ферм/gi, "объектов"],
    [/комнатами/gi, "зонами выращивания"],
    [/комнатах/gi, "зонах выращивания"],
    [/комнаты/gi, "зоны выращивания"],
    [/комнату/gi, "зону выращивания"],
    [/комнате/gi, "зоне выращивания"],
    [/комната/gi, "зона выращивания"],
    [/комнат/gi, "зон выращивания"],
    [/устройства/gi, "контроллеры"],
    [/устройств/gi, "контроллеров"],
    [/устройством/gi, "контроллером"],
    [/устройстве/gi, "контроллере"],
    [/устройство/gi, "контроллер"],
  ],
  ua: [
    [/фермами/gi, "об’єктами"],
    [/фермах/gi, "об’єктах"],
    [/ферми/gi, "об’єкти"],
    [/ферму/gi, "об’єкт"],
    [/фермі/gi, "об’єкті"],
    [/ферма/gi, "об’єкт"],
    [/ферм/gi, "об’єктів"],
    [/кімнатами/gi, "просторами вирощування"],
    [/кімнатах/gi, "просторах вирощування"],
    [/кімнати/gi, "простори вирощування"],
    [/кімнату/gi, "простір вирощування"],
    [/кімнаті/gi, "просторі вирощування"],
    [/кімната/gi, "простір вирощування"],
    [/кімнат/gi, "просторів вирощування"],
    [/пристрої/gi, "контролери"],
    [/пристроїв/gi, "контролерів"],
    [/пристроєм/gi, "контролером"],
    [/пристроїм/gi, "контролером"],
    [/пристроїй/gi, "контролері"],
    [/пристрій/gi, "контролер"],
    [/пристрою/gi, "контролера"],
    [/пристрої/gi, "контролері"],
  ],
  es: [
    [/Granjas/g, "Instalaciones"],
    [/Granja/g, "Instalación"],
    [/granjas/g, "instalaciones"],
    [/granja/g, "instalación"],
    [/Salas/g, "Espacios de cultivo"],
    [/Sala/g, "Espacio de cultivo"],
    [/salas/g, "espacios de cultivo"],
    [/sala/g, "espacio de cultivo"],
    [/Dispositivos/g, "Controladores"],
    [/Dispositivo/g, "Controlador"],
    [/dispositivos/g, "controladores"],
    [/dispositivo/g, "controlador"],
  ],
};

function applyTerminology(lang, value) {
  if (typeof value !== "string") return value;
  const replacements = TERMINOLOGY_REPLACEMENTS[lang];
  if (!replacements?.length) return value;
  return replacements.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), value);
}

Object.entries(reviewsAdminTranslations).forEach(([lang, data]) => {
  if (!translations[lang]) {
    translations[lang] = { language: languageLabels[lang] || lang };
  }
  translations[lang].reviewsAdmin = data;
  if (languageLabels[lang]) {
    translations[lang].language = languageLabels[lang];
  }
});

const LangContext = createContext({
  lang: "en",
  setLang: () => {},
  t: () => {},
});

export function LangProvider({ initialLang = "en", children }) {
  const [lang, setLangState] = useState(() => resolveLanguage(initialLang));

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem("lang", lang);
      } catch (err) {
        // Ignore write failures (e.g. private mode)
      }
    }
  }, [lang]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const dir = lang === "he" ? "rtl" : "ltr";
    document.documentElement.lang = lang === "ua" ? "uk" : lang;
    document.documentElement.dir = dir;
    document.body.dir = dir;
  }, [lang]);

  const setLang = useCallback((nextLang) => {
    setLangState(resolveLanguage(nextLang));
  }, []);
  const t = (path, ...args) => {
    const parts = path.split(".");
    const get = (obj) => parts.reduce((v, p) => (v ? v[p] : undefined), obj);
    let val = get(translations[lang]);
    if (val === undefined) val = get(translations.en);
    const resolved = typeof val === "function" ? val(...args) : (val ?? path);
    return applyTerminology(lang, resolved);
  };
  return (
    <LangContext.Provider value={{ lang, setLang, t, translations }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
